// Core simulation: saucer flight, tractor beam, abductions, waves, drones.

import * as THREE from 'three';
import type { BiomeId, World } from './world';
import { PLAY_RADIUS, LAKE, ROAD, MAPS, onRoad, nextBiome, groundUnder, groundRange } from './world';
import { unlockLand } from './lands';
import {
  buildSaucer, buildAnimal, buildRedHeifer, makeDonkeyAura, buildCultist, buildDrone, buildBullet, buildNormie, buildShack,
  buildShieldBubble, makeBlobShadow, makeBloodSplat, makeLightningBolt,
  buildMeteor, buildOre, buildEmpRing,
  pickMember, PoofPool, ScorePopPool, makeGlowSprite, buildHideyProp,
  buildPylon, buildWireSpan, buildArcFlash, disposePylon, disposeWireSpan,
  buildPylonStump, wireYAt, PYLON_RADIUS, PYLON_HEIGHT, CONDUCTOR_H, preloadCult3dModel,
  type SaucerRig, type CultistRig, type DroneRig, type NormieRig,
  type TruckRig, type MeteorRig, type OreRig, type ShieldRig, type HideyRig,
  type EmpRig, type PylonRig, type SpanRig,
} from './entities';
import type { SupplyDropRig } from './supply';
import type { TaxmanRig } from './taxman';
import type { GroupieRig } from './groupies';
import type { FieldDefense } from './combine';
import type { FiatRelayRig, BlackHoleRig } from './late-world';
import { animalForBiome, animalName, rollHerdSpecies, type AnimalSpecies } from './animals';
import { openingMembersForBiome, type CultMember } from './roster';
import { consumeSpecialPress, input } from './input';
import {
  sfxAbductPop, playAnimalCall, sfxDroneZap, sfxHullHit, sfxCoreRecharge, sfxWaveClear,
  sfxComboUp, sfxDrop, sfxRustle, sfxSplat, sfxRumble, sfxBeamHit, sfxBeamGate, sfxExplode,
  sfxFeverStart, sfxBossDown, sfxSplash, sfxThunder, startRainLoop, stopRainLoop,
  setBeamHum, setMusicTrack, playVoiceClip, playVoiceClipTracked, VOICE,
  sfxMeteorFall, sfxMeteorImpact, sfxBlackHoleOpen, sfxGulp, sfxEmpBlast,
  sfxTruckHorn, sfxChutePop, sfxChoir, sfxHowl, setBlackHoleHum, setNearLake,
  sfxShieldUp, sfxDepart, sfxTourComplete, memberVoice, preloadVoiceClip, setAmbientBiome,
  preloadMusicTrack,
  updateRotorField, stopRotorField, setHeliRotor, setGridHum, sfxArc, sfxDroneLock,
  sfxCrashFall, BIOME_TRACK, BIOME_VOICE, isMuted, type RotorSource,
} from './audio';
import { unlock, awardDef, type AwardDef } from './achievements';
import { recordSoul, gatheredCount } from './congregation';
import type { DamageSource } from './debrief';

// Altitude is a consequence of load, not of the beam. An empty beam has
// nothing to hold up, so the saucer can drop into the weeds; the moment it
// takes weight it must ride high and haul. See docs/v0.18-altitude.md.
const CRUISE_ALT = 7.4;
const SAUCER_ALT = CRUISE_ALT;
// Pinned by the grid, not by feel: conductors leave a tower at CONDUCTOR_H 8.7
// and sag near 6.8 mid-span against a 1.35 strike window, so 5.0 is the first
// altitude that makes a whole span passable. 5.6 is still struck.
const SQUAT_ALT = 5.0;
// Long enough that clipping the surface on the way past is not a car wash.
const CAR_WASH_DWELL_S = 0.8;
// Taking weight is immediate; setting back down is a slower settle.
const ALT_LOAD_RATE = 9;
const ALT_SQUAT_RATE = 4;
const SAUCER_SPEED = 15.5;
const BEAM_SPEED_FACTOR = 0.38;
const BEAM_CAPTURE_RADIUS = 2.8;
const BEAM_BREAK_RADIUS = 4.2;
const LIFT_SPEED = 3.4;
// What the winch is holding. A leader is the heaviest thing in the game: the
// slowest haul, the longest spell pinned at cruise, and the longest look at
// them on the way up.
// Hand depth back to a held placard inside this radius of the saucer. The disc
// plus a margin: far enough that the board is occluded before it reaches the
// hull, close enough that scenery still cannot hide a slogan.
const PLACARD_DEPTH_R = 7;
const LIFT_MASS = {
  animal: 1,
  ore: 1.04,
  crate: 1.06,
  groupie: 1.08,
  normie: 1.1,
  drone: 1.12,
  cultist: 1.2,
} as const;
const GRAVITY = 14;
const HERD_TARGET = 16;
/** The Donkey Farm's herd, and the rails they keep to. The pen ring is built
 *  at 8.4 in world.ts; grazing stops short of it so a penned donkey is inside
 *  the fence rather than leaning on it. The herd starts out, not in: the gate
 *  was left open and the whole seven are loose on the land. */
const DONKEY_HERD = 7;
const DONKEY_PEN_RADIUS = 7.2;
/** How far a loose donkey strays from its own farm. Wide enough that finding
 *  one is a search, close enough that the round-up stays a local job rather
 *  than seven trips across the map. */
const DONKEY_STRAY_MIN = 16;
const DONKEY_STRAY_MAX = 46;
/** The ferry is finished inside this radius of the pen centre — a donkey set
 *  down here is home, and anywhere else is another trip. */
const DONKEY_DROP_R = DONKEY_PEN_RADIUS + 1.6;
/** Loose stock is marked; penned stock settles back into the scenery. The loose
 *  glow has to carry across a field at flying height, so it is a real orange
 *  rather than a hint of warmth — still tighter and dimmer than the cult aura,
 *  which stays the brightest thing on the ground. */
const DONKEY_AURA_LOOSE = 0.28;
const DONKEY_AURA_PENNED = 0.07;
/** What the cabinet autopilot thinks a loose donkey is worth chasing. A pen
 *  run is a fetch plus a return leg, so the demo takes one when it is close to
 *  hand and gets on with the Council when it is not. */
const DONKEY_BOT_WEIGHT = 2.4;
const CULTIST_TARGET = 5;
const GROUPIE_TARGET = 4;
const COUNCIL_SIZE = 12;  // souls delivered to complete a stage and move on
const STAGE_BONUS = 2500; // per stage cleared, scales with the stage number
const RITUAL_MIN_S = 5;
const RITUAL_MAX_S = 22;  // safety only: allows one existing line plus the full 7.14s rite
const TOUR_ARRIVAL_MS = 3000;
const TOUR_FINALE_MS = 4600;
const SPLAT_FALL = 3.5;
// The drop is funny when the cargo falls from near the top of its own lift, not
// from a fixed height above the field. Held as the shipped fraction (3.5 of a
// 4.9 climb at cruise) so lowering the hull cannot put the splat out of reach:
// at DIVE_ALT the whole lift is 2.5, and an absolute 3.5 can never be met.
const SPLAT_FALL_FRACTION = SPLAT_FALL / 4.9;

/** Drop height that earns a splat, scaled to the lift this cargo was making. */
function splatFallFor(lift: Lifted, landY: number): number {
  if (lift.captureY === undefined) return SPLAT_FALL;
  return Math.max(1.2, (lift.captureY - landY) * SPLAT_FALL_FRACTION);
}
const COMBO_WINDOW_S = 6;
const COMBO_WINDOW_MIN_S = 3.6;
// Measured, not felt. A headless bot flown invulnerable for 720s on three
// seeds lifts every 3.4-3.7s at the median but 4.7-5.6s at p75, and the old
// 0.4 step put the window at 5.2s on x4 and 4.8s on x5 — straight through
// that tail, on exactly the two rungs where the ladder also starts asking for
// three and then four more links. Nothing was missing to grab: all 49 chain
// breaks across those runs had a liftable target inside 40 units, a median of
// 8-10 away. 0.3 buys x4 5.4s and x5 5.1s, which is the tail rather than the
// median, and leaves the 3.6s floor where it was.
const COMBO_WINDOW_STEP_S = 0.3;

export interface CouncilPilgrimage {
  minRadius: number;
  maxRadius: number;
  walkSpeed: number;
}

const EARLY_PILGRIMAGE: CouncilPilgrimage = { minRadius: 34, maxRadius: 68, walkSpeed: 1.12 };
const MID_PILGRIMAGE: CouncilPilgrimage = { minRadius: 30, maxRadius: 52, walkSpeed: 1.3 };
const LATE_PILGRIMAGE: CouncilPilgrimage = { minRadius: 26, maxRadius: 40, walkSpeed: 1.5 };

/**
 * Gathering the Council should gain momentum without turning twelve real
 * rescues into a smaller quota. After the first delivery, leaders answer the
 * stone in three increasingly urgent pilgrimage bands. The opening five still
 * begin across the whole land; only replacements and walking targets converge.
 *
 * Every band starts well outside the circle. Answering the call is a walk in
 * from the land, not a queue that forms on the doorstep: leaders appearing a
 * few strides from the stones made the fetch trivial and, worse, made the
 * congregation impossible to read against the leaders still waiting.
 */
export function councilPilgrimageFor(council: number): CouncilPilgrimage | null {
  if (council < 1) return null;
  if (council < 4) return EARLY_PILGRIMAGE;
  if (council < 8) return MID_PILGRIMAGE;
  return LATE_PILGRIMAGE;
}

/**
 * The congregation stands inside this radius of the monolith — it is the fence
 * that holds delivered leaders in, and the one that keeps everyone else out.
 */
const STONE_RING_R = 6.4;
/** No leader is placed, and no leader walks to a target, this close in. Only a
 *  completed delivery puts anybody inside the circle. */
const STONE_KEEP_OUT = 18;

let groupieRuntime: Promise<typeof import('./groupies')> | null = null;
let fieldDefenseRuntime: Promise<typeof import('./combine')> | null = null;
let crashRuntime: Promise<typeof import('./crash')> | null = null;
let challengeRuntime: Promise<typeof import('./challenges')> | null = null;
let supplyRuntime: typeof import('./supply') | null = null;
let supplyLoad: Promise<typeof import('./supply')> | null = null;
let lateWorldRuntime: typeof import('./late-world') | null = null;
let lateWorldLoad: Promise<typeof import('./late-world')> | null = null;
let waterDetailRuntime: typeof import('./water-detail') | null = null;
let waterDetailLoad: Promise<typeof import('./water-detail')> | null = null;
let glyphMarksRuntime: typeof import('./glyph-marks') | null = null;
let glyphMarksLoad: Promise<typeof import('./glyph-marks')> | null = null;

function loadLateWorld(): Promise<typeof import('./late-world')> {
  if (lateWorldRuntime) return Promise.resolve(lateWorldRuntime);
  return lateWorldLoad ??= import('./late-world')
    .then((runtime) => lateWorldRuntime = runtime)
    .catch((error) => { lateWorldLoad = null; throw error; });
}

function loadGroupies(): Promise<typeof import('./groupies')> {
  if (groupieRuntime) return groupieRuntime;
  const pending = import('./groupies');
  groupieRuntime = pending;
  void pending.catch(() => { if (groupieRuntime === pending) groupieRuntime = null; });
  return pending;
}

function loadFieldDefense(): Promise<typeof import('./combine')> {
  if (fieldDefenseRuntime) return fieldDefenseRuntime;
  const pending = import('./combine');
  fieldDefenseRuntime = pending;
  void pending.catch(() => { if (fieldDefenseRuntime === pending) fieldDefenseRuntime = null; });
  return pending;
}

function loadCrash(): Promise<typeof import('./crash')> {
  if (crashRuntime) return crashRuntime;
  const pending = import('./crash');
  crashRuntime = pending;
  void pending.catch(() => { if (crashRuntime === pending) crashRuntime = null; });
  return pending;
}

function loadChallenges(): Promise<typeof import('./challenges')> {
  if (challengeRuntime) return challengeRuntime;
  const pending = import('./challenges');
  challengeRuntime = pending;
  void pending.catch(() => { if (challengeRuntime === pending) challengeRuntime = null; });
  return pending;
}

function loadSupply(): Promise<typeof import('./supply')> {
  if (supplyRuntime) return Promise.resolve(supplyRuntime);
  return supplyLoad ??= import('./supply')
    .then((runtime) => supplyRuntime = runtime)
    .catch((error) => { supplyLoad = null; throw error; });
}

/**
 * The tier-5 artwork, fetched while the formation is still building. Tier 3
 * lands at 2.8s and tier 5 at 6.5s, so there is most of four seconds to pull a
 * chunk this small; the stamp checks for it anyway and simply waits another
 * frame rather than consuming the tier with nothing to draw.
 */
function loadGlyphMarks(): Promise<typeof import('./glyph-marks')> {
  if (glyphMarksRuntime) return Promise.resolve(glyphMarksRuntime);
  return glyphMarksLoad ??= import('./glyph-marks')
    .then((runtime) => glyphMarksRuntime = runtime)
    .catch((error) => { glyphMarksLoad = null; throw error; });
}

function loadWaterDetail(): Promise<typeof import('./water-detail')> {
  if (waterDetailRuntime) return Promise.resolve(waterDetailRuntime);
  return waterDetailLoad ??= import('./water-detail')
    .then((runtime) => waterDetailRuntime = runtime)
    .catch((error) => { waterDetailLoad = null; throw error; });
}

/**
 * The chain ladder. It used to stop at x4 (six chained), so a player who chained
 * forty scored exactly like one who chained six — the game's best skill, routing
 * to keep the chain alive, was invisible past the sixth link. Now it keeps
 * climbing, and `comboWindowFor` tightens the window as it does, so a long chain
 * is a mounting risk rather than a formality.
 */
export function comboMultFor(count: number): number {
  if (count < 2) return 1;
  if (count < 4) return 2;
  if (count < 6) return 3;
  if (count < 9) return 4;
  if (count < 13) return 5;
  if (count < 18) return 6;
  return 7 + Math.floor((count - 18) / 6);
}

/** 6.0s at x2 down to a 3.6s floor. */
export function comboWindowFor(mult: number): number {
  return Math.max(COMBO_WINDOW_MIN_S, COMBO_WINDOW_S - Math.max(0, mult - 2) * COMBO_WINDOW_STEP_S);
}

/**
 * Lifts still owed for the next rung, and the rung they buy.
 *
 * The ladder has been uncapped since v0.13 and nobody climbs it: across the
 * 31 runs published to the relays, 14 stopped on exactly x4 and only two ever
 * passed it. A headless bot that cannot read a badge or a contract reproduces
 * the same spike, so this is structural rather than signposting — the rungs
 * widen (2, 2, 3, 4, 5 lifts) exactly where the window tightens. What the HUD
 * never said is how close the next one is: at x4 with seven links banked you
 * are two lifts from x5, and the old readout showed you nothing but "x4".
 */
export function comboNextRung(count: number): { need: number; mult: number } {
  const banked = Math.max(0, Math.floor(count));
  const mult = comboMultFor(banked);
  let n = banked + 1;
  while (comboMultFor(n) === mult) n++;
  return { need: n - banked, mult: comboMultFor(n) };
}

/** Armed waves pay 50% extra when no real energy damage was taken. The opening
 * two lesson waves deliberately retain their familiar flat bonus. */
export function waveClearBonus(wave: number, damaged: boolean): number {
  return wave * (300 + (wave >= 3 && !damaged ? 150 : 0));
}

/**
 * Armed pressure keeps climbing after the old wave-13 ceiling, but slowly:
 * one extra hostile every four waves. The opening twelve-wave curve is kept
 * byte-for-byte equivalent to the shipped lesson and early-run balance.
 */
export function droneCountForWave(wave: number): number {
  if (wave < 3) return 0;
  const openingCurve = 1 + Math.floor((wave - 3) / 2);
  if (wave < 13) return Math.min(openingCurve, 6);
  return Math.min(10, 6 + Math.floor((wave - 13) / 4));
}

export function shooterCountForWave(wave: number): number {
  if (wave < 3) return 0;
  if (wave < 13) return Math.min(wave - 2, 6);
  return Math.min(9, 6 + Math.floor((wave - 13) / 4));
}

/** Base seconds between drone locks; per-drone jitter is added at the shot site. */
export function droneFireBaseForWave(wave: number): number {
  if (wave < 13) return Math.max(1.8, 4 - wave * 0.18);
  return Math.max(1.35, 1.8 - (wave - 13) * 0.05);
}

export interface ThreatSurge {
  wave: number;
  drones: number;
  shooters: number;
}

type WaveEventKind = 'wave' | 'voice' | 'drone' | 'boss' | 'storm' | 'meteor' | 'bloodmoon' | 'buildings' | 'badge';

interface WaveEventCue {
  kind: WaveEventKind;
  wave: number;
  runId: string;
  delay: number;
  fire: () => void;
}

/** The four late waves where the capped hostile population actually grows. */
export function threatSurgeForWave(wave: number): ThreatSurge | null {
  if (wave < 17) return null;
  const drones = droneCountForWave(wave);
  const shooters = shooterCountForWave(wave);
  if (drones === droneCountForWave(wave - 1) && shooters === shooterCountForWave(wave - 1)) return null;
  return { wave, drones, shooters };
}

export type HostileKind = 'drone' | 'shooter';

interface HostileRespawnCue {
  kind: HostileKind;
  runId: string;
  /** Active simulation seconds, not wall time. */
  remaining: number;
}

/** One persistent relay, upgraded at the same four tiers as the hostile curve. */
export function relayTierForWave(wave: number): number {
  if (wave < 17) return 0;
  return Math.min(4, 1 + Math.floor((wave - 17) / 4));
}

/** Existing 4s/5s replacement cadence at tier one, tightening by half a second per tier. */
export function hostileRespawnDelayForWave(kind: HostileKind, wave: number): number {
  const base = kind === 'drone' ? 4 : 5;
  const tier = relayTierForWave(wave);
  return tier === 0 ? base : base - (tier - 1) * 0.5;
}
const HULL_MAX = 5;
/**
 * Five internal cells are presented as a continuous 100% energy store. A sixth
 * cell is the capacity ceiling for POWER CORE and the patron blessing. Keeping
 * this small internal scale preserves the established combat tuning while
 * fractional drains and animal recharge remove the old fixed-lives behaviour.
 * Three separate things hand out capacity — POWER CORE, the patron's blessing,
 * and a stage departure — plus DECLINE at the draft, and the
 * ceiling used to be a bare `6` in one perk predicate and `HULL_MAX + 1` in
 * another place. One named cap, clamped at both sites that can raise the max,
 * so a fourth source cannot quietly push it to seven.
 */
export const HULL_CAP = 6;
const ENERGY_DRAIN: Record<DamageSource, number> = {
  grid: 1.35,
  meteor: 1.5,
  lightning: 1.2,
  drone: 0.8,
  local: 0.7,
  taxman: 1.1,
  unknown: 1,
};
const ANIMAL_CHARGE: Record<AnimalSpecies, number> = {
  cow: 0.18,
  goat: 0.13,
  reindeer: 0.2,
  sheep: 0.12,
  pig: 0.15,
  // A donkey is no longer a battery. It cannot be taken into the hold at all:
  // it is ferried back to its pen, and it pays this on arrival. Priced as a
  // courtesy top-up for the trip rather than the best charge on the field,
  // because the round-up bonus is where the herd's real value sits.
  donkey: 0.07,
  martian: 0.24,
  mooncow: 0.22,
};

/** Routine herd work should steady a flight, not erase every mistake. Safe
 * cores therefore accept only a trickle, damaged cores take the normal charge,
 * and the doubled emergency siphon is reserved for genuinely critical energy. */
export function animalRecoveryMultiplier(energyFraction: number): number {
  if (energyFraction <= 0.25) return 2;
  if (energyFraction <= 0.5) return 1;
  return 0.5;
}

/** Exported balance seams keep continuous energy deterministic and testable. */
export function energyDrainFor(source: DamageSource): number {
  return ENERGY_DRAIN[source];
}

export function animalChargeFor(
  species: AnimalSpecies,
  heifer = false,
  rustled = false,
  recoveryMultiplier = 1,
): number {
  const charge = ANIMAL_CHARGE[species] + (heifer ? 0.5 : 0) + (rustled ? 0.12 : 0);
  return charge * recoveryMultiplier;
}

export type CombatChargeKind = 'dodge' | 'drone' | 'shooter';
const COMBAT_CHARGE: Record<CombatChargeKind, number> = {
  // A capture steals one attacker's complete weapon cell. A near miss pays a
  // smaller kinetic skim: useful over a skilled run, never safer than a hit.
  dodge: 0.15,
  drone: ENERGY_DRAIN.drone,
  shooter: ENERGY_DRAIN.local,
};

export function combatChargeFor(kind: CombatChargeKind): number {
  return COMBAT_CHARGE[kind];
}

/** How near the Taxman has to be for the beam to bite him. Wider than an
 *  ordinary capture, and widened again by BEAM WIDTH — he is a helicopter
 *  holding station, not a cow standing still. */
export function taxmanBeamReachFor(beamRadiusMult: number): number {
  return BEAM_CAPTURE_RADIUS * beamRadiusMult + TAXMAN_BEAM_RADIUS_BONUS;
}
const INVULN_S = 2.8;
const DRONE_TELEGRAPH_S = 0.65;
const NORMIE_TELEGRAPH_S = 0.95;
// One hostile firing lane at a time. The short reset preserves pressure while
// stopping several distant enemies from collapsing their warnings into one
// unreadable volley.
const HOSTILE_FIRE_LANE_GAP_S = 0.35;
const BULLET_AXIS = new THREE.Vector3(0, 1, 0);
const BOSS_EVERY = 5;
const TAXMAN_TELEGRAPH_S = 0.9;
// Boss health is measured in seconds of beam contact. Keep the first Taxman
// brisk enough to defeat without requiring a lucky early perk draft.
const TAXMAN_BASE_BEAM_S = 4;
const TAXMAN_BEAM_S_PER_WAVE = 0.2;
const TAXMAN_BEAM_RADIUS_BONUS = 2;
const TAXMAN_ORBIT_RADIUS = 11;
/**
 * How hard he re-establishes his orbit radius, per second of error. With
 * `TAXMAN_ORBIT_RADIUS` this is the whole shape of the fight: a chase settles
 * where the pilot's closing speed matches his retreat, at
 * `TAXMAN_ORBIT_RADIUS - speed / TAXMAN_STATION_KEEPING`. At `SAUCER_SPEED`
 * that is inside the beam; at the 38% of cruise a held beam leaves you, it is
 * 6.8 against a 4.8 reach — so a pilot who flies at him beaming never touches
 * him. That is why the banner names the approach and not just the beam, and
 * `tests/taxman-profile.mjs` measures both chases rather than trusting this.
 */
const TAXMAN_STATION_KEEPING = 1.4;
const FEVER_DURATION = 10;
const STORM_EVERY = 3;
const METEOR_EVERY = 4;
const BLOOD_EVERY = 7;
const YEET_SPLAT_SPEED = 8;
const CRATE_FIRST_S = 12;
const CRATE_GAP_MIN_S = 14;
const CRATE_GAP_RAND_S = 12;
const CRATE_LIFE_S = 24;
const CRATE_DROP_ALT = 20;
const CRATE_CHUTE_SPEED = 2.6;
const CRATE_FALL_SPEED = 8;
const MAGNET_RANGE = 15;
const MAGNET_PULL = 6.5;
const WARP_FACTOR = 0.45;
const CONVOY_FIRST_S = 48;
const CONVOY_GAP_MIN_S = 80;
const CONVOY_GAP_RAND_S = 35;
const CONVOY_SPEED = 0.145; // rad/s along the ring road
const METEOR_GAP_MIN_S = 1.7;
const METEOR_GAP_RAND_S = 1.4;
const METEOR_FALL_SPEED = 34;
const METEOR_SPAWN_Y = 40;
const ORE_LIFE_S = 22;
const HOLE_RADIUS = 16;
const HOLE_HOSTILE_RADIUS = 30;
const HOLE_PULL = 9;
const HOLE_EAT_R = 1.35;
const EMP_STUN_S = 6;
const EMP_BLAST_S = 1.15;
const RELAY_FIRST_WAVE = 17;
const RELAY_SCORE = 600;
const COWRAIN_INTERVAL_S = 1.25;
const CHUTE_FALL_SPEED = 2.3;

export interface PerkDef {
  id: string;
  name: string;
  desc: string;
  tier?: 'keystone';
  /** Number already installed before this offered pick. */
  level?: number;
}

export interface PerkBuildEntry {
  id: string;
  name: string;
  level: number;
  tier?: 'keystone';
}

export type PowerKind = 'magnet' | 'shield' | 'overclock' | 'double' | 'warp'
  | 'blackhole' | 'emp' | 'cowrain';

/** Every collected/deployed payload grants its full advertised duration. EMP
 * is the exception: another pulse refreshes the six-second electronics lockout
 * instead of claiming a twelve-second stun that the actors do not receive. */
export function activatedPowerSeconds(kind: PowerKind, current: number, duration: number): number {
  return kind === 'emp' ? Math.max(current, duration) : current + duration;
}

export interface PowerDef {
  kind: PowerKind;
  name: string;
  emoji: string;
  dur: number;
  color: string;
}

export interface ActiveSystemItem {
  kind: PowerKind;
  name: string;
  emoji: string;
  frac: number;
  seconds: number;
  color: string;
}

export const POWER_DEFS: PowerDef[] = [
  { kind: 'magnet', name: 'MAGNET BEAM', emoji: '🧲', dur: 12, color: '#ff5ad0' },
  { kind: 'shield', name: 'HODL SHIELD', emoji: '🛡', dur: 10, color: '#5ab8ff' },
  { kind: 'overclock', name: 'OVERCLOCK', emoji: '⚡', dur: 12, color: '#ffe45a' },
  { kind: 'double', name: 'ORANGE COIN ×2', emoji: '🟠', dur: 15, color: '#f7931a' },
  { kind: 'warp', name: 'TIME WARP', emoji: '⏳', dur: 10, color: '#8bff5e' },
  { kind: 'blackhole', name: 'BLACK HOLE', emoji: '🕳', dur: 9, color: '#b26bff' },
  { kind: 'emp', name: 'EMP SHOCKWAVE', emoji: '💥', dur: EMP_STUN_S, color: '#7df9ff' },
  { kind: 'cowrain', name: 'COW RAIN', emoji: '🐄', dur: 10, color: '#ffd9e8' },
];

/** Not a perk id: the draft's opt-out, which trades the card for a core recharge. */
export const DECLINE_ID = 'decline';

export type SpecialKind = 'shield' | 'blackhole' | 'emp' | 'cowrain';

export interface SpecialReserveItem {
  kind: SpecialKind;
  name: string;
  emoji: string;
  dur: number;
}

export type CrashPresentation = 'falling' | 'impact' | null;

function isSpecialKind(kind: PowerKind): kind is SpecialKind {
  return kind === 'shield' || kind === 'blackhole' || kind === 'emp' || kind === 'cowrain';
}

/** Haptic tick on a phone or connected rumble-capable gamepad. */
function buzz(pattern: number | number[]): void {
  navigator.vibrate?.(pattern);
  if (navigator.getGamepads?.().some((pad) => pad?.connected)) {
    void import('./haptics').then((module) => module.default(pattern), () => undefined);
  }
}

export interface GameHooks {
  onScore(score: number): void;
  onCows(cows: number): void;
  onCombo(mult: number, active: boolean, fraction: number, need?: number, nextMult?: number): void;
  onWave(wave: number): void;
  onWaveMastery(active: boolean): void;
  onHull(hull: number, max: number): void;
  onDamage(critical: boolean): void;
  onQuota(done: number, quota: number, council: number, councilSize: number): void;
  /** Run totals the daily contract can be written against but no other HUD
   *  reads. Pushed on change rather than polled, so a contract for a system
   *  that never fires simply never reports. */
  onObjective(metric: 'donkeys' | 'combines' | 'crop', value: number): void;
  onToast(member: CultMember): void;
  onBanner(text: string): void;
  onBeamHint(animal: string): void;
  onLeaderHint(): void;
  onCarry(text: string | null): void;
  onCarryNavigation(deg: number, distance: number, delivering: boolean, target?: string): void;
  onThreat(deg: number | null, urgency: number, count: number, kind?: 'lock' | 'shot' | 'field'): void;
  onPerkOffer(perks: PerkDef[], canDecline: boolean, clearBonus?: number, streak?: number): void;
  onBuild(items: PerkBuildEntry[]): void;
  onPowerups(items: ActiveSystemItem[]): void;
  onSpecial(items: SpecialReserveItem[]): void;
  onFever(frac: number, active: boolean): void;
  onBoss(frac: number | null, hit?: boolean): void;
  onAward(def: AwardDef): void;
  onFlash(strength: number): void;
  onBiome(label: string, kicker?: string, durationMs?: number): void;
  onRitual(active: boolean): void;
  onCrash(state: CrashPresentation): void;
  onGameOver(): void;
}

interface Lifted {
  y: number;
  fallV: number;
  peakY: number;
  /** First height in this lift, used only to show honest beam progress. */
  startY?: number;
  /** Height this lift was going to complete at, kept from the last held frame.
   *  The splat is judged against it so "dropped from near the top" survives a
   *  change of flight altitude. */
  captureY?: number;
  // Set the moment the beam breaks: inherited saucer momentum. The yeet.
  vx?: number;
  vz?: number;
}

interface AnimalEnt {
  rig: THREE.Group;
  species: AnimalSpecies;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  heading: number;
  state: 'wander' | 'graze' | 'panic';
  stateT: number;
  target: THREE.Vector3;
  lift: Lifted | null;
  bobT: number;
  squashT: number;
  heifer: boolean;
  /** Kept behind the rails of The Donkey Farm rather than roaming the land. */
  paddock?: { x: number; z: number; r: number };
  rustled?: boolean;            // stolen off a convoy truck — double points
  chute: THREE.Group | null;    // cow-rain parachute while descending
  chuteY: number | null;        // absolute altitude while under canopy
  /** The Donkey Farm's loose stock. A donkey is never hold cargo: catching one
   *  slings it under the hull for the ferry home, exactly as a leader rides to
   *  the stone. `penned` is set once it is set down inside the rails. */
  aura?: THREE.Sprite;
  ferry?: 'carried' | 'descending' | null;
  penHome?: THREE.Vector3 | null;
  penned?: boolean;
}

type CultistMode = 'ground' | 'carried' | 'descending' | 'delivered';

interface CultistEnt {
  rig: CultistRig;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  heading: number;
  stateT: number;
  target: THREE.Vector3;
  lift: Lifted | null;
  squashT: number;
  mode: CultistMode;
  homeSpot: THREE.Vector3 | null;
}

/** A placard its owner let go of on the way up. */
interface Placard {
  group: THREE.Group;
  vel: THREE.Vector3;
  spin: number;
  groundY: number;
  fade: number;
}

interface GroupieEnt {
  rig: GroupieRig;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  heading: number;
  lift: Lifted | null;
  squashT: number;
}

interface BuildingEnt {
  group: THREE.Group;
  pos: THREE.Vector3;
  hp: number;
  maxHp: number;
  occupant: CultMember | null;
  baseY: number;
}

interface HideyEnt {
  rig: HideyRig;
  pos: THREE.Vector3;
  hp: number;
  maxHp: number;
  occupant: CultMember;
  baseY: number;
  wiggleT: number;  // countdown to the next tell-tale twitch
  wiggling: number; // seconds left of the current twitch
}

interface Chunk {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  rotVel: THREE.Vector3;
  life: number;
}

/** A transmission tower. Solid at any altitude, and it holds up the wires. */
interface PylonEnt {
  rig: PylonRig;
  pos: THREE.Vector3; // base, on the ground
  hp: number;
  maxHp: number;
}

/** A non-firing ground station that authorises late-run hostile replacements. */
interface RelayEnt {
  rig: FiatRelayRig;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  hp: number;
  maxHp: number;
  tier: number;
  stunT: number;
}

/** The live conductors between two towers. Dies when either tower does. */
interface SpanEnt {
  rig: SpanRig;
  from: THREE.Vector3;
  to: THREE.Vector3;
  a: PylonEnt;
  b: PylonEnt;
}

interface DroneEnt {
  rig: DroneRig;
  shadow: THREE.Mesh;
  angle: number;
  radius: number;
  dir: number;
  fireT: number;
  telegraph: number;
  lift: Lifted | null;
  pos: THREE.Vector3;
  stunT: number;
}

interface NormieEnt {
  rig: NormieRig;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  heading: number;
  cooldown: number;
  telegraph: number;
  lift: Lifted | null;
  squashT: number;
  stunT: number;
}

interface Bullet {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  source: DamageSource;
  closest?: number;
}

interface Splat {
  mesh: THREE.Mesh;
  life: number;
}

interface BossEnt {
  rig: TaxmanRig;
  pos: THREE.Vector3;
  angle: number;
  radius: number;
  hp: number;
  maxHp: number;
  fireT: number;
  telegraph: number;
  beamedT: number;
  stunT: number;
}

interface TruckCow {
  rig: THREE.Group;
  slot: THREE.Vector3;
  species: AnimalSpecies;
}

interface TruckEnt {
  rig: TruckRig;
  angle: number;        // position on the ring road
  travelled: number;    // radians driven since spawn
  cows: (TruckCow | null)[];
  honkT: number;
}

interface MeteorEnt {
  rig: MeteorRig;
  x: number;
  z: number;
  y: number;
  decal: THREE.Mesh;
}

interface OreEnt {
  rig: OreRig;
  pos: THREE.Vector3;
  lift: Lifted | null;
  life: number;
}

interface HoleEnt {
  rig: BlackHoleRig;
  pos: THREE.Vector3;
  age: number;
}

interface PerkImpl extends PerkDef {
  can(g: Game): boolean;
  apply(g: Game): void;
}

interface CrateEnt {
  def: PowerDef;
  rig: SupplyDropRig;
  shadow: THREE.Mesh;
  pos: THREE.Vector3;
  fallY: number | null; // descending from the mothership; null once landed
  chute: THREE.Group | null;
  airCatch: boolean;
  lift: Lifted | null;
  life: number;
  spin: number;
}

interface Strike {
  x: number;
  z: number;
  t: number;
  phase: 'telegraph' | 'bolt';
  decal: THREE.Mesh;
  bolt: THREE.Group | null;
}

export class Game {
  phase: 'idle' | 'playing' | 'over' = 'idle';
  score = 0;
  wave = 1;
  stage = 1;          // which leg of the tour: assemble the council to advance
  blessed = false;    // set by the menu when a patron paid before launch
  hull = HULL_MAX;
  quota = 0;
  quotaDone = 0;
  cowsAbducted = 0;
  cultistsAbducted = 0;
  groupiesAbducted = 0;
  dronesAbducted = 0;
  bestCombo = 1;
  councilIds = new Set<string>();
  councilAwarded = false;
  /** The Donkey Farm round-up pays once per land, however many times a pilot
   *  beams the herd back out afterwards. */
  donkeyBonusAwarded = false;
  /** True only after the seventh Council is delivered on the Moon. */
  tourComplete = false;
  /** True only when the pilot deliberately banks a live flight from pause. */
  extracted = false;
  runId = '';
  /** Simulation time that was actually playable; pause and perk drafts do not count. */
  runTimeS = 0;

  private saucer: SaucerRig;
  private saucerPos = new THREE.Vector3(0, 0, 6);
  private saucerVel = new THREE.Vector3();
  private saucerY = 0;
  private altCmd = CRUISE_ALT;
  /** Unbroken seconds spent under the lake surface. See updateCarWash. */
  private carWashT = 0;
  /** Total mass under the beam or aboard this frame. Drives altitude. */
  private beamLoad = 0;
  private invulnT = 0;
  private beamActive = false;
  /** Previous simulation frame's strongest active lift, for the beam rings. */
  private beamLiftProgress = 0;
  private beamLiftStage = 0;

  // Kept as `cows` because published score claims use the established `cows`
  // field; entries themselves now carry their actual animal species.
  private cows: AnimalEnt[] = [];
  private cultists: CultistEnt[] = [];
  private groupies: GroupieEnt[] = [];
  private fanStage = 0;
  private drones: DroneEnt[] = [];
  private normies: NormieEnt[] = [];
  private buildings: BuildingEnt[] = [];
  private hideys: HideyEnt[] = [];
  private pylons: PylonEnt[] = [];
  private spans: SpanEnt[] = [];
  private stumps: THREE.Group[] = [];
  private arcs: { group: THREE.Group; life: number }[] = [];
  private relay: RelayEnt | null = null;
  private combine: FieldDefense | null = null;
  private crash: import('./crash').SaucerCrash | null = null;
  combinesDestroyed = 0;
  private gridHitT = 0;
  private gridWarnT = 0;
  private pylonHintDone = false;
  // Let the land title finish before the grid tutorial takes over the centre
  // of the screen. Without this, pylon lands open with two stacked overlays.
  private pylonHintDelayT = 0;
  // Public so headless tests can flip the mobile framing on.
  mobileView = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  // CSS already calms the HUD; this keeps the 3D camera aligned with the same
  // operating-system preference. Public so the app can react to live changes.
  reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  private chunks: Chunk[] = [];
  private bullets: Bullet[] = [];
  private hostileFireLaneT = 0;
  private dodgeT = 0;
  private splats: Splat[] = [];
  private placards: Placard[] = [];
  private poofs: PoofPool;
  normiesAbducted = 0;
  cropBonus = 0;
  private cropPopPending = 0;
  private cropPopT = 0;
  private hoverPos = new THREE.Vector3();
  private effectPos = new THREE.Vector3();
  private threatView = new THREE.Vector3();
  private threatNdc = new THREE.Vector3();
  private fieldThreatPos = new THREE.Vector3();
  private hoverT = 0;
  private stampTiers = new Map<string, number>();

  private comboCount = 0;
  private comboT = 0;
  private comboMult = 1;
  private comboHudBucket = -1;
  private cultAbductStreak = 0;
  private threatSurge: (ThreatSurge & { delay: number }) | null = null;
  private waveEvents: WaveEventCue[] = [];
  private hostileRespawns: HostileRespawnCue[] = [];

  private timeS = 0;

  // v0.4: perk draft, 600B fever, boss, juice.
  paused = false;
  userPaused = false;
  private pendingPerks: PerkDef[] | null = null;
  private pendingDecline = false;
  private dailyStreak = 0;
  private beamRadiusMult = 1;
  private speedMult = 1;
  private liftMult = 1;
  private scoreMult = 1;
  private beamMoveMult = 1;
  private bulletSlow = 1;
  private feverGainMult = 1;
  private hullMax = HULL_MAX;
  private fever = 0;
  private feverT = 0;
  private lastFeverShown = -1;
  private lastFeverActive = false;
  private shakeMag = 0;
  private hitstopT = 0;
  private waveDamaged = false;
  /** One contextual recovery lesson per flight, only after energy is genuinely low. */
  private energyHintDone = false;
  /** Cyan intake pulse on the saucer's existing rim lights; adds no geometry. */
  private energyPulseT = 0;
  /** Optional post-launch biome objectives stay out of the title bundle. */
  private task: import('./challenges').LandChallengeController | null = null;
  lastDamageSource: DamageSource = 'unknown';
  private boss: BossEnt | null = null;
  private taxmanBuilder: typeof import('./taxman').buildTaxman | null = null;
  private hostileBeamHitT = 0;
  private pops: ScorePopPool;

  // v0.7: supply-drop power-ups.
  private crates: CrateEnt[] = [];
  private crateT = CRATE_FIRST_S;
  private powerT = new Map<PowerKind, number>();
  private powerHudT = 0;
  private specialQueue: PowerDef[] = [];
  private shield: ShieldRig;
  private shieldPopT = 0;   // ignition overshoot animation
  private shieldHitT = 0;   // flare when the shield eats a hit
  private lastShieldT = 0;
  private waterSpill: THREE.Sprite;

  // v0.5: storm, reflection, hints, badges.
  stormActive = false;
  private stormLevel = 0;
  private strikeT = 0;
  private strikes: Strike[] = [];
  private stormHullAtStart = 0;
  private reflection: THREE.Group;
  private reflectionMats: THREE.MeshBasicMaterial[] = [];
  private reflectionRipples: THREE.Mesh[] = [];
  private perksTaken = 0;
  private perkLevels = new Map<string, number>();
  runAwards: string[] = [];
  private carriedT = 0;
  private carryHints = 0;
  private beamHintDone = false;
  private leaderHintArmed = false;
  private leaderHintAtMs = -1;
  /** The beam has been made to work on something living. Ferrying a donkey
   *  proves it just as well as taking a cow into the hold, and the first-run
   *  handoff to the leader has to fire either way. */
  private beamProven = false;
  // Player-facing instruction cadence follows active wall time. The fixed-step
  // simulation deliberately slows under frame pressure; controls must not take
  // fifteen seconds to introduce themselves on a struggling phone.
  private presentationMs = 0;
  private presentationFresh = true;
  /** Active-play countdown to a nearby herd call. Beam reactions remain
   * immediate; this makes each land audible even before the first pickup. */
  private herdCallT = 0;
  private buildingHintDone = false;
  private welcomed = false;

  // v0.9: cabinet attract mode — the saucer flies itself.
  demoMode = false;
  private botThinkT = 0;
  private botTargetPos = new THREE.Vector3();
  private botHasTarget = false;
  private botBeamOn = false;
  private botSpecialT = 0;

  // v0.8: convoys, meteors, blood moons, singularities, rituals.
  private trucks: TruckEnt[] = [];
  private convoyT = CONVOY_FIRST_S;
  private meteors: MeteorEnt[] = [];
  private meteorT = 0;
  meteorWave = false;
  private ores: OreEnt[] = [];
  private hole: HoleEnt | null = null;
  private holeConsumed = 0;
  private empRing: EmpRig | null = null;
  private empRingT = 0;
  private rainCowT = 0;
  bloodMoon = false;
  private bloodLevel = 0;
  private howlT = 0;
  private ritualT = 0;
  private ritualMinUntilMs = 0;
  private ritualLastUpdateMs = 0;
  private ritualMaxT = 0;
  private ritualVoiceDone = true;
  private ritualSerial = 0;
  private ritualPillar: THREE.Mesh | null = null;
  private ritualRings: THREE.Mesh[] = [];
  private finaleStartedMs = 0;
  private finaleUntilMs = 0;
  private finaleSaucerY = 0;
  private arrivalUntilMs = 0;
  private crateGapMult = 1;
  private cultScoreMult = 1;
  private orangeAfterburner = false;
  private sacredStatic = false;
  private herdDividend = false;
  private secondSignatureInstalled = false;
  private secondSignatureReady = false;

  private static readonly PERK_POOL: PerkImpl[] = [
    { id: 'beam', name: 'WIDER BEAM', desc: 'Tractor aperture +25%. More cone, more animals.',
      can: (g) => g.beamRadiusMult < 1.95, apply: (g) => { g.beamRadiusMult *= 1.25; } },
    { id: 'thrust', name: 'OVERDRIVE THRUSTERS', desc: 'Saucer speed +15%. Fiat cannot catch you.',
      can: (g) => g.speedMult < 1.6, apply: (g) => { g.speedMult *= 1.15; } },
    { id: 'hull', name: 'POWER CORE', desc: '+20% capacity and charge now.',
      can: (g) => g.hullMax < HULL_CAP,
      apply: (g) => {
        g.hullMax = Math.min(g.hullMax + 1, HULL_CAP);
        g.hull = Math.min(g.hull + 1, g.hullMax);
        g.hooks.onHull(g.hull, g.hullMax);
      } },
    { id: 'greed', name: 'NUMBER GO UP', desc: 'All points ×1.25. It is the mission.',
      can: (g) => g.scoreMult < 2.4, apply: (g) => { g.scoreMult *= 1.25; } },
    { id: 'winch', name: 'MAGNET WINCH', desc: 'Lift speed +30%. Yoink.',
      can: (g) => g.liftMult < 2.2, apply: (g) => { g.liftMult *= 1.3; } },
    { id: 'timelock', name: 'TIME LOCK', desc: 'Enemy fire 20% slower. Confiscation delayed.',
      can: (g) => g.bulletSlow > 0.55, apply: (g) => { g.bulletSlow *= 0.8; } },
    { id: 'stabilisers', name: 'BEAM STABILISERS', desc: 'Fly 50% faster while beaming.',
      can: (g) => g.beamMoveMult < 2.2, apply: (g) => { g.beamMoveMult *= 1.5; } },
    { id: 'momentum', name: 'CULT MOMENTUM', desc: '600B meter charges 25% faster.',
      can: (g) => g.feverGainMult < 2, apply: (g) => { g.feverGainMult *= 1.25; } },
    { id: 'salvage', name: 'SALVAGE RIGHTS', desc: 'Supply drops land 35% more often.',
      can: (g) => g.crateGapMult > 0.4, apply: (g) => { g.crateGapMult *= 0.65; } },
    { id: 'prophet', name: 'PROPHET MOTIVE', desc: 'Cult deliveries worth 50% more.',
      can: (g) => g.cultScoreMult < 2.3, apply: (g) => { g.cultScoreMult *= 1.5; } },
    { id: 'afterburner', name: 'ORANGE AFTERBURNER', desc: 'Reaching x4 grants 4s of OVERCLOCK.', tier: 'keystone',
      can: (g) => !g.orangeAfterburner, apply: (g) => { g.orangeAfterburner = true; } },
    { id: 'static', name: 'SACRED STATIC', desc: 'Every third leader delivery fires an EMP.', tier: 'keystone',
      can: (g) => !g.sacredStatic, apply: (g) => { g.sacredStatic = true; } },
    { id: 'dividend', name: 'HERD DIVIDEND', desc: 'Every sixth animal calls in a supply drop.', tier: 'keystone',
      can: (g) => !g.herdDividend, apply: (g) => { g.herdDividend = true; } },
    { id: 'signature', name: 'SECOND SIGNATURE', desc: 'Survive one lethal drain on reserve energy + shield.', tier: 'keystone',
      can: (g) => !g.secondSignatureInstalled,
      apply: (g) => { g.secondSignatureInstalled = true; g.secondSignatureReady = true; } },
  ];

  constructor(private world: World, private hooks: GameHooks) {
    this.saucer = buildSaucer();
    this.world.scene.add(this.saucer.group);
    this.world.scene.add(this.saucer.shadow);
    this.poofs = new PoofPool(world.scene);
    this.pops = new ScorePopPool(world.scene);
    this.reflection = this.buildReflection();
    // After buildReflection so the lake ghost never mirrors the bubble.
    this.shield = buildShieldBubble();
    this.shield.group.visible = false;
    this.saucer.group.add(this.shield.group);
    // Beam light spilling onto the lake surface.
    this.waterSpill = makeGlowSprite(5.5);
    this.waterSpill.visible = false;
    this.world.scene.add(this.waterSpill);
    this.saucerY = this.world.heightAt(this.saucerPos.x, this.saucerPos.z) + SAUCER_ALT;
    this.updateSaucerVisual(0);
    this.placeCamera(1);
  }

  /** Ghost copy of the saucer mirrored beneath the lake surface. */
  private buildReflection(): THREE.Group {
    const clone = this.saucer.group.clone(true);
    clone.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        const src = o.material as THREE.Material & { color?: THREE.Color };
        const mat = new THREE.MeshBasicMaterial({
          color: src.color ? src.color.clone().multiplyScalar(0.55) : new THREE.Color('#4a5568'),
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        o.material = mat;
        o.renderOrder = 1;
        this.reflectionMats.push(mat);
      } else if (o instanceof THREE.Sprite || o instanceof THREE.Light) {
        o.visible = false;
      }
    });
    clone.visible = false;
    this.world.scene.add(clone);
    return clone;
  }

  /** Warm the first mid-run reward without putting its renderer on the title path. */
  async warmSupply(): Promise<void> {
    try { await loadSupply(); } catch { /* a later spawn retries the chunk */ }
  }

  private async warmWaterDetail(): Promise<void> {
    try {
      const runtime = await loadWaterDetail();
      runtime.finishWater(this.world.water);
      if (this.reflectionRipples.length === 0) {
        this.reflectionRipples = runtime.buildReflectionRipples(this.world.scene);
      }
    } catch { /* the base water and reflection remain fully playable */ }
  }

  /** Finish the known first-interaction kits while the launch gate still owns
   * the screen. Late encounters are tiny beside the opening cast, but parsing
   * them on first use was enough to hitch a busy phone frame. Warm their code
   * here too; deferred retries keep every individual enhancement optional. */
  async warmGameplay(): Promise<void> {
    await Promise.all([
      this.warmSupply(),
      this.warmWaterDetail(),
      loadGroupies().then((runtime) => runtime.prewarmGroupies()).catch(() => undefined),
      loadChallenges().catch(() => undefined),
      loadFieldDefense().catch(() => undefined),
      loadLateWorld().catch(() => undefined),
      loadCrash().catch(() => undefined),
      this.warmTaxman().catch(() => undefined),
    ]);
  }

  start(startLand?: BiomeId): void {
    void this.warmGameplay();
    // A fresh run can open in any land the player has unlocked (menu picker);
    // omitted means "wherever the world already is" (internal restarts, demo).
    if (startLand && startLand !== this.world.biome) this.world.applyBiome(startLand);
    setAmbientBiome(this.world.biome);
    // Clear any previous run.
    for (const c of this.cows) { this.world.scene.remove(c.rig, c.shadow); }
    for (const c of this.cultists) { this.world.scene.remove(c.rig.group, c.shadow); }
    for (const g of this.groupies) { this.world.scene.remove(g.rig.group, g.shadow); }
    for (const d of this.drones) { this.world.scene.remove(d.rig.group, d.shadow); }
    for (const n of this.normies) { this.world.scene.remove(n.rig.group, n.shadow); }
    for (const b of this.bullets) { this.world.scene.remove(b.mesh); }
    for (const sp of this.splats) { this.world.scene.remove(sp.mesh); }
    for (const card of this.placards) this.disposePlacard(card);
    for (const bd of this.buildings) { this.world.scene.remove(bd.group); }
    for (const hd of this.hideys) { this.world.scene.remove(hd.rig.group); }
    this.clearRelay();
    this.clearCombine();
    this.clearCrash();
    this.clearPylons();
    for (const ch of this.chunks) { this.world.scene.remove(ch.mesh); }
    for (const cr of this.crates) { this.world.scene.remove(cr.rig.group, cr.shadow); }
    for (const t of this.trucks) { this.world.scene.remove(t.rig.group); }
    for (const m of this.meteors) { this.world.scene.remove(m.rig.group, m.decal); }
    for (const o of this.ores) { this.world.scene.remove(o.rig.group); }
    this.closeBlackHole();
    if (this.empRing) { this.world.scene.remove(this.empRing.group); this.empRing = null; }
    if (this.ritualPillar) { this.world.scene.remove(this.ritualPillar); this.ritualPillar = null; }
    for (const ring of this.ritualRings) this.world.scene.remove(ring);
    this.ritualRings = [];
    this.trucks = [];
    this.convoyT = CONVOY_FIRST_S;
    this.meteors = [];
    this.meteorT = 0;
    this.meteorWave = false;
    this.ores = [];
    this.holeConsumed = 0;
    this.empRingT = 0;
    this.rainCowT = 0;
    this.bloodMoon = false;
    this.howlT = 0;
    this.ritualT = 0;
    this.ritualMinUntilMs = 0;
    this.ritualLastUpdateMs = 0;
    this.ritualMaxT = 0;
    this.ritualVoiceDone = true;
    this.ritualSerial++;
    this.tourComplete = false;
    this.extracted = false;
    this.finaleStartedMs = 0;
    this.finaleUntilMs = 0;
    this.finaleSaucerY = 0;
    this.arrivalUntilMs = 0;
    this.hooks.onRitual(false);
    this.crateGapMult = 1;
    this.cultScoreMult = 1;
    this.orangeAfterburner = false;
    this.sacredStatic = false;
    this.herdDividend = false;
    this.secondSignatureInstalled = false;
    this.secondSignatureReady = false;
    this.crates = [];
    this.crateT = CRATE_FIRST_S;
    this.powerT.clear();
    this.powerHudT = 0;
    this.specialQueue = [];
    consumeSpecialPress();
    this.hooks.onPowerups([]);
    this.hooks.onSpecial([]);
    this.cows = [];
    this.cultists = [];
    this.groupies = [];
    this.fanStage = 0;
    this.drones = [];
    this.normies = [];
    this.buildings = [];
    this.hideys = [];
    this.chunks = [];
    this.bullets = [];
    this.hostileFireLaneT = 0;
    this.dodgeT = 0;
    this.splats = [];
    this.placards = [];
    this.hooks.onCarry(null);
    this.hooks.onThreat(null, 0, 0);
    this.normiesAbducted = 0;
    this.cropBonus = 0;
    this.cropPopPending = 0;
    this.cropPopT = 0;
    this.hoverT = 0;
    this.stampTiers.clear();
    this.combinesDestroyed = 0;

    // v0.4 run state.
    if (this.boss) { this.world.scene.remove(this.boss.rig.group); this.boss = null; }
    this.hooks.onBoss(null);
    this.beamRadiusMult = 1;
    this.speedMult = 1;
    this.liftMult = 1;
    this.scoreMult = 1;
    this.beamMoveMult = 1;
    this.bulletSlow = 1;
    this.feverGainMult = 1;
    this.hullMax = HULL_MAX;
    this.fever = 0;
    this.feverT = 0;
    this.lastFeverShown = -1;
    this.lastFeverActive = false;
    this.hooks.onFever(0, false);
    this.shakeMag = 0;
    this.hitstopT = 0;
    this.waveDamaged = false;
    this.energyHintDone = false;
    this.energyPulseT = 0;
    this.hostileBeamHitT = 0;
    this.beamLiftProgress = 0;
    this.beamLiftStage = 0;
    this.paused = false;
    this.pendingPerks = null;
    this.pendingDecline = false;
    this.endStorm(false);
    this.perksTaken = 0;
    this.perkLevels.clear();
    this.hooks.onBuild([]);
    this.runAwards = [];
    this.carriedT = 0;
    this.carryHints = 0;
    this.beamHintDone = false;
    this.leaderHintArmed = false;
    this.leaderHintAtMs = -1;
    this.beamProven = false;
    this.presentationMs = 0;
    this.presentationFresh = true;
    this.pylonHintDone = false;
    this.pylonHintDelayT = 4.4;
    this.userPaused = false;
    // Attract mode must not consume the real pilot's welcome and opening
    // lesson. Nor should an unlocked later-land start introduce an experienced
    // pilot as if they had just arrived: this greeting belongs to Valley.
    const justWelcomed = !this.welcomed && !this.demoMode && this.world.biome === 'valley';
    this.herdCallT = justWelcomed ? 6.5 : 3.2;
    if (justWelcomed) {
      this.welcomed = true;
      this.leaderHintArmed = true;
      setTimeout(() => {
        const openingStillLive = () => this.phase === 'playing' && this.world.biome === 'valley' &&
          this.stage === 1 && this.wave === 1 && this.runTimeS < 8;
        if (openingStillLive()) {
          // Re-check at the actual speaker start as well as at this timer. If
          // another line owns the voice bus, onboarding must not emerge from
          // the queue several waves after its moment has passed.
          playVoiceClip(VOICE.ttsWelcome, 0.9, 1, 0, undefined, false, openingStillLive);
        }
      }, 900);
    }

    this.phase = 'playing';
    this.score = 0;
    this.wave = 1;
    this.stage = 1;
    this.carWashT = 0;
    this.hull = HULL_MAX;
    this.shieldPopT = 0;
    this.shieldHitT = 0;
    this.lastShieldT = 0;
    // Patron's blessing: pay the saucer's fuel bill, fly overcharged.
    if (this.blessed && !this.demoMode) {
      this.hullMax = Math.min(HULL_MAX + 1, HULL_CAP);
      this.hull = this.hullMax;
      const shield = POWER_DEFS.find((d) => d.kind === 'shield')!;
      this.storeSpecial({ ...shield, dur: 14 }, false);
      // Slot the announcement after the welcome/biome lines have had their say.
      setTimeout(() => {
        if (this.phase !== 'playing') return;
        this.hooks.onBanner("🙏 PATRON'S BLESSING — +20% CORE · SHIELD STORED");
        playVoiceClip(VOICE.ttsBlessed, 0.95);
      }, justWelcomed ? 6600 : 2400);
    }
    this.quotaDone = 0;
    this.quota = this.quotaFor(1);
    this.cowsAbducted = 0;
    this.cultistsAbducted = 0;
    this.groupiesAbducted = 0;
    this.dronesAbducted = 0;
    this.bestCombo = 1;
    this.councilIds.clear();
    this.councilAwarded = false;
    this.donkeyBonusAwarded = false;
    this.comboCount = 0;
    this.comboMult = 1;
    this.comboT = 0;
    this.comboHudBucket = -1;
    this.cultAbductStreak = 0;
    this.threatSurge = null;
    this.waveEvents = [];
    this.hostileRespawns = [];
    this.invulnT = 0;
    this.lastDamageSource = 'unknown';
    this.runId = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 13)
      : `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
    this.runTimeS = 0;
    this.saucerPos.set(0, 0, 6);
    this.saucerVel.set(0, 0, 0);
    this.world.corn.reset();
    // Each biome opens with its own bed; wave five may change the current land
    // to "$600B HOLE", but the next destination takes its music back.
    setMusicTrack(BIOME_TRACK[this.world.biome]);
    // Announce tonight's harvest: title card now, voice line once the
    // (first-run-only) welcome has had its say.
    const openingAnimal = animalForBiome(this.world.biome);
    this.hooks.onBiome(
      this.world.biomeLabel,
      `${openingAnimal.emoji} ${openingAnimal.plural.toUpperCase()} IN RANGE`,
    );
    const biomeVoice = BIOME_VOICE[this.world.biome];
    const announcing = this.world.biome;
    const announcingRun = this.runId;
    setTimeout(() => {
      if (this.phase === 'playing' && this.world.biome === announcing && this.runId === announcingRun) {
        playVoiceClip(biomeVoice, 0.95);
      }
    }, justWelcomed ? 4200 : 700);
    // Off-world, the change in the beam's behaviour is the first thing a player
    // will notice and the last thing they would guess. Say it out loud.
    if (this.gravity < 0.6) {
      const pull = Math.round(this.gravity * 100);
      // Captured, and checked again on the way out: a departure inside the
      // delay would otherwise announce the land you have just left.
      const land = this.world.biome;
      const gravityRun = this.runId;
      setTimeout(() => {
        if (this.phase !== 'playing' || this.world.biome !== land || this.runId !== gravityRun) return;
        this.hooks.onBanner(`🌑 LOW GRAVITY — ${pull}% PULL · WHAT YOU DROP, HANGS`);
        playVoiceClip(VOICE.ttsLowG, 0.95);
      }, justWelcomed ? 9000 : 5600);
    }

    // The first satisfying action should never depend on a lucky spawn. Put a
    // local animal just off the nose so a new player can move a fraction and beam it.
    this.spawnCow(true, new THREE.Vector3(this.saucerPos.x + 2.2, 0, this.saucerPos.z - 0.7));
    for (let i = 1; i < HERD_TARGET; i++) this.spawnCow(true);
    // The leaders and their official 3D models are the game's identity, not a
    // late-map secret. Put the first one visibly between launch and the stone;
    // the remaining leaders still scatter across the whole land.
    const stoneDx = this.world.stoneAt.x - this.saucerPos.x;
    const stoneDz = this.world.stoneAt.z - this.saucerPos.z;
    const stoneDist = Math.max(1, Math.hypot(stoneDx, stoneDz));
    const towardX = stoneDx / stoneDist;
    const towardZ = stoneDz / stoneDist;
    const openingLeader = new THREE.Vector3(
      // Keep the lateral offset inside the portrait safe frame at spawn. On a
      // heavily loaded phone, presentation time can reach the lesson before
      // enough fixed simulation steps have let this leader walk any closer.
      this.saucerPos.x + towardX * 7.5 - towardZ * 2,
      0,
      this.saucerPos.z + towardZ * 7.5 + towardX * 2,
    );
    const openingCast = openingMembersForBiome(this.world.biome);
    this.spawnCultist(openingLeader, openingCast[0]);
    for (let i = 1; i < CULTIST_TARGET; i++) this.spawnCultist(undefined, openingCast[i]);
    // The volunteers wait for the first successful animal pickup, keeping the
    // arrival title and beam lesson visually unambiguous.
    this.spawnBuildings();
    this.spawnHideys();
    this.spawnPylons();

    this.hooks.onScore(0);
    this.hooks.onCows(0);
    this.hooks.onWave(1);
    this.hooks.onWaveMastery(false);
    this.hooks.onHull(this.hull, this.hullMax);
    this.hooks.onQuota(0, this.quota, 0, COUNCIL_SIZE);
    this.hooks.onCombo(1, false, 0);
  }

  get durationMs(): number {
    return this.runTimeS * 1000;
  }

  /** Called once per visible rendered frame; gameplay remains fixed-step. */
  advancePresentationTime(frameMs: number): void {
    if (this.phase !== 'playing' || this.demoMode || this.paused || this.userPaused) return;
    // Count all time for which the game was visibly presenting a frame. The
    // old one-second cap made the five-second first hint arrive at ten seconds
    // on a cold/slow renderer because every long frame silently lost time.
    // Hidden tabs and pauses are already excluded by the gates above and the
    // visibility handler resets the RAF baseline on return.
    // start() can do synchronous world setup between two RAF callbacks. The
    // next delta began before the run existed, so counting all of it can make
    // the five-second beam lesson appear early. Retain at most one ordinary
    // frame from that first mixed interval; later visible stalls count fully.
    const visibleFrameMs = this.presentationFresh
      ? Math.min(Math.max(0, frameMs), 1000 / 30)
      : Math.max(0, frameMs);
    this.presentationFresh = false;
    this.presentationMs += visibleFrameMs;
    // The crash is a short visible cinematic, not gameplay simulation. Driving
    // it from capped fixed steps made a 2.7s death last 20s+ when a phone or a
    // busy software renderer delivered only a handful of frames. Advance it by
    // visible wall time so impact, wreck hold and report keep their real pace.
    if (this.hull <= 0 && this.crash?.update(Math.max(0, frameMs) / 1000)) {
      this.finishCrash();
    }
  }

  private quotaFor(wave: number): number {
    return 4 + wave * 2;
  }

  private dronesFor(wave: number): number {
    return droneCountForWave(wave);
  }

  private normiesFor(wave: number): number {
    return shooterCountForWave(wave);
  }

  /** A nearby, level, open patch: visible enough to act on, never blocking the stone or road. */
  private relaySpot(): THREE.Vector3 {
    // Random retries occasionally exhausted the whole budget in a crowded
    // Town and fell back to an off-screen point. Sweep the complete nearby
    // annulus instead and choose the safest candidate nearest screen centre.
    // The random phase keeps repeat deployments from becoming rote without
    // making visibility a lottery.
    this.world.camera.updateMatrixWorld();
    const phase = Math.random() * Math.PI * 2;
    let best: { score: number; pos: THREE.Vector3 } | null = null;
    for (let r = 18; r <= 34; r += 2) {
      for (let step = 0; step < 96; step++) {
        const a = phase + step / 96 * Math.PI * 2;
        const x = this.saucerPos.x + Math.cos(a) * r;
        const z = this.saucerPos.z + Math.sin(a) * r;
        if (Math.hypot(x, z) > PLAY_RADIUS - 9) continue;
        if (Math.hypot(x - this.world.stoneAt.x, z - this.world.stoneAt.z) < 16) continue;
        if (Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r + 5) continue;
        if (onRoad(x, z, 4)) continue;
        const ground = groundRange(x, z, 2);
        if (ground.high - ground.low > 0.72) continue;
        const blocked = this.buildings.some((b) => Math.hypot(x - b.pos.x, z - b.pos.z) < 6) ||
          this.hideys.some((h) => Math.hypot(x - h.pos.x, z - h.pos.z) < 5) ||
          this.pylons.some((p) => Math.hypot(x - p.pos.x, z - p.pos.z) < 7);
        if (blocked) continue;
        // This object is introduced during a crowded phone fight. Keep its dish
        // in the central play strip, below the HUD and above the touch controls,
        // rather than merely placing it somewhere near the saucer in world space.
        const view = new THREE.Vector3(x, ground.high + 4.25, z).project(this.world.camera);
        if (view.z < -1 || view.z > 1 || Math.abs(view.x) > 0.58 || view.y < -0.25 || view.y > 0.28) continue;
        const score = Math.abs(view.x) * 1.4 + Math.abs(view.y) + Math.abs(r - 24) * 0.008 +
          (ground.high - ground.low) * 0.05;
        if (!best || score < best.score) best = { score, pos: new THREE.Vector3(x, ground.high, z) };
      }
    }
    if (best) return best.pos;

    // All biome maps deliberately grade the central ferry lane. This should
    // only be reachable if future set pieces occupy the entire annulus; keep
    // the emergency target near the saucer and visible instead of reverting to
    // an arbitrary point anywhere in the field.
    const x = this.saucerPos.x;
    const z = this.saucerPos.z - 18;
    return new THREE.Vector3(x, groundRange(x, z, 2).high, z);
  }

  private placeRelay(relay: RelayEnt): void {
    relay.pos.copy(this.relaySpot());
    relay.rig.group.position.copy(relay.pos);
    relay.shadow.position.set(relay.pos.x, relay.pos.y + 0.04, relay.pos.z);
    this.world.scene.add(relay.rig.group, relay.shadow);
  }

  private deployRelay(wave: number): void {
    const tier = relayTierForWave(wave);
    if (tier === 0) return;
    const maxHp = 2.2 + (tier - 1) * 0.35;
    if (this.relay) {
      this.relay.tier = tier;
      this.relay.maxHp = maxHp;
      this.relay.hp = maxHp;
      this.relay.stunT = 0;
      return;
    }
    if (!lateWorldRuntime) {
      const run = this.runId;
      void loadLateWorld().then(() => {
        if (this.phase === 'playing' && this.runId === run) this.deployRelay(wave);
      });
      return;
    }
    const relay: RelayEnt = {
      rig: lateWorldRuntime.buildFiatRelay(),
      shadow: makeBlobShadow(2.25),
      pos: new THREE.Vector3(),
      hp: maxHp,
      maxHp,
      tier,
      stunT: 0,
    };
    this.relay = relay;
    this.placeRelay(relay);
  }

  private clearRelay(): void {
    if (!this.relay) return;
    this.world.scene.remove(this.relay.rig.group, this.relay.shadow);
    this.relay = null;
  }

  private destroyRelay(reason: 'beam' | 'blackhole'): void {
    const relay = this.relay;
    if (!relay) return;
    this.relay = null;
    this.world.scene.remove(relay.rig.group, relay.shadow);
    if (reason === 'blackhole') {
      this.poofs.burst(relay.pos.clone().setY(relay.pos.y + 1.2), 'blue', 16);
      sfxGulp(0.72);
      this.addShake(0.45);
    } else {
      this.explodeAt(relay.pos, relay.rig.group, ['#737b86', '#4b5058', '#ff4030']);
    }
    const pts = this.addScore(RELAY_SCORE + (relay.tier - 1) * 200);
    this.popScore(relay.pos.x, relay.pos.y + 3.2, relay.pos.z, pts);
    this.addFever(0.18);
    this.hooks.onBanner(`📡 FIAT RELAY DOWN · REPLACEMENTS CUT  +${pts}`);
  }

  private updateRelay(dt: number): void {
    const relay = this.relay;
    if (!relay) return;
    relay.stunT = Math.max(0, relay.stunT - dt);
    const stunned = relay.stunT > 0;
    const activeColor = stunned ? '#67c8ff' : '#ff4030';
    relay.rig.signalMat.color.set(activeColor);
    relay.rig.fieldMat.color.set(activeColor);
    relay.rig.glow.material.color.set(activeColor);
    relay.rig.dish.rotation.y += dt * (stunned ? 0.22 : 1.25 + relay.tier * 0.16);
    const pulse = 1 + Math.sin(this.timeS * (stunned ? 3 : 7)) * 0.08;
    relay.rig.sweep.scale.setScalar((1 + (relay.tier - 1) * 0.08) * pulse);
    relay.rig.column.scale.y = 0.92 + Math.sin(this.timeS * 4) * 0.08;
    relay.rig.fieldMat.opacity = stunned ? 0.08 : 0.16 + Math.sin(this.timeS * 6) * 0.045;
    relay.rig.glow.material.opacity = stunned ? 0.28 : 0.45 + Math.sin(this.timeS * 5) * 0.1;

    const flat = Math.hypot(relay.pos.x - this.saucerPos.x, relay.pos.z - this.saucerPos.z);
    if (this.beamActive && flat < 3.6) {
      relay.hp -= dt;
      relay.rig.signalMat.color.set('#f7931a');
      sfxRumble();
      const stress = 1 - Math.max(0, relay.hp) / relay.maxHp;
      relay.rig.group.rotation.z = Math.sin(this.timeS * 26) * stress * 0.055;
      if (relay.hp <= 0) this.destroyRelay('beam');
    } else {
      relay.rig.group.rotation.z *= Math.max(0, 1 - dt * 7);
    }
  }

  /* ---------------- field defence ---------------- */

  private clearCombine(): void {
    this.combine?.dispose();
    this.combine = null;
  }

  private clearCrash(): void {
    this.crash?.dispose();
    this.crash = null;
    this.hooks.onCrash(null);
  }

  /** Freeze at zero energy, then let the deferred crash sequence finish in-world. */
  private beginCrash(): void {
    if (this.crash) return;
    this.beamActive = false;
    this.saucer.beam.group.visible = false;
    this.silenceRunBeds();
    // Browser controls and the cinematic rail must react to the lethal hit,
    // not wait for the deferred crash chunk to finish downloading. The drive
    // failure belongs here for the same reason: it must start on the hit.
    this.hooks.onCrash('falling');
    sfxCrashFall(this.panOf(this.saucerPos.x));
    const run = this.runId;
    void loadCrash().then(({ SaucerCrash }) => {
      if (this.phase !== 'playing' || this.runId !== run || this.hull > 0) return;
      this.crash = new SaucerCrash(this);
    }, () => {
      if (this.phase === 'playing' && this.runId === run) this.finishCrash();
    });
  }

  private finishCrash(): void {
    this.phase = 'over';
    this.hooks.onGameOver();
  }

  /** Tier three stops being free money from wave five: one machine leaves the
   * nearest safe farm and attacks the artwork rather than the saucer. */
  private challengeFormation(key: string, cx: number, cz: number): void {
    if (this.demoMode || this.wave < 5 || (this.stampTiers.get(key) ?? 0) < 3) return;
    const run = this.runId;
    const land = this.world.biome;
    void loadFieldDefense().then(({ FieldDefense }) => {
      if (this.phase !== 'playing' || this.runId !== run || this.world.biome !== land) return;
      (this.combine ??= new FieldDefense(this)).challenge(key, cx, cz);
    }, () => undefined);
  }

  /**
   * Queue a replacement in active simulation time. A wall-clock timeout used
   * to expire behind pause, perk drafts, the council ritual and the protected
   * new-land reveal, allowing combat state to change under an opaque screen.
   */
  private scheduleHostileRespawn(kind: HostileKind): void {
    this.hostileRespawns.push({
      kind,
      runId: this.runId,
      remaining: hostileRespawnDelayForWave(kind, this.wave),
    });
  }

  /** Runs only from the live-combat branch of update(), so every freeze is real. */
  private updateHostileRespawns(dt: number): void {
    for (let i = this.hostileRespawns.length - 1; i >= 0; i--) {
      const cue = this.hostileRespawns[i];
      if (cue.runId !== this.runId) {
        this.hostileRespawns.splice(i, 1);
        continue;
      }
      cue.remaining -= dt;
      if (cue.remaining > 0) continue;

      const count = cue.kind === 'drone' ? this.drones.length : this.normies.length;
      const target = cue.kind === 'drone' ? this.dronesFor(this.wave) : this.normiesFor(this.wave);
      if (count >= target || (this.wave >= RELAY_FIRST_WAVE && !this.relay)) {
        this.hostileRespawns.splice(i, 1);
        continue;
      }
      // An expired cue waits at zero while EMP holds the relay. It resumes on
      // the first active frame after the stun, without adding another hidden
      // wall-clock delay.
      if (this.wave >= RELAY_FIRST_WAVE && this.relay!.stunT > 0) {
        cue.remaining = 0;
        continue;
      }
      if (cue.kind === 'drone') this.spawnDrone();
      else this.spawnNormie();
      this.hostileRespawns.splice(i, 1);
    }
  }

  /**
   * The perk draft covers the moment reinforcements spawn. Hold this notice in
   * simulation time so it appears after the player chooses, not behind the
   * opaque draft and not while the game is paused/backgrounded.
   */
  private queueThreatSurge(wave: number, delay = 1.35): void {
    const surge = threatSurgeForWave(wave);
    if (!surge) return;
    this.threatSurge = { ...surge, delay };
  }

  private updateThreatSurge(dt: number): void {
    if (!this.threatSurge) return;
    this.threatSurge.delay -= dt;
    if (this.threatSurge.delay > 0) return;
    if (!lateWorldRuntime) {
      this.threatSurge.delay = 0;
      void loadLateWorld();
      return;
    }
    const { wave, drones, shooters } = this.threatSurge;
    this.threatSurge = null;
    this.deployRelay(wave);
    const tier = relayTierForWave(wave);
    const action = tier === 1 ? 'BEAM RED RELAY · CUT REPLACEMENTS' : `RELAY MK${tier} · REPLACEMENTS FASTER`;
    this.hooks.onBanner(`📡 FIAT SURGE · ${drones} DRONES · ${shooters} SHOOTERS · ${action}`);
    sfxDroneLock(0);
    if (wave === 17) playVoiceClip(VOICE.fiatNam, 0.9);
    buzz([15, 35, 15]);
  }

  /**
   * Major wave beats wait behind the opaque perk draft and advance only while
   * play is active. Keeping the callback run-scoped also prevents an old cue
   * from leaking into a restarted run after a pause, ritual or slow frame.
   */
  private queueWaveEvent(kind: WaveEventKind, wave: number, delay: number, fire: () => void): void {
    this.waveEvents.push({ kind, wave, runId: this.runId, delay, fire });
  }

  private updateWaveEvents(dt: number): void {
    if (this.waveEvents.length === 0) return;
    for (const event of this.waveEvents) event.delay -= dt;
    const due = this.waveEvents.filter((event) => event.delay <= 0);
    this.waveEvents = this.waveEvents.filter((event) => event.delay > 0);
    for (const event of due) {
      if (event.runId !== this.runId || this.phase !== 'playing') continue;
      event.fire();
    }
  }

  /** Fold the queued wave title into the upgrade confirmation instead of
   * replacing the player's chosen perk banner a fraction of a second later. */
  private consumeQueuedWaveTitle(): boolean {
    const queued = this.waveEvents.some((event) => event.kind === 'wave' && event.wave === this.wave);
    if (queued) {
      this.waveEvents = this.waveEvents.filter((event) => event.kind !== 'wave' || event.wave !== this.wave);
    }
    return queued;
  }

  /* ---------------- spawning ---------------- */

  private randomFieldSpot(minR = 4, maxR = PLAY_RADIUS - 4): THREE.Vector3 {
    const v = new THREE.Vector3();
    for (let tries = 0; tries < 10; tries++) {
      const a = Math.random() * Math.PI * 2;
      const r = minR + Math.random() * (maxR - minR);
      v.set(Math.cos(a) * r, 0, Math.sin(a) * r);
      if (Math.hypot(v.x - LAKE.x, v.z - LAKE.z) > LAKE.r + 2) break;
    }
    v.y = this.world.heightAt(v.x, v.z);
    return v;
  }

  /** Distance from the monolith, on the flat. The stone carries the terrain
   *  height under it, so a straight `distanceTo` measures the hill as well. */
  private stoneRange(x: number, z: number): number {
    return Math.hypot(x - this.world.stoneAt.x, z - this.world.stoneAt.z);
  }

  /**
   * Push a point out of the sacred circle along its own bearing from the stone.
   * The congregation is the one crowd allowed inside those stones: a leader who
   * wandered in behind the beam, or was dropped in mid-lift, reads exactly like
   * a delivered one and quietly makes the count look wrong.
   */
  private clearOfStone(v: THREE.Vector3, radius: number): void {
    const stone = this.world.stoneAt;
    const dx = v.x - stone.x;
    const dz = v.z - stone.z;
    const d = Math.hypot(dx, dz);
    if (d >= radius) return;
    // Dead centre has no bearing to keep; send them out along the map's own
    // outward direction rather than an arbitrary axis.
    const bearing = d > 0.001 ? Math.atan2(dz, dx) : Math.atan2(stone.z, stone.x);
    v.x = stone.x + Math.cos(bearing) * radius;
    v.z = stone.z + Math.sin(bearing) * radius;
    keepInField(v);
  }

  /** A wandering leader's place on the land: anywhere but the doorstep of the
   *  stone they have not reached yet. */
  private randomLeaderSpot(minR: number, maxR: number): THREE.Vector3 {
    for (let tries = 0; tries < 14; tries++) {
      const v = this.randomFieldSpot(minR, maxR);
      if (this.stoneRange(v.x, v.z) >= STONE_KEEP_OUT) return v;
    }
    const v = this.randomFieldSpot(minR, maxR);
    this.clearOfStone(v, STONE_KEEP_OUT);
    v.y = this.world.heightAt(v.x, v.z);
    return v;
  }

  /** Where a loose donkey turns up: out on its own land, clear of the rails it
   *  should be behind, and clear of the lake and the stone circle. */
  private strayDonkeySpot(pen: { x: number; z: number; r: number }): THREE.Vector3 {
    const v = new THREE.Vector3();
    for (let tries = 0; tries < 20; tries++) {
      const a = Math.random() * Math.PI * 2;
      const r = DONKEY_STRAY_MIN + Math.random() * (DONKEY_STRAY_MAX - DONKEY_STRAY_MIN);
      v.set(pen.x + Math.cos(a) * r, 0, pen.z + Math.sin(a) * r);
      if (Math.hypot(v.x, v.z) > PLAY_RADIUS - 8) continue;
      if (Math.hypot(v.x - LAKE.x, v.z - LAKE.z) <= LAKE.r + 2) continue;
      if (this.stoneRange(v.x, v.z) < STONE_KEEP_OUT) continue;
      v.y = this.world.heightAt(v.x, v.z);
      return v;
    }
    // The farm can sit near the rim. A centre-facing fallback keeps the stray
    // on the land rather than parked against the boundary.
    const inward = Math.atan2(-pen.z, -pen.x);
    const r = (DONKEY_STRAY_MIN + DONKEY_STRAY_MAX) * 0.5;
    v.set(pen.x + Math.cos(inward) * r, 0, pen.z + Math.sin(inward) * r);
    keepInField(v);
    this.clearOfStone(v, STONE_KEEP_OUT);
    v.y = this.world.heightAt(v.x, v.z);
    return v;
  }

  private randomCouncilPilgrimageSpot(band: CouncilPilgrimage): THREE.Vector3 {
    const stone = this.world.stoneAt;
    const v = new THREE.Vector3();
    for (let tries = 0; tries < 18; tries++) {
      const a = Math.random() * Math.PI * 2;
      const r = band.minRadius + Math.random() * (band.maxRadius - band.minRadius);
      v.set(stone.x + Math.cos(a) * r, 0, stone.z + Math.sin(a) * r);
      if (Math.hypot(v.x, v.z) > PLAY_RADIUS - 6) continue;
      if (Math.hypot(v.x - LAKE.x, v.z - LAKE.z) <= LAKE.r + 2) continue;
      v.y = this.world.heightAt(v.x, v.z);
      return v;
    }
    // The stone can sit off-centre. A centre-facing fallback remains in the
    // requested band even if repeated random candidates met water or the rim.
    const inward = Math.atan2(-stone.z, -stone.x);
    const r = (band.minRadius + band.maxRadius) * 0.5;
    v.set(stone.x + Math.cos(inward) * r, 0, stone.z + Math.sin(inward) * r);
    keepInField(v);
    v.y = this.world.heightAt(v.x, v.z);
    return v;
  }

  /** The rails of The Donkey Farm, on the one land that has one. */
  private donkeyPen(): { x: number; z: number; r: number } | null {
    const at = MAPS[this.world.biome].donkeyFarm;
    return at ? { x: at[0], z: at[1], r: DONKEY_PEN_RADIUS } : null;
  }

  private penSpot(pen: { x: number; z: number; r: number }): THREE.Vector3 {
    const a = Math.random() * Math.PI * 2;
    // sqrt keeps the herd spread evenly over the pen instead of crowding the
    // gate post in the middle.
    const r = Math.sqrt(Math.random()) * pen.r;
    const v = new THREE.Vector3(pen.x + Math.cos(a) * r, 0, pen.z + Math.sin(a) * r);
    v.y = this.world.heightAt(v.x, v.z);
    return v;
  }

  private spawnCow(anywhere = false, fixedPos?: THREE.Vector3): AnimalEnt {
    // The Donkey Farm keeps its own population, and it keeps it loose. Every
    // ordinary top-up puts the herd back out on the land rather than behind the
    // rails, because filling the pen is the pilot's job: the gate was left
    // open, and the empty field is the objective, not a bug.
    // A placed animal — the opening cow, cow rain, a rustled beast — is never
    // rerouted into the herd; it was put where it was for a reason.
    const pen = fixedPos ? null : this.donkeyPen();
    const restock = pen !== null &&
      this.cows.reduce((n, c) => n + (c.species === 'donkey' ? 1 : 0), 0) < DONKEY_HERD;
    const pos = fixedPos?.clone() ?? (restock
      ? this.strayDonkeySpot(pen!)
      : anywhere
        ? this.randomFieldSpot(6, PLAY_RADIUS - 8)
        : this.randomFieldSpot(PLAY_RADIUS - 8, PLAY_RADIUS - 3));
    pos.y = this.world.heightAt(pos.x, pos.z);
    // The Golden Calf: rare, one at a time, wave 2 onward. The cult insists
    // she is golden; the graphics remain, proudly, the red heifer's.
    // Under a blood moon she comes out to graze far more boldly.
    const heiferChance = this.bloodMoon ? 0.3 : 0.12;
    const heifer = !restock && this.wave >= 2 &&
      Math.random() < heiferChance && !this.cows.some((c) => c.heifer);
    // Every land keeps a minority species in with its headline animal, so a
    // field reads as livestock rather than as one prop cloned sixteen times.
    const species: AnimalSpecies = heifer ? 'cow' : restock ? 'donkey' : rollHerdSpecies(this.world.biome);
    const rig = heifer ? buildRedHeifer() : buildAnimal(species);
    const animalBiome = this.world.biome;
    if (heifer && (animalBiome === 'mars' || animalBiome === 'moon')) {
      void import('./late-world').then(({ addAnimalSpaceGear }) => addAnimalSpaceGear(rig));
    }
    const shadow = makeBlobShadow(1.1);
    // Placed here, not left to the first update: a beast added to the scene
    // without a position is drawn at the world origin for a frame, and on a
    // land with any relief that means it is drawn inside a hill.
    rig.position.copy(pos);
    shadow.position.set(pos.x, pos.y + 0.05, pos.z);
    this.world.scene.add(rig, shadow);
    const animal: AnimalEnt = {
      rig, species, shadow, pos,
      heading: Math.random() * Math.PI * 2,
      state: 'wander',
      stateT: 1 + Math.random() * 3,
      target: this.randomFieldSpot(),
      // Loose from the moment it appears: only a completed ferry sets a
      // paddock on a donkey, and nothing else in the game wears one.
      paddock: undefined,
      lift: null,
      bobT: Math.random() * 10,
      squashT: 0,
      heifer,
      chute: null,
      chuteY: null,
    };
    if (species === 'donkey') {
      const aura = makeDonkeyAura();
      aura.material.opacity = DONKEY_AURA_LOOSE;
      rig.add(aura);
      animal.aura = aura;
      animal.ferry = null;
      animal.penned = false;
    }
    this.cows.push(animal);
    if (heifer) this.hooks.onBanner('✨ THE GOLDEN CALF GRAZES SOMEWHERE ✨');
    return animal;
  }

  private takenMemberIds(): Set<string> {
    const taken = new Set(this.cultists.map((c) => c.rig.member.id));
    for (const b of this.buildings) if (b.occupant) taken.add(b.occupant.id);
    for (const h of this.hideys) taken.add(h.occupant.id);
    for (const id of this.councilIds) taken.add(id);
    return taken;
  }

  private spawnCultist(at?: THREE.Vector3, fixed?: CultMember): void {
    const member = fixed ?? pickMember(this.takenMemberIds());
    // Only the leaders actually assigned to this land need their name line.
    // This starts well before a wandering leader can reach the beam, without
    // making PLAY wait for all 31 voices on every run.
    preloadVoiceClip(memberVoice(member.id), true, this.runId);
    const rig = buildCultist(member);
    const cultBiome = this.world.biome;
    if (cultBiome === 'mars' || cultBiome === 'moon') {
      void import('./late-world').then(({ addSpaceSuit }) => addSpaceSuit(rig.group, cultBiome));
    }
    const shadow = makeBlobShadow(0.8);
    // The opening five still scatter across the whole land, less the ground the
    // circle keeps for itself. Replacements enter the active pilgrimage band,
    // avoiding late-council edge-of-map lotteries.
    const pilgrimage = councilPilgrimageFor(this.councilIds.size);
    const pos = at ? at.clone() : pilgrimage
      ? this.randomCouncilPilgrimageSpot(pilgrimage)
      : this.randomLeaderSpot(10, PLAY_RADIUS - 6);
    // A leader placed by name — a burst shack, a cracked wheelie bin, the
    // opening lesson — keeps the spot its prop was standing on, and only ever
    // gives ground to the circle itself. A wandering one starts a proper walk
    // away, whichever band called it.
    this.clearOfStone(pos, at ? STONE_RING_R + 2 : STONE_KEEP_OUT);
    pos.y = this.world.heightAt(pos.x, pos.z);
    rig.group.position.copy(pos);
    shadow.position.set(pos.x, pos.y + 0.05, pos.z);
    this.world.scene.add(rig.group, shadow);
    this.cultists.push({
      rig, shadow, pos,
      heading: Math.random() * Math.PI * 2,
      stateT: 2 + Math.random() * 3,
      target: pilgrimage ? this.randomCouncilPilgrimageSpot(pilgrimage) : this.randomLeaderSpot(10, PLAY_RADIUS - 6),
      lift: null,
      squashT: 0,
      mode: 'ground',
      homeSpot: null,
    });
  }

  private summonCouncilPilgrims(): void {
    const pilgrimage = councilPilgrimageFor(this.councilIds.size);
    if (!pilgrimage) return;
    for (const cult of this.cultists) {
      if (cult.mode !== 'ground' || cult.lift) continue;
      cult.target.copy(this.randomCouncilPilgrimageSpot(pilgrimage));
      cult.stateT = 5 + Math.random() * 4;
    }
  }

  /** Four hilltop volunteers per land. They are finite bonus targets, not
   *  Council members, and arrive after the pilot proves the beam on an animal. */
  private spawnFans(): void {
    const run = this.runId;
    const land = this.world.biome;
    void loadGroupies().then(({ buildGroupie, groupieRally }) => {
      if (this.phase !== 'playing' || this.runId !== run || this.world.biome !== land || this.groupies.length) return;
      const rally = groupieRally(land, this.saucerPos.x, this.saucerPos.z,
        this.buildings.map((b) => b.pos), this.world);
      for (let i = 0; i < GROUPIE_TARGET; i++) {
        const pos = new THREE.Vector3(rally[i][0], 0, rally[i][1]);
        keepInField(pos);
        pos.y = this.world.heightAt(pos.x, pos.z);
        const rig = buildGroupie(i);
        if (land === 'mars' || land === 'moon') {
          void import('./late-world').then(({ addSpaceSuit }) => addSpaceSuit(rig.group, land));
        }
        const shadow = makeBlobShadow(0.85);
        rig.group.position.copy(pos);
        shadow.position.set(pos.x, pos.y + 0.05, pos.z);
        this.world.scene.add(rig.group, shadow);
        this.groupies.push({
          rig, shadow, pos, heading: Math.random() * Math.PI * 2,
          lift: null, squashT: 0,
        });
        this.poofs.burst(new THREE.Vector3(pos.x, pos.y + 0.8, pos.z), 'green', 1);
      }
      this.hooks.onBanner('📣 FOUR HILLTOP VOLUNTEERS · FIND FLARES');
    });
  }

  private spawnBuildings(): void {
    const spots: THREE.Vector3[] = [];
    const map = MAPS[this.world.biome];
    let guard = 0;
    while (spots.length < 9 && guard++ < 200) {
      const p = this.randomFieldSpot(14, PLAY_RADIUS - 10);
      if (p.distanceTo(this.world.stoneAt) < 14) continue;
      if (p.distanceTo(this.world.chapelAt) < 12) continue;
      if (onRoad(p.x, p.z, 3.5)) continue;
      if (map.pumpkins.some(([x, z]) => Math.hypot(p.x - x, p.z - z) < 12)) continue;
      if (!this.world.propClearAt(p.x, p.z, 3.2)) continue;
      if (spots.some((q) => q.distanceTo(p) < 10)) continue;
      // Nobody builds a shack on a cliff. Hold out for reasonable ground while
      // there are tries left, then take what is going rather than end up with
      // fewer buildings than the run expects.
      const { low, high } = groundRange(p.x, p.z, 2.4);
      if (high - low > 1.2 && guard < 150) continue;
      spots.push(p);
    }
    // Four of them hide a cult leader.
    const hidden = new Set<number>();
    while (hidden.size < Math.min(4, spots.length)) hidden.add(Math.floor(Math.random() * spots.length));
    spots.forEach((pos, idx) => {
      // Seated on the HIGHEST ground under the footprint, with a footing deep
      // enough to close the gap downhill. Seating it at the centre height
      // hangs a corner over the slope; seating it at the lowest buries the
      // ground floor.
      const { low, high } = groundRange(pos.x, pos.z, 2.4);
      const group = buildShack(idx, this.world.biome, high - low);
      pos.y = high;
      group.position.set(pos.x, pos.y, pos.z);
      group.rotation.y = Math.random() * Math.PI * 2;
      this.world.scene.add(group);
      this.buildings.push({
        group,
        pos,
        hp: 1.6,
        maxHp: 1.6,
        occupant: hidden.has(idx) ? pickMember(this.takenMemberIds()) : null,
        baseY: pos.y,
      });
      const occupant = this.buildings[this.buildings.length - 1].occupant;
      if (occupant) preloadVoiceClip(memberVoice(occupant.id), true, this.runId);
    });
  }

  /** Three oddities per land, each hiding a leader. Gentle puzzles: they
   *  twitch, they glow faintly, and the beam pops them open. */
  private spawnHideys(): void {
    const pumpkin = MAPS[this.world.biome].pumpkins[0];
    const spots: THREE.Vector3[] = pumpkin ? [new THREE.Vector3(pumpkin[0], 0, pumpkin[1])] : [];
    let guard = 0;
    while (spots.length < 3 && guard++ < 160) {
      const p = this.randomFieldSpot(16, PLAY_RADIUS - 12);
      if (p.distanceTo(this.world.stoneAt) < 16) continue;
      if (p.distanceTo(this.world.chapelAt) < 12) continue;
      if (onRoad(p.x, p.z, 3.5)) continue;
      if (this.buildings.some((b) => b.pos.distanceTo(p) < 9)) continue;
      if (spots.some((q) => q.distanceTo(p) < 24)) continue;
      // These small props have no foundation to hide a downhill gap. Mars in
      // particular can otherwise park one across a crater lip, leaving the
      // whole thing visibly hovering when it is seated on the high corner.
      // Hold out for a footprint that can make honest contact with the ground.
      const { low, high } = groundRange(p.x, p.z, 1.3);
      if (high - low > 0.75) continue;
      spots.push(p);
    }
    spots.forEach((pos, idx) => {
      const rig = buildHideyProp(this.world.biome, idx);
      // Small enough that the high ground under it is the right seat outright.
      pos.y = groundRange(pos.x, pos.z, 1.3).high;
      rig.group.position.set(pos.x, pos.y, pos.z);
      rig.group.rotation.y = Math.random() * Math.PI * 2;
      this.world.scene.add(rig.group);
      const occupant = pickMember(this.takenMemberIds());
      preloadVoiceClip(memberVoice(occupant.id), true, this.runId);
      this.hideys.push({
        rig, pos,
        hp: 1.3,
        maxHp: 1.3,
        occupant,
        baseY: pos.y,
        wiggleT: 2 + Math.random() * 5,
        wiggling: 0,
      });
    });
    // Resolves after the caller has also raised Town's pylons; nothing in this
    // optional task module sits on the title-screen download path.
    void loadChallenges().then((runtime) => {
      this.task = runtime.default(this);
    });
  }

  private updateHideys(dt: number): void {
    for (let i = this.hideys.length - 1; i >= 0; i--) {
      const h = this.hideys[i];
      const group = h.rig.group;
      const beamOn = this.beamActive &&
        Math.hypot(h.pos.x - this.saucerPos.x, h.pos.z - this.saucerPos.z) < 3.4;
      // The tell: a slow aura breath, brighter while the beam works.
      h.rig.aura.material.opacity = (beamOn ? 0.4 : 0.14) + Math.sin(this.timeS * 2.1 + i * 2.4) * 0.05;
      if (beamOn) {
        h.hp -= dt;
        const stress = 1 - Math.max(h.hp, 0) / h.maxHp;
        group.position.set(
          h.pos.x + (Math.random() - 0.5) * 0.22 * stress,
          h.baseY + stress * 0.4,
          h.pos.z + (Math.random() - 0.5) * 0.22 * stress,
        );
        group.rotation.z = Math.sin(this.timeS * 28) * 0.09 * stress;
        const swell = 1 + stress * 0.7;
        group.scale.set(swell, 1 + stress * 0.32, swell);
        sfxRumble();
        if (h.hp <= 0) {
          this.world.scene.remove(group);
          this.hideys.splice(i, 1);
          this.explodeAt(h.pos, group, h.rig.palette);
          this.spawnCultist(h.pos, h.occupant);
          this.hooks.onBanner(`${h.rig.label}! ${h.occupant.name.toUpperCase()} FOUND`);
          playVoiceClip(memberVoice(h.occupant.id), 0.95);
          const pts = this.addScore(250);
          this.popScore(h.pos.x, h.pos.y + 2.4, h.pos.z, pts);
          this.addFever(0.12);
          continue;
        }
      } else {
        // Idle: settle back, and twitch now and then so sharp eyes spot it.
        group.scale.setScalar(1);
        if (h.wiggling > 0) {
          h.wiggling -= dt;
          group.rotation.z = Math.sin(this.timeS * 26) * 0.05 * Math.min(1, h.wiggling * 3);
          group.position.y = h.baseY + Math.abs(Math.sin(this.timeS * 18)) * 0.08;
        } else {
          h.wiggleT -= dt;
          group.rotation.z *= Math.max(0, 1 - dt * 8);
          const settle = Math.min(1, dt * 6);
          group.position.x += (h.pos.x - group.position.x) * settle;
          group.position.y += (h.baseY - group.position.y) * settle;
          group.position.z += (h.pos.z - group.position.z) * settle;
          if (h.wiggleT <= 0) {
            h.wiggleT = 3.5 + Math.random() * 5;
            h.wiggling = 0.5;
          }
        }
      }
    }
  }

  /* ---------------- the grid ---------------- */

  /** Raise this land's transmission line, if it has one. Towers come straight
   *  from the map so the line is part of the place, not a random hazard. */
  private spawnPylons(): void {
    // The grid is Town's identity, never a generic obstacle that can leak into
    // another land even if map data is edited later.
    if (this.world.biome !== 'town') return;
    const map = MAPS.town;
    // The wreck of the tower that used to close each gap, so the way through
    // is something you can see rather than something you have to deduce.
    for (const [x, z] of map.gates ?? []) {
      const stump = buildPylonStump();
      stump.position.set(x, groundUnder(x, z, 1.9), z);
      stump.rotation.y = x * 0.7 + z * 0.3;
      this.world.scene.add(stump);
      this.stumps.push(stump);
    }
    const runs = map.pylons;
    if (!runs) return;
    for (const run of runs) {
      const raised: PylonEnt[] = [];
      for (const [x, z] of run) {
        const rig = buildPylon();
        const pos = new THREE.Vector3(x, groundUnder(x, z, 1.9), z);
        rig.group.position.copy(pos);
        this.world.scene.add(rig.group);
        const pylon: PylonEnt = { rig, pos, hp: 2.2, maxHp: 2.2 };
        raised.push(pylon);
        this.pylons.push(pylon);
      }
      // Turn each tower so its cross-arms sit square across its own span.
      for (let i = 0; i < raised.length; i++) {
        const neighbour = raised[i + 1] ?? raised[i - 1];
        if (!neighbour) break;
        const p = raised[i];
        p.rig.group.rotation.y = Math.atan2(neighbour.pos.z - p.pos.z, neighbour.pos.x - p.pos.x) + Math.PI / 2;
      }
      // Wires only run WITHIN a run, so the break between runs is a way through.
      for (let i = 0; i < raised.length - 1; i++) {
        const a = raised[i];
        const b = raised[i + 1];
        const rig = buildWireSpan(a.pos, b.pos);
        this.world.scene.add(rig.group);
        this.spans.push({ rig, from: a.pos.clone(), to: b.pos.clone(), a, b });
      }
    }
  }

  private clearPylons(): void {
    for (const p of this.pylons) {
      this.world.scene.remove(p.rig.group);
      disposePylon(p.rig);
    }
    for (const s of this.spans) {
      this.world.scene.remove(s.rig.group);
      disposeWireSpan(s.rig);
    }
    for (const s of this.stumps) this.world.scene.remove(s);
    for (const a of this.arcs) this.world.scene.remove(a.group);
    this.pylons = [];
    this.spans = [];
    this.stumps = [];
    this.arcs = [];
    this.gridHitT = 0;
    this.gridWarnT = 0;
    setGridHum(0);
  }

  private arcAt(at: THREE.Vector3): void {
    const flash = buildArcFlash();
    flash.position.copy(at);
    this.world.scene.add(flash);
    this.arcs.push({ group: flash, life: 0.3 });
    sfxArc(this.panOf(at.x));
  }

  /** Bring a tower down: the wires it held go with it. */
  private dropPylon(index: number): void {
    const pylon = this.pylons[index];
    this.world.scene.remove(pylon.rig.group);
    disposePylon(pylon.rig);
    this.pylons.splice(index, 1);
    for (let i = this.spans.length - 1; i >= 0; i--) {
      const span = this.spans[i];
      if (span.a !== pylon && span.b !== pylon) continue;
      this.world.scene.remove(span.rig.group);
      disposeWireSpan(span.rig);
      this.spans.splice(i, 1);
    }
    this.arcAt(new THREE.Vector3(pylon.pos.x, pylon.pos.y + PYLON_HEIGHT * 0.6, pylon.pos.z));
    this.explodeAt(pylon.pos, pylon.rig.group, ['#6d757f', '#4a505a', '#8d97a8']);
    const pts = this.addScore(400);
    this.popScore(pylon.pos.x, pylon.pos.y + 4, pylon.pos.z, pts);
    this.hooks.onBanner(`⚡ GRID DOWN  +${pts}`);
    this.addFever(0.14);
    // No combo bump: the chain is for abductions. Buildings and hidey props do
    // not extend it either, and a 2.2s beam hold is the opposite of a chain.
  }

  private updatePylons(dt: number): void {
    this.gridHitT = Math.max(0, this.gridHitT - dt);
    this.pylonHintDelayT = Math.max(0, this.pylonHintDelayT - dt);
    for (let i = this.arcs.length - 1; i >= 0; i--) {
      const arc = this.arcs[i];
      arc.life -= dt;
      if (arc.life <= 0) {
        this.world.scene.remove(arc.group);
        this.arcs.splice(i, 1);
        continue;
      }
      arc.group.scale.setScalar(1 + (0.3 - arc.life) * 4);
      arc.group.rotation.y += dt * 12;
    }
    if (this.pylons.length === 0 && this.spans.length === 0) {
      setGridHum(0); // the last tower is down; the land goes quiet
      return;
    }

    let nearestLive = Infinity;

    for (let i = this.pylons.length - 1; i >= 0; i--) {
      const pylon = this.pylons[i];
      // Aircraft warning lamp, out of phase per tower.
      const blink = Math.sin(this.timeS * 2 + i * 1.7) > 0.4;
      pylon.rig.lampMat.color.set(blink ? '#ff4030' : '#3a0f0c');

      const flat = Math.hypot(pylon.pos.x - this.saucerPos.x, pylon.pos.z - this.saucerPos.z);
      // Beamed towers come down: 2.2 seconds of aperture on the lattice.
      if (this.beamActive && flat < 3.4) {
        pylon.hp -= dt;
        sfxRumble();
        const stress = 1 - Math.max(pylon.hp, 0) / pylon.maxHp;
        pylon.rig.group.rotation.z = Math.sin(this.timeS * 24) * 0.05 * stress;
        if (pylon.hp <= 0) {
          this.dropPylon(i);
          continue;
        }
      } else {
        pylon.rig.group.rotation.z *= Math.max(0, 1 - dt * 6);
      }
      // Steelwork is solid at any altitude. Flying into it costs a hull. The
      // radius is the lattice plus the hull: at PYLON_RADIUS alone you had to
      // fly through the exact centre of a tower to hit anything, which is not
      // what a player who aims at a pylon expects to happen.
      if (flat < PYLON_RADIUS + 1.5 && this.saucerY < pylon.pos.y + PYLON_HEIGHT + 1) {
        this.strikeGrid(pylon.pos.x, pylon.pos.z, Math.min(this.saucerY, pylon.pos.y + PYLON_HEIGHT));
      }
      nearestLive = Math.min(nearestLive, flat);
    }

    let nearestSpan = Infinity;
    for (const span of this.spans) {
      const dx = span.to.x - span.from.x;
      const dz = span.to.z - span.from.z;
      const len2 = dx * dx + dz * dz;
      const t = THREE.MathUtils.clamp(
        ((this.saucerPos.x - span.from.x) * dx + (this.saucerPos.z - span.from.z) * dz) / len2, 0, 1);
      const cx = span.from.x + dx * t;
      const cz = span.from.z + dz * t;
      const lateral = Math.hypot(this.saucerPos.x - cx, this.saucerPos.z - cz);
      nearestSpan = Math.min(nearestSpan, lateral);
      // Distant spans recede into the town; their markers flare back to full
      // warning brightness as the saucer enters the dangerous corridor.
      const heat = THREE.MathUtils.clamp(1 - (lateral - 4) / 22, 0, 1);
      (span.rig.markers.material as THREE.MeshLambertMaterial).emissiveIntensity =
        0.3 + heat * heat * 2.7;
      if (lateral > 4.4) continue;
      nearestLive = Math.min(nearestLive, lateral);
      // The conductors leave the arms at CONDUCTOR_H above each tower's base
      // and sag between them, which puts the whole span inside the saucer's
      // cruise band. The bundle is wider than the hull, so a span is a curtain
      // rather than something to thread — the gap between runs is the way past.
      const wireY = wireYAt(span.from.y + CONDUCTOR_H, span.to.y + CONDUCTOR_H, t);
      if (lateral < 3.7 && Math.abs(this.saucerY - wireY) < 1.35) {
        this.strikeGrid(cx, cz, wireY);
      }
    }

    // The warning goes off when the line is actually ahead of you, not on a
    // timer: a land can hold its grid out at the edge of the map, and being
    // told about wires you cannot see teaches nothing.
    this.gridWarnT = Math.max(0, this.gridWarnT - dt);
    if (!this.pylonHintDone && this.pylonHintDelayT <= 0 && nearestSpan < 34 && this.phase === 'playing') {
      this.pylonHintDone = true;
      this.hooks.onBanner('⚡ LIVE GRID AHEAD — THE WIRES BITE · BEAM A PYLON TO DROP IT');
      playVoiceClip(VOICE.ttsPylons, 0.95);
    } else if (this.pylonHintDone && this.gridWarnT <= 0 && nearestSpan < 11 && this.gridHitT <= 0) {
      // And again, briefly, whenever you close right in on a live span.
      this.gridWarnT = 6;
      this.hooks.onBanner('⚡ WIRES');
    }

    // Mains hum swells as you close on live steel.
    setGridHum(nearestLive === Infinity ? 0 : Math.max(0, 1 - nearestLive / 16));
  }

  /** Hull against the grid: arc, damage, and a shove back out of the wires so
   *  the invulnerability window is not spent sitting inside them. */
  private strikeGrid(x: number, z: number, y: number): void {
    // The cabinet is not billed by the grid: the autopilot has no route around
    // a line, so a demo in the town would bounce off the wires until it died.
    // The towers and spans still stand — nothing on screen implies a hit.
    if (this.demoMode) return;
    // One contact per cooldown, not one per frame. invulnT alone is not enough:
    // a HODL shield absorbs without granting invulnerability, so sitting in a
    // span used to fire "SHIELD HELD" sixty times a second.
    if (this.invulnT > 0 || this.gridHitT > 0) return;
    this.gridHitT = 0.9;
    this.arcAt(new THREE.Vector3(x, y, z));
    const away = new THREE.Vector3(this.saucerPos.x - x, 0, this.saucerPos.z - z);
    // Dead-centre on a span there is no "out" to compute, so shove the saucer
    // back the way it came rather than picking an axis that may run ALONG the
    // wires and leave it inside them.
    if (away.lengthSq() < 0.0001) away.set(-this.saucerVel.x, 0, -this.saucerVel.z);
    if (away.lengthSq() < 0.0001) away.set(1, 0, 0);
    away.normalize();
    this.saucerPos.x = x + away.x * 5.2;
    this.saucerPos.z = z + away.z * 5.2;
    this.saucerVel.set(away.x * 9, 0, away.z * 9);
    this.hullHit('grid');
    if (this.hull > 0) this.hooks.onBanner('⚡ LIVE WIRES — MIND THE PYLONS');
  }

  private spawnDrone(): void {
    const rig = buildDrone(this.world.biome);
    const shadow = makeBlobShadow(1);
    this.world.scene.add(rig.group, shadow);
    const radius = 10 + Math.random() * 20;
    const angle = Math.random() * Math.PI * 2;
    this.drones.push({
      rig, shadow, angle, radius,
      dir: Math.random() < 0.5 ? 1 : -1,
      fireT: 2 + Math.random() * 2,
      telegraph: 0,
      lift: null,
      pos: new THREE.Vector3(),
      stunT: 0,
    });
  }

  private spawnNormie(): void {
    const rig = buildNormie(Math.floor(Math.random() * 5), this.world.biome);
    const shadow = makeBlobShadow(0.7);
    this.world.scene.add(rig.group, shadow);
    const pos = this.randomFieldSpot(PLAY_RADIUS - 14, PLAY_RADIUS - 4);
    this.normies.push({
      rig, shadow, pos,
      heading: Math.random() * Math.PI * 2,
      cooldown: 2 + Math.random() * 2,
      telegraph: 0,
      lift: null,
      squashT: 0,
      stunT: 0,
    });
  }

  /* ---------------- scoring ---------------- */

  /** Stereo position of a world x for panned SFX. */
  private panOf(x: number): number {
    return THREE.MathUtils.clamp((x - this.saucerPos.x) / 30, -1, 1);
  }

  private addScore(base: number): number {
    const points = Math.round(base * this.comboMult * this.scoreMult) *
      (this.feverActive ? 2 : 1) * (this.hasPower('double') ? 2 : 1);
    this.score += points;
    this.hooks.onScore(this.score);
    return points;
  }

  private popScore(x: number, y: number, z: number, points: number): void {
    this.pops.spawn(new THREE.Vector3(x, y, z), `+${points}`, this.feverActive ? '#ffd24a' : '#b4ff8c');
  }

  private restoreEnergy(amount: number, label: string | null, loud = true): number {
    if (this.hull >= this.hullMax) return 0;
    const before = this.hull;
    this.hull = Math.min(this.hullMax, this.hull + amount);
    const restored = Math.round((this.hull - before) / this.hullMax * 100);
    if (restored <= 0) return 0;
    this.hooks.onHull(this.hull, this.hullMax);
    if (label !== null) {
      this.pops.spawn(
        this.effectPos.set(this.saucerPos.x, this.saucerY + 1.1, this.saucerPos.z),
        label ? `⚡ ${label} +${restored}%` : `⚡ +${restored}%`, '#7df9ff',
      );
    }
    this.energyPulseT = 0.75;
    if (loud) {
      sfxCoreRecharge();
      this.poofs.burst(
        this.effectPos.set(this.saucerPos.x, this.saucerY, this.saucerPos.z),
        'blue', 8,
      );
    }
    return restored;
  }

  /** Animals are living batteries only once they are safely aboard. The entity
   * is removed before this runs, so dropping/re-beaming one cannot farm charge. */
  private rechargeFromAnimal(animal: AnimalEnt): number {
    if (this.hull >= this.hullMax) return 0;
    const recoveryMultiplier = animalRecoveryMultiplier(this.hull / this.hullMax);
    // Only a critical core invokes the emergency siphon. Above half, ordinary
    // captures deliberately trickle in at half rate so routine harvesting
    // cannot keep the meter pinned without the player avoiding fire.
    const emergency = recoveryMultiplier === 2;
    return this.restoreEnergy(
      animalChargeFor(animal.species, animal.heifer, Boolean(animal.rustled), recoveryMultiplier),
      emergency ? 'SIPHON' : '',
    );
  }

  private get feverActive(): boolean {
    return this.feverT > 0;
  }

  private addFever(n: number): void {
    if (this.feverT > 0) return;
    this.fever = Math.min(1, this.fever + n * this.feverGainMult);
    if (this.fever >= 1) {
      this.feverT = FEVER_DURATION;
      this.hooks.onBanner('₿ 600B MODE — DOUBLE POINTS ₿');
      sfxFeverStart();
      playVoiceClip(VOICE.fourTwenty, 0.9);
      buzz([20, 30, 20, 30, 40]);
      this.awardBadge('fever');
    }
  }

  private updateFever(dt: number): void {
    if (this.feverT > 0) {
      this.feverT -= dt;
      if (this.feverT <= 0) { this.feverT = 0; this.fever = 0; }
    } else if (this.fever > 0) {
      this.fever = Math.max(0, this.fever - 0.03 * dt);
    }
    const shown = this.feverT > 0 ? this.feverT / FEVER_DURATION : this.fever;
    if (Math.abs(shown - this.lastFeverShown) > 0.005 || (this.feverT > 0) !== this.lastFeverActive) {
      this.lastFeverShown = shown;
      this.lastFeverActive = this.feverT > 0;
      this.hooks.onFever(shown, this.feverT > 0);
    }
  }

  private addShake(mag: number): void {
    if (this.reducedMotion) return;
    this.shakeMag = Math.min(1.4, this.shakeMag + mag);
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    if (reduced) this.shakeMag = 0;
  }

  /* ---------------- perk draft ---------------- */

  /** Installed upgrades in pick order. Kept public for the decision and pause
   * screens; simulation authority remains in the private scalar effects. */
  perkBuild(): PerkBuildEntry[] {
    return [...this.perkLevels].flatMap(([id, level]) => {
      const perk = Game.PERK_POOL.find((candidate) => candidate.id === id);
      return perk ? [{ id, name: perk.name, level, tier: perk.tier }] : [];
    });
  }

  private offerPerks(clearBonus = 0): void {
    const available = Game.PERK_POOL.filter((p) => p.can(this));
    if (available.length === 0) return;
    // Keystones are cadence rewards, never random filler. An ordinary draft
    // gets reliable upgrades only; every third install gets exactly one
    // run-defining choice alongside two reliable alternatives.
    const pool = available.filter((p) => p.tier !== 'keystone');
    const picks: PerkImpl[] = [];
    const keystoneDue = (this.perksTaken + 1) % 3 === 0;
    if (pool.length === 0 && !keystoneDue) return;
    // Every third install gets a run-defining option. It still competes with
    // two reliable upgrades, preserving a real choice instead of a freebie.
    if (keystoneDue) {
      const keystones = available.filter((p) => p.tier === 'keystone');
      if (keystones.length > 0) {
        const chosen = keystones[Math.floor(Math.random() * keystones.length)];
        picks.push(chosen);
      }
    }
    // A streak buys width, never power. The fourth card is one more thing to
    // choose between, not a stronger thing to choose, so a run made by a
    // player who turned up yesterday is still comparable on the board.
    const width = this.dailyStreak >= 2 ? 4 : 3;
    while (picks.length < width && pool.length > 0) {
      picks.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
    }
    if (picks.length === 0) return;
    this.pendingPerks = picks.map(({ id, name, desc, tier }) => ({
      id, name, desc, tier, level: this.perkLevels.get(id) ?? 0,
    }));
    // The cabinet has nobody holding a controller. Leaving its first draft
    // open froze the 55-second attract reel just as combat was about to begin.
    // Install one offered upgrade immediately so the demo remains a flight,
    // while the normal pilot still gets the complete decision screen.
    if (this.demoMode) {
      const choice = this.pendingPerks.find((perk) => perk.id === 'hull') ?? this.pendingPerks[0];
      this.pendingDecline = false;
      this.paused = true;
      this.choosePerk(choice.id);
      return;
    }
    // Worthless at full charge and agonising at wave 12 on low energy. That the
    // option vanishes when you are healthy is the point of it. It also has to
    // vanish when POWER CORE is on the table, which pays the same recharge and
    // also raises capacity, making DECLINE a strictly dominated dead row.
    this.pendingDecline = this.hull < this.hullMax && !picks.some((p) => p.id === 'hull');
    this.paused = true;
    setBeamHum(false);
    this.hooks.onPerkOffer(this.pendingPerks, this.pendingDecline, clearBonus, this.dailyStreak);
  }

  choosePerk(id: string): boolean {
    if (!this.pendingPerks) return false;
    const offered = this.pendingPerks.some((p) => p.id === id);
    const declined = id === DECLINE_ID && this.pendingDecline;
    const perk = offered ? Game.PERK_POOL.find((p) => p.id === id) : undefined;
    // A stale or retargeted UI event must not dismiss the draft and resume the
    // simulation without actually installing the choice the pilot touched.
    if (!declined && !perk) return false;
    this.pendingPerks = null;
    this.pendingDecline = false;
    this.paused = false;
    // The opaque draft can hide a bullet or a nearly-complete enemy lock. Give
    // the pilot one visible blink of damage grace while controls return; threat
    // timers keep running, so this is reorientation rather than another pause.
    if (offered || declined) this.invulnT = Math.max(this.invulnT, 1.25);
    if (declined) {
      // Deliberately not a perk: no install, so it never counts toward FULL STACK.
      const before = this.hull;
      this.hull = Math.min(this.hull + 1, this.hullMax);
      this.hooks.onHull(this.hull, this.hullMax);
      const restored = Math.round((this.hull - before) / this.hullMax * 100);
      const waveTitle = this.consumeQueuedWaveTitle() ? `WAVE ${this.wave} · ` : '';
      this.hooks.onBanner(`${waveTitle}⚡ CORE RECHARGED — +${restored}%`);
      sfxComboUp(5);
      buzz(15);
      return true;
    }
    if (perk) {
      perk.apply(this);
      this.perkLevels.set(id, (this.perkLevels.get(id) ?? 0) + 1);
      this.perksTaken++;
      this.hooks.onBuild(this.perkBuild());
      if (this.perksTaken >= 5) this.awardBadge('stack');
      const waveTitle = this.consumeQueuedWaveTitle() ? `WAVE ${this.wave} · ` : '';
      this.hooks.onBanner(`${waveTitle}${perk.name} INSTALLED`);
      sfxComboUp(5);
      buzz(15);
    }
    return true;
  }

  setUserPaused(on: boolean): void {
    if (this.phase !== 'playing') return;
    if (this.hull <= 0) return;
    this.userPaused = on;
    if (on) {
      this.silenceRunBeds();
    } else if (!isMuted()) {
      // Long-lived beds without a per-frame setter need an explicit restore.
      setBlackHoleHum(this.hole !== null);
      if (this.stormActive) startRainLoop();
    }
  }

  private silenceRunBeds(): void {
    setBeamHum(false);
    setBlackHoleHum(false);
    stopRainLoop();
    stopRotorField();
    setHeliRotor(false);
    setGridHum(0);
    this.combine?.silence();
  }

  /** Deliberately bank a live flight without inventing a crash or losing its score. */
  extractRun(): boolean {
    if (this.demoMode || this.phase !== 'playing' || this.hull <= 0 || this.tourComplete) return false;
    input.x = 0;
    input.z = 0;
    input.beam = false;
    input.special = false;
    consumeSpecialPress();
    this.beamActive = false;
    this.saucer.beam.group.visible = false;
    this.userPaused = false;
    this.paused = false;
    this.extracted = true;
    this.phase = 'over';
    this.hooks.onCarry(null);
    this.hooks.onThreat(null, 0, 0);
    this.pops.clear();
    this.silenceRunBeds();
    this.hooks.onGameOver();
    return true;
  }

  /** Leave a run after its immutable report has already been captured. */
  abandonRun(): void {
    // The game-over report has already captured its immutable result. Moving
    // from that report to the menu still needs the same world cleanup as QUIT.
    if (this.demoMode || this.phase === 'idle') return;
    this.returnToIdle();
  }

  /** Shared hard stop for player abandonment and the cabinet demo. */
  private returnToIdle(): void {
    this.demoMode = false;
    input.x = 0;
    input.z = 0;
    input.beam = false;
    input.special = false;
    consumeSpecialPress();
    this.phase = 'idle';
    this.beamActive = false;
    this.saucer.beam.group.visible = false;
    this.reflection.visible = false;
    for (const ripple of this.reflectionRipples) ripple.visible = false;
    this.waterSpill.visible = false;
    this.shield.group.visible = false;
    this.pops.clear();
    this.silenceRunBeds();
    this.closeBlackHole();
    this.clearRelay();
    this.clearCombine();
    this.clearCrash();
    // Returning to the menu can immediately repaint the preview as Valley.
    // Town's separately-owned grid must leave with the run, not survive that
    // biome repaint as seven orphaned pylons in another land.
    this.clearPylons();
    if (this.ritualPillar) {
      this.world.scene.remove(this.ritualPillar);
      this.ritualPillar = null;
    }
    for (const ring of this.ritualRings) this.world.scene.remove(ring);
    this.ritualRings = [];
    this.ritualT = 0;
    this.ritualSerial++;
    this.tourComplete = false;
    this.extracted = false;
    this.finaleStartedMs = 0;
    this.finaleUntilMs = 0;
    this.finaleSaucerY = 0;
    this.arrivalUntilMs = 0;
    this.hooks.onRitual(false);
    if (this.empRing) {
      this.world.scene.remove(this.empRing.group);
      this.empRing = null;
    }
    for (const arc of this.arcs) this.world.scene.remove(arc.group);
    this.arcs = [];
    for (const drone of this.drones) drone.rig.warning.visible = false;
    for (const normie of this.normies) normie.rig.warning.visible = false;
    this.endStorm(false);
    this.bloodMoon = false;
    this.meteorWave = false;
    this.paused = false;
    this.userPaused = false;
    this.pendingPerks = null;
    this.pendingDecline = false;
    this.threatSurge = null;
    this.waveEvents = [];
    this.hostileRespawns = [];
    this.powerT.clear();
    this.specialQueue = [];
    this.hooks.onPowerups([]);
    this.hooks.onSpecial([]);
    this.hooks.onBoss(null);
    this.hooks.onCarry(null);
    this.hooks.onThreat(null, 0, 0);
    this.hooks.onFever(0, false);
  }

  /* ---------------- attract-mode demo ---------------- */

  startDemo(): void {
    this.demoMode = true;
    this.botThinkT = 0;
    this.botHasTarget = false;
    this.botBeamOn = false;
    this.botSpecialT = 0;
    this.start();
  }

  /** Demo interrupted or finished: back to a clean idle state, no report. */
  abortDemo(): void {
    if (!this.demoMode) return;
    this.returnToIdle();
  }

  /** Cabinet autopilot: chase crates and herd animals, ferry leaders to the stone. */
  private driveBot(dt: number): void {
    this.botSpecialT = Math.max(0, this.botSpecialT - dt);
    const lockingThreat = this.drones.some((drone) => drone.telegraph > 0) ||
      this.normies.some((normie) => normie.telegraph > 0);
    // A collected payload should appear in the reel, not sit forever in a HUD
    // slot. Pressure spends it immediately; a full two-item reserve also fires
    // the oldest payload so the demo can keep collecting and showing systems.
    if (this.specialQueue.length > 0 && this.botSpecialT <= 0 &&
      (lockingThreat || this.bullets.length > 0 || this.specialQueue.length >= 2)) {
      this.useSpecial();
      this.botSpecialT = 8;
    }
    const carried = this.cultists.some((c) => c.mode === 'carried');
    // A slung donkey is the same errand as a leader: fly it home, beam off.
    // Its position is the saucer's own while it hangs there, so leaving it in
    // the ordinary candidate list parks the demo on top of itself.
    const hauling = carried ? undefined : this.cows.find((cow) => cow.ferry === 'carried');
    const pen = hauling ? this.donkeyPen() : null;
    const ferrying = carried || pen !== null;
    let tx: number;
    let tz: number;
    if (carried) {
      tx = this.world.stoneAt.x;
      tz = this.world.stoneAt.z;
      this.botBeamOn = false;
    } else if (pen) {
      tx = pen.x;
      tz = pen.z;
      this.botBeamOn = false;
    } else {
      this.botThinkT -= dt;
      if (!this.botHasTarget || this.botThinkT <= 0) {
        this.botThinkT = 0.9;
        this.pickBotTarget();
      }
      tx = this.botTargetPos.x;
      tz = this.botTargetPos.z;
    }
    const dx = tx - this.saucerPos.x;
    const dz = tz - this.saucerPos.z;
    const dist = Math.hypot(dx, dz);
    // A touch of wander so it reads as flown, not computed.
    const wander = Math.sin(this.timeS * 0.9) * 0.15;
    input.x = THREE.MathUtils.clamp(dx / 7 + wander, -1, 1);
    input.z = THREE.MathUtils.clamp(dz / 7 - wander * 0.6, -1, 1);
    if (!ferrying) {
      if (dist < 2.1) this.botBeamOn = true;
      else if (dist > 4.4) this.botBeamOn = false;
    }
    input.beam = this.botBeamOn && !ferrying;
  }

  private pickBotTarget(): void {
    const sp = this.saucerPos;
    let bx = 0;
    let bz = 0;
    let found = false;
    let bestScore = Infinity;
    const consider = (x: number, z: number, weight: number) => {
      const d = Math.hypot(x - sp.x, z - sp.z) * weight;
      if (d < bestScore) { bestScore = d; bx = x; bz = z; found = true; }
    };
    // Once the beam has started lifting something, finish the job. Lifted
    // entities used to disappear from the candidate list, so the bot changed
    // its mind at the next think tick and spent attract mode repeatedly
    // dropping half-abducted cows, ore, crates and leaders.
    for (const c of this.crates) if (c.lift) consider(c.pos.x, c.pos.z, 0);
    for (const o of this.ores) if (o.lift) consider(o.pos.x, o.pos.z, 0);
    for (const d of this.drones) if (d.lift) consider(d.pos.x, d.pos.z, 0);
    for (const n of this.normies) if (n.lift) consider(n.pos.x, n.pos.z, 0);
    for (const c of this.cultists) if (c.mode === 'ground' && c.lift) consider(c.pos.x, c.pos.z, 0);
    for (const g of this.groupies) if (g.lift) consider(g.pos.x, g.pos.z, 0);
    for (const c of this.cows) if (c.lift) consider(c.pos.x, c.pos.z, 0);
    if (found) {
      this.botTargetPos.set(bx, 0, bz);
      this.botHasTarget = true;
      return;
    }
    // Locking attackers are the one collection target that can interrupt the
    // ordinary crate/ore/leader priority. The beam is the game's counterplay;
    // let the cabinet demonstrate it instead of flying placidly through fire.
    for (const d of this.drones) if (!d.lift && d.telegraph > 0) consider(d.pos.x, d.pos.z, 1);
    for (const n of this.normies) if (!n.lift && n.telegraph > 0) consider(n.pos.x, n.pos.z, 1);
    if (found) {
      this.botTargetPos.set(bx, 0, bz);
      this.botHasTarget = true;
      return;
    }
    // The Taxman belongs on this rung and is deliberately left off it. Measured
    // over eight seeds, a bot that hunts him without dodging dies to him at
    // wave 5 on 7/8, and one that survives clears the whole tour without ever
    // dodging — which breaks what the straight-line pilot is for. The attract
    // reel is 55s and never reaches wave 5 either way. Boss engagement lives in
    // the dodging pilot in `tests/survival.mjs`, whose contract is that it does
    // the taught counterplay.

    for (const c of this.crates) if (!c.lift) consider(c.pos.x, c.pos.z, c.fallY === null ? 0.5 : 0.65);
    for (const o of this.ores) if (!o.lift) consider(o.pos.x, o.pos.z, 0.6);
    for (const c of this.cultists) if (c.mode === 'ground' && !c.lift) consider(c.pos.x, c.pos.z, 0.8);
    for (const g of this.groupies) if (!g.lift) consider(g.pos.x, g.pos.z, 0.65);
    // Chuted cows hang stationary in XZ and are captured the moment the cone
    // touches them, so they are the easiest target on the board, not one to
    // skip: the bot used to ignore COW RAIN entirely and look idle through it.
    // A donkey mid-ferry rides the saucer's own coordinates, and a penned one
    // is finished business — beaming it back out would only cost another trip.
    // A loose one is a side errand with a return leg attached, so it is weighted
    // to be taken when it is genuinely on the way and ignored when it is not.
    for (const c of this.cows) {
      if (c.lift || c.ferry || c.penned) continue;
      // Anything hanging stationary under a canopy is the easiest capture on
      // the board whatever it is, so the chute preference is read first.
      const weight = c.chuteY !== null ? 0.7 : c.species === 'donkey' ? DONKEY_BOT_WEIGHT : 1;
      consider(c.pos.x, c.pos.z, weight);
    }
    if (found) this.botTargetPos.set(bx, 0, bz);
    this.botHasTarget = found;
  }

  /* ---------------- supply-drop power-ups ---------------- */

  private hasPower(kind: PowerKind): boolean {
    return (this.powerT.get(kind) ?? 0) > 0;
  }

  private syncSpecialHud(): void {
    this.hooks.onSpecial(this.specialQueue.map(({ kind, name, emoji, dur }) => ({
      kind: kind as SpecialKind, name, emoji, dur,
    })));
  }

  private storeSpecial(def: PowerDef, announce = true): void {
    this.specialQueue.push(def);
    this.syncSpecialHud();
    if (announce) this.hooks.onBanner(`${def.emoji} ${def.name} STORED #${this.specialQueue.length} — USE SPECIAL`);
  }

  /** Deploy the oldest payload in the reserve. Shift/mobile/gamepad are edge-triggered. */
  private useSpecial(): void {
    const def = this.specialQueue.shift();
    if (!def) {
      this.hooks.onBanner('◇ NO SPECIAL STORED');
      return;
    }
    const total = activatedPowerSeconds(def.kind, this.powerT.get(def.kind) ?? 0, def.dur);
    this.powerT.set(def.kind, total);
    this.powerHudT = 0;
    if (def.kind === 'blackhole') this.openBlackHole();
    else if (def.kind === 'emp') this.fireEmp();
    else if (def.kind === 'cowrain') this.rainCowT = 0.15;
    this.syncSpecialHud();
    this.hooks.onBanner(`${def.emoji} ${def.name} DEPLOYED · ${Math.ceil(total)}S`);
    buzz([18, 25, 35]);
  }

  /** Time warp: enemy fire and flight run at 45% while active. */
  private get warpF(): number {
    return this.hasPower('warp') ? WARP_FACTOR : 1;
  }

  /** `force` is for the dev panel: the rig bakes in the colour and glyph, so the
   *  kind has to be known before the crate is built, not swapped afterwards. */
  private spawnCrate(force?: PowerDef): void {
    if (!supplyRuntime) {
      void this.warmSupply();
      this.crateT = Math.min(this.crateT, 0.25);
      return;
    }
    let def = force ?? POWER_DEFS[Math.floor(Math.random() * POWER_DEFS.length)];
    if (!force && this.hasPower(def.kind)) {
      def = POWER_DEFS[Math.floor(Math.random() * POWER_DEFS.length)]; // one reroll
    }
    const rig = supplyRuntime.buildSupplyDrop(def.color, def.emoji);
    const shadow = makeBlobShadow(0.9);
    const pos = this.randomFieldSpot(8, PLAY_RADIUS - 8);
    this.world.scene.add(rig.group, shadow);
    this.crates.push({
      def, rig, shadow, pos,
      fallY: pos.y + CRATE_DROP_ALT,
      chute: rig.chute,
      airCatch: false,
      lift: null,
      life: CRATE_LIFE_S,
      spin: Math.random() * Math.PI * 2,
    });
    this.hooks.onBanner(`${def.emoji} ${def.name} DESCENDING · CATCH IT`);
  }

  private updateCrates(dt: number): void {
    this.crateT -= dt;
    if (this.crateT <= 0 && this.crates.length < 2) {
      this.spawnCrate();
      this.crateT = (CRATE_GAP_MIN_S + Math.random() * CRATE_GAP_RAND_S) * this.crateGapMult;
    }
    for (let i = this.crates.length - 1; i >= 0; i--) {
      const c = this.crates[i];
      const groundY = this.world.heightAt(c.pos.x, c.pos.z);
      c.spin += dt * 2.2;

      if (c.fallY !== null) {
        c.fallY -= (c.chute ? CRATE_CHUTE_SPEED : CRATE_FALL_SPEED) * dt;
        c.pos.y = c.fallY;
        // The beam points down: wait until the payload is below the hull, then
        // cut the canopy and hand the real altitude to the normal lift model.
        if (c.chute && c.fallY <= this.saucerY - 2.7 && this.inBeam(c.pos.x, c.pos.z)) {
          const caughtY = c.fallY;
          c.rig.group.remove(c.chute);
          c.chute = null;
          c.airCatch = true;
          c.fallY = null;
          c.rig.badge.position.y = 3.2;
          c.lift = { y: caughtY, fallV: 0, peakY: caughtY };
          sfxChutePop(this.panOf(c.pos.x));
          continue;
        }
        if (c.fallY <= groundY) {
          c.fallY = null;
          c.pos.y = groundY;
          if (c.chute) { c.rig.group.remove(c.chute); c.chute = null; }
          c.rig.badge.position.y = 3.2;
          c.rig.group.rotation.z = 0;
          this.poofs.burst(new THREE.Vector3(c.pos.x, groundY + 0.4, c.pos.z), 'orange', 6);
          sfxDrop(this.panOf(c.pos.x));
        } else {
          c.rig.group.position.set(c.pos.x, c.fallY, c.pos.z);
          c.rig.group.rotation.y = c.spin * 0.22;
          c.rig.group.rotation.z = Math.sin(this.timeS * 1.35 + c.spin) * 0.07;
          c.shadow.position.set(c.pos.x, groundY + 0.05, c.pos.z);
          continue;
        }
      }

      if (c.lift) {
        const result = this.updateLift(c.pos, c.lift, dt, groundY, 2.5, LIFT_MASS.crate);
        if (result === 'captured') {
          this.collectCrate(c, i);
          continue;
        }
        if (result === 'dropped') {
          // Crates are mothership-grade: they never break.
          c.lift = null;
          c.pos.y = groundY;
          sfxDrop(this.panOf(c.pos.x));
        } else {
          c.rig.group.position.set(c.pos.x, c.lift.y, c.pos.z);
          c.rig.group.rotation.y = c.spin * 3;
          c.shadow.position.set(c.pos.x, groundY + 0.05, c.pos.z);
          continue;
        }
      } else if (this.inBeam(c.pos.x, c.pos.z)) {
        c.lift = { y: groundY, fallV: 0, peakY: groundY };
        continue;
      }

      c.life -= dt;
      if (c.life <= 0) {
        this.world.scene.remove(c.rig.group, c.shadow);
        this.crates.splice(i, 1);
        this.poofs.burst(new THREE.Vector3(c.pos.x, groundY + 0.6, c.pos.z), 'blue', 4);
        continue;
      }
      const bob = Math.sin(this.timeS * 2.6 + c.spin) * 0.12;
      c.rig.group.position.set(c.pos.x, groundY + bob, c.pos.z);
      c.rig.group.rotation.y = c.spin;
      (c.rig.halo.material as THREE.MeshBasicMaterial).opacity = 0.5 + Math.sin(this.timeS * 4) * 0.25;
      (c.rig.column.material as THREE.MeshBasicMaterial).opacity = 0.15 + Math.sin(this.timeS * 2.3) * 0.06;
      const lit = c.life > 4 || Math.floor(this.timeS * 6) % 2 === 0; // expiry blink
      c.rig.core.visible = lit;
      c.rig.badge.visible = lit;
      c.shadow.position.set(c.pos.x, groundY + 0.05, c.pos.z);
    }
  }

  private collectCrate(c: CrateEnt, idx: number): void {
    this.world.scene.remove(c.rig.group, c.shadow);
    this.crates.splice(idx, 1);
    if (isSpecialKind(c.def.kind)) {
      this.storeSpecial(c.def);
    } else {
      const previous = this.powerT.get(c.def.kind) ?? 0;
      const total = activatedPowerSeconds(c.def.kind, previous, c.def.dur);
      this.powerT.set(c.def.kind, total);
      this.powerHudT = 0;
      this.hooks.onBanner(previous > 0
        ? `${c.def.emoji} ${c.def.name} STACKED · +${c.def.dur}S · ${Math.ceil(total)}S`
        : `${c.def.emoji} ${c.def.name} ONLINE · ${c.def.dur}S`);
    }
    sfxComboUp(6);
    playVoiceClip(VOICE.whoo, 0.8);
    buzz([15, 25, 15]);
    this.poofs.burst(new THREE.Vector3(c.pos.x, this.saucerY - 1, c.pos.z), 'orange', 10);
    this.awardBadge('supply');
    const pts = this.addScore(c.airCatch ? 300 : 150);
    this.popScore(c.pos.x, this.saucerY - 1.6, c.pos.z, pts);
  }

  private updatePowerups(dt: number): void {
    let changed = false;
    for (const [kind, t] of this.powerT) {
      const left = t - dt;
      if (left <= 0) {
        this.powerT.delete(kind);
        changed = true;
      } else {
        this.powerT.set(kind, left);
      }
    }
    this.powerHudT -= dt;
    if ((this.powerT.size > 0 && this.powerHudT <= 0) || changed) {
      this.powerHudT = 0.1;
      const pills = POWER_DEFS
        .filter((d) => this.powerT.has(d.kind))
        .map((d) => {
          const remaining = this.powerT.get(d.kind) ?? 0;
          return {
            kind: d.kind, emoji: d.emoji, name: d.name, color: d.color,
            frac: Math.min(1, remaining / d.dur), seconds: Math.max(1, Math.ceil(remaining)),
          };
        });
      this.hooks.onPowerups(pills);
    }
  }

  /** Magnet beam: drag ground targets toward the beam column. */
  private magnetPull(pos: THREE.Vector3, dt: number): void {
    if (!this.hasPower('magnet') || !this.beamActive) return;
    const dx = this.saucerPos.x - pos.x;
    const dz = this.saucerPos.z - pos.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.001 || d > MAGNET_RANGE) return;
    const pull = MAGNET_PULL * dt / d;
    pos.x += dx * pull;
    pos.z += dz * pull;
  }

  /* ---------------- black hole ---------------- */

  private openBlackHole(): void {
    this.closeBlackHole();
    if (!lateWorldRuntime) {
      const run = this.runId;
      void loadLateWorld().then(() => {
        if (!this.hole && this.phase === 'playing' && this.runId === run && this.hasPower('blackhole')) this.openBlackHole();
      });
      return;
    }
    const x = this.saucerPos.x;
    const z = this.saucerPos.z;
    const y = this.world.heightAt(x, z);
    const rig = lateWorldRuntime.buildBlackHole();
    rig.group.position.set(x, y + 0.15, z);
    this.world.scene.add(rig.group);
    this.hole = { rig, pos: new THREE.Vector3(x, y, z), age: 0 };
    this.holeConsumed = 0;
    sfxBlackHoleOpen();
    setBlackHoleHum(true);
    this.addShake(0.5);
    buzz([20, 30, 50]);
  }

  private closeBlackHole(): void {
    if (!this.hole) return;
    this.world.scene.remove(this.hole.rig.group);
    this.poofs.burst(this.hole.pos.clone().setY(this.hole.pos.y + 0.8), 'blue', 12);
    this.hole = null;
    setBlackHoleHum(false);
  }

  /** Drag a ground position toward the singularity. Returns distance. */
  private holePull(pos: THREE.Vector3, dt: number, factor = 1, radius = HOLE_RADIUS): number {
    const hole = this.hole!;
    const dx = hole.pos.x - pos.x;
    const dz = hole.pos.z - pos.z;
    const d = Math.hypot(dx, dz);
    if (d > radius || d < 0.001) return d;
    const pull = HOLE_PULL * factor * (1.25 - d / radius) * dt / d;
    pos.x += dx * pull;
    pos.z += dz * pull;
    return Math.hypot(hole.pos.x - pos.x, hole.pos.z - pos.z);
  }

  private consumeAtHole(kind: 'cow' | 'normie' | 'drone', base: number, pos: THREE.Vector3): void {
    const hole = this.hole!;
    const pts = this.addScore(base);
    this.popScore(hole.pos.x, hole.pos.y + 2 + Math.random() * 1.5, hole.pos.z, pts);
    this.addFever(0.1);
    this.bumpCombo();
    this.progressQuota();
    sfxGulp(0.85 + Math.random() * 0.4);
    this.poofs.burst(new THREE.Vector3(pos.x, hole.pos.y + 0.7, pos.z), 'blue', 5);
    this.holeConsumed++;
    if (this.holeConsumed >= 8) this.awardBadge('singularity');
    void kind;
  }

  private updateHole(dt: number): void {
    const hole = this.hole;
    if (!hole) return;
    if (!this.hasPower('blackhole')) {
      this.closeBlackHole();
      return;
    }
    const left = this.powerT.get('blackhole') ?? 0;
    hole.age += dt;
    const wind = Math.min(1, hole.age * 3);                  // spin-up
    const fade = Math.min(1, left / 1.2);                    // last-second collapse
    const g = hole.rig;
    g.disc.rotation.z -= dt * (2.6 + (1 - fade) * 6);
    g.group.scale.setScalar((0.25 + wind * 0.75) * (0.3 + fade * 0.7));
    (g.disc.material as THREE.MeshBasicMaterial).opacity = 0.9 * fade;
    (g.ring.material as THREE.MeshBasicMaterial).opacity = (0.6 + Math.sin(this.timeS * 9) * 0.3) * fade;
    g.ring.scale.setScalar(1 + Math.sin(this.timeS * 5) * 0.12);

    // Herd animals: dragged and, at the horizon, filed under liabilities.
    for (let i = this.cows.length - 1; i >= 0; i--) {
      const cow = this.cows[i];
      // A donkey slung under the hull shares the saucer's own coordinates, so
      // every hazard that reads a herd position would otherwise destroy it
      // wherever the pilot happens to fly. The ferry is not lost by accident.
      if (cow.lift || cow.ferry || cow.chuteY !== null) continue;
      if (cow.heifer) continue; // never yoked, never swallowed
      const d = this.holePull(cow.pos, dt);
      if (d < HOLE_EAT_R) {
        this.consumeAtHole('cow', this.bloodMoon ? 200 : 100, cow.pos);
        this.world.scene.remove(cow.rig, cow.shadow);
        this.cows.splice(i, 1);
        this.spawnCow();
      } else if (d < HOLE_RADIUS) {
        cow.state = 'panic';
        cow.stateT = Math.max(cow.stateT, 0.4);
        cow.heading = Math.atan2(hole.pos.z - cow.pos.z, hole.pos.x - cow.pos.x);
        if (Math.random() < dt * 0.7) {
          playAnimalCall(cow.species, 0.8 + Math.random() * 0.2, 0.5, this.panOf(cow.pos.x));
        }
      }
    }
    // Armed locals and airborne drones are the singularity's most satisfying
    // targets. Give hostiles a wider, stronger capture field than livestock:
    // a nine-second special should visibly remove baddies, not merely ruffle a
    // cow that happened to be parked on its tiny centre point.
    for (let i = this.normies.length - 1; i >= 0; i--) {
      const normie = this.normies[i];
      if (normie.lift) continue;
      const d = this.holePull(normie.pos, dt, 1.65, HOLE_HOSTILE_RADIUS);
      if (d < HOLE_EAT_R) {
        this.consumeAtHole('normie', 300, normie.pos);
        this.world.scene.remove(normie.rig.group, normie.shadow);
        this.normies.splice(i, 1);
        this.scheduleHostileRespawn('shooter');
      }
    }
    for (let i = this.drones.length - 1; i >= 0; i--) {
      const drone = this.drones[i];
      if (drone.lift) continue;
      const d = this.holePull(drone.pos, dt, 1.9, HOLE_HOSTILE_RADIUS);
      // Drone patrols orbit the world origin, so retain the pulled position in
      // their polar authority before updateDrones advances the next frame.
      drone.radius = Math.hypot(drone.pos.x, drone.pos.z);
      drone.angle = Math.atan2(drone.pos.z, drone.pos.x);
      drone.stunT = Math.max(drone.stunT, 0.2);
      drone.telegraph = 0;
      if (d < HOLE_EAT_R + 0.8) {
        this.consumeAtHole('drone', 250, drone.pos);
        this.world.scene.remove(drone.rig.group, drone.shadow);
        this.drones.splice(i, 1);
      }
    }
    // The relay is bolted down, but not to spacetime. Pulling it over the
    // horizon cuts replacements without turning the singularity into quota.
    if (this.relay) {
      const d = this.holePull(this.relay.pos, dt, 0.55);
      this.relay.pos.y = this.world.heightAt(this.relay.pos.x, this.relay.pos.z);
      this.relay.rig.group.position.copy(this.relay.pos);
      this.relay.shadow.position.set(this.relay.pos.x, this.relay.pos.y + 0.04, this.relay.pos.z);
      if (d < HOLE_EAT_R + 0.7) this.destroyRelay('blackhole');
    }
    // Cultists are dragged but the prophecy forbids the ledger losing them.
    for (const cult of this.cultists) {
      if (cult.mode !== 'ground' || cult.lift) continue;
      const d = this.holePull(cult.pos, dt, 0.35);
      if (d < HOLE_EAT_R + 1) {
        const away = Math.atan2(cult.pos.z - hole.pos.z, cult.pos.x - hole.pos.x);
        cult.pos.x = hole.pos.x + Math.cos(away) * (HOLE_EAT_R + 1);
        cult.pos.z = hole.pos.z + Math.sin(away) * (HOLE_EAT_R + 1);
      }
    }
    // Bullets spiral in and are gone.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      const dx = hole.pos.x - b.mesh.position.x;
      const dy = hole.pos.y + 0.6 - b.mesh.position.y;
      const dz = hole.pos.z - b.mesh.position.z;
      const d = Math.hypot(dx, dy, dz);
      if (d < HOLE_RADIUS * 0.8) {
        b.vel.x += dx / d * 40 * dt;
        b.vel.y += dy / d * 40 * dt;
        b.vel.z += dz / d * 40 * dt;
        if (d < 1.2) {
          this.world.scene.remove(b.mesh);
          this.bullets.splice(i, 1);
          this.poofs.burst(b.mesh.position.clone(), 'blue', 3);
        }
      }
    }
  }

  /* ---------------- EMP shockwave ---------------- */

  private fireEmp(): void {
    if (this.empRing) this.world.scene.remove(this.empRing.group);
    this.empRing = buildEmpRing();
    this.empRing.group.position.set(this.saucerPos.x, this.saucerY - 0.6, this.saucerPos.z);
    this.world.scene.add(this.empRing.group);
    this.empRingT = EMP_BLAST_S;
    sfxEmpBlast();
    this.hooks.onFlash(0.45);
    this.addShake(0.5);
    buzz([10, 15, 60]);
    for (const b of this.bullets) {
      this.poofs.burst(b.mesh.position.clone(), 'blue', 2);
      this.world.scene.remove(b.mesh);
    }
    this.bullets = [];
    // Drones are flying electronics: a full EMP pulse fries every one already
    // in the sky. Ground shooters, the Taxman, relay and combine retain the
    // advertised six-second lockout instead of being silently one-shot.
    const fried = this.drones.splice(0);
    for (const drone of fried) {
      this.poofs.burst(drone.pos.clone().setY(drone.pos.y + 1), 'blue', 12);
      this.world.scene.remove(drone.rig.group, drone.shadow);
    }
    if (fried.length > 0) {
      const points = this.addScore(250 * fried.length);
      this.popScore(this.saucerPos.x, this.saucerY + 1.4, this.saucerPos.z, points);
      this.addFever(Math.min(0.6, fried.length * 0.12));
      this.bumpCombo();
      this.hooks.onBanner(`💥 EMP FRIED ${fried.length} DRONE${fried.length === 1 ? '' : 'S'}  +${points}`);
    }
    for (const n of this.normies) { n.stunT = Math.max(n.stunT, EMP_STUN_S); n.telegraph = 0; }
    if (this.boss) { this.boss.stunT = Math.max(this.boss.stunT, EMP_STUN_S); this.boss.telegraph = 0; }
    if (this.relay) this.relay.stunT = Math.max(this.relay.stunT, EMP_STUN_S);
    this.combine?.stun(EMP_STUN_S);
  }

  /** Keep the displayed EMP countdown and the real electronics lockout in the
   * same authority. This also catches a hostile spawned during the pulse. */
  private applyEmpField(): void {
    const left = this.powerT.get('emp') ?? 0;
    if (left <= 0) return;
    for (const d of this.drones) { d.stunT = Math.max(d.stunT, left); d.telegraph = 0; }
    for (const n of this.normies) { n.stunT = Math.max(n.stunT, left); n.telegraph = 0; }
    if (this.boss) { this.boss.stunT = Math.max(this.boss.stunT, left); this.boss.telegraph = 0; }
    if (this.relay) this.relay.stunT = Math.max(this.relay.stunT, left);
    this.combine?.stun(left);
  }

  private updateEmpRing(dt: number): void {
    if (!this.empRing) return;
    this.empRingT -= dt;
    if (this.empRingT <= 0) {
      this.world.scene.remove(this.empRing.group);
      this.empRing = null;
      return;
    }
    const rig = this.empRing;
    const t = 1 - this.empRingT / EMP_BLAST_S;
    const ease = 1 - (1 - t) * (1 - t);
    const fade = Math.pow(1 - t, 1.35);
    rig.group.scale.setScalar(0.65 + ease * 42);
    rig.group.rotation.y += dt * (1.8 + t * 4);
    rig.rings.forEach((ring, i) => {
      ring.rotation.z += dt * (i % 2 === 0 ? 2.4 : -3.1);
      ring.scale.setScalar(0.84 + i * 0.1 + Math.sin(t * 18 - i) * 0.045);
      (ring.material as THREE.MeshBasicMaterial).opacity = (0.92 - i * 0.14) * fade;
    });
    (rig.shell.material as THREE.MeshBasicMaterial).opacity = 0.34 * fade;
    rig.shell.rotation.x += dt * 2.8;
    rig.shell.rotation.z -= dt * 2.1;
    (rig.bolts.material as THREE.LineBasicMaterial).opacity = (0.45 + Math.sin(t * 95) * 0.35) * fade;
    rig.bolts.rotation.y -= dt * 5.5;
    rig.glow.material.opacity = 0.78 * fade;
    rig.glow.material.rotation += dt * 1.7;
  }

  /* ---------------- cow rain ---------------- */

  private updateCowRain(dt: number): void {
    if (!this.hasPower('cowrain')) return;
    if (!supplyRuntime) { void this.warmSupply(); return; }
    this.rainCowT -= dt;
    if (this.rainCowT > 0) return;
    this.rainCowT = COWRAIN_INTERVAL_S;
    // Find a drop spot near the saucer, not over the lake, inside the field.
    for (let tries = 0; tries < 6; tries++) {
      const a = Math.random() * Math.PI * 2;
      const r = 5 + Math.random() * 12;
      const x = this.saucerPos.x + Math.cos(a) * r;
      const z = this.saucerPos.z + Math.sin(a) * r;
      if (Math.hypot(x, z) > PLAY_RADIUS - 4) continue;
      if (Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r + 2) continue;
      // COW RAIN remains the joke on the label: these are literal cows even
      // when the local herd is made of goats or reindeer.
      const species: AnimalSpecies = 'cow';
      const rig = buildAnimal(species);
      const shadow = makeBlobShadow(1.1);
      const chute = supplyRuntime.buildChute();
      rig.add(chute);
      this.world.scene.add(rig, shadow);
      const chuteY = this.saucerY + 12 + Math.random() * 5;
      rig.position.set(x, chuteY, z);
      this.cows.push({
        rig, species, shadow,
        pos: new THREE.Vector3(x, this.world.heightAt(x, z), z),
        heading: Math.random() * Math.PI * 2,
        state: 'graze',
        stateT: 2,
        target: this.randomFieldSpot(),
        lift: null,
        bobT: 0,
        squashT: 0,
        heifer: false,
        chute,
        chuteY,
      });
      return;
    }
  }

  /* ---------------- cattle convoy ---------------- */

  private async spawnConvoy(): Promise<void> {
    // One convoy on the ring road at a time — `updateConvoy` only schedules a
    // new one when the road is clear, and that rule belongs here too.
    if (this.trucks.length > 0) return;
    const land = this.world.biome;
    const run = this.runId;
    const { buildTruck, spaceTrainVoice } = await loadLateWorld();
    if (this.phase !== 'playing' || this.runId !== run || this.world.biome !== land || this.trucks.length) return;
    const offWorld = land === 'mars' || land === 'moon';
    const start = Math.random() * Math.PI * 2;
    for (let i = 0; i < 3; i++) {
      const rig = buildTruck(i, land);
      const cows: (TruckCow | null)[] = [];
      for (const slot of rig.cowSlots) {
        const species: AnimalSpecies = offWorld ? animalForBiome(land).species : 'cow';
        const cowRig = buildAnimal(species);
        cowRig.scale.setScalar(0.82);
        cowRig.position.copy(slot);
        cows.push({ rig: cowRig, slot: slot.clone(), species });
        rig.group.add(cowRig);
      }
      this.world.scene.add(rig.group);
      this.trucks.push({
        rig,
        angle: start - i * 0.16,
        travelled: 0,
        cows,
        honkT: 1.5 + i * 0.8,
      });
    }
    this.hooks.onBanner(offWorld
      ? '🚂 PRESSURISED LIVESTOCK TRAIN ON THE RING ROAD'
      : '🚚 CATTLE CONVOY ON THE RING ROAD');
    sfxTruckHorn(0);
    setTimeout(() => {
      if (this.phase === 'playing' && this.runId === run) {
        playVoiceClip(offWorld ? spaceTrainVoice : VOICE.ttsConvoy, 0.95);
      }
    }, 900);
  }

  private updateConvoy(dt: number): void {
    if (this.trucks.length === 0) {
      if (this.wave >= 3) {
        this.convoyT -= dt;
        if (this.convoyT <= 0) {
          this.spawnConvoy();
          this.convoyT = CONVOY_GAP_MIN_S + Math.random() * CONVOY_GAP_RAND_S;
        }
      }
      return;
    }
    for (let i = this.trucks.length - 1; i >= 0; i--) {
      const truck = this.trucks[i];
      const step = CONVOY_SPEED * this.warpF * dt;
      truck.angle += step;
      truck.travelled += step;
      const x = Math.cos(truck.angle) * ROAD.r;
      const z = Math.sin(truck.angle) * ROAD.r;
      const y = this.world.heightAt(x, z);
      const heading = truck.angle + Math.PI / 2;
      truck.rig.group.position.set(x, y + Math.sin(this.timeS * 11 + i) * 0.03, z);
      truck.rig.group.rotation.y = -heading;

      // Done a full lap (and change): drive off into the fog.
      if (truck.travelled > Math.PI * 2.1) {
        this.world.scene.remove(truck.rig.group);
        this.trucks.splice(i, 1);
        continue;
      }

      // Air horn when the saucer shadows the cab.
      truck.honkT -= dt;
      const saucerDist = Math.hypot(x - this.saucerPos.x, z - this.saucerPos.z);
      if (truck.honkT <= 0 && saucerDist < 16) {
        sfxTruckHorn(this.panOf(x));
        truck.honkT = 6 + Math.random() * 3;
      }

      // Beam a cow straight off the moving flatbed.
      for (let s = 0; s < truck.cows.length; s++) {
        const tc = truck.cows[s];
        if (!tc) continue;
        const world = tc.slot.clone();
        truck.rig.group.localToWorld(world);
        tc.rig.position.y = tc.slot.y + Math.sin(this.timeS * 9 + s * 2) * 0.05; // nervous cargo
        if (this.beamActive &&
            Math.hypot(world.x - this.saucerPos.x, world.z - this.saucerPos.z) < BEAM_CAPTURE_RADIUS * this.beamRadiusMult) {
          truck.rig.group.remove(tc.rig);
          truck.cows[s] = null;
          this.world.scene.add(tc.rig);
          tc.rig.position.copy(world);
          const shadow = makeBlobShadow(1.1);
          this.world.scene.add(shadow);
          this.cows.push({
            rig: tc.rig,
            species: tc.species,
            shadow,
            pos: new THREE.Vector3(world.x, this.world.heightAt(world.x, world.z), world.z),
            heading,
            state: 'panic',
            stateT: 2,
            target: this.randomFieldSpot(),
            lift: { y: world.y, fallV: 0, peakY: world.y },
            bobT: 0,
            squashT: 0,
            heifer: false,
            rustled: true,
            chute: null,
            chuteY: null,
          });
          playAnimalCall(tc.species, 1.1, 0.9, this.panOf(world.x));
        }
      }

      // Roadkill: the convoy does not brake for the herd.
      const fx = x + Math.cos(heading) * 3;
      const fz = z + Math.sin(heading) * 3;
      for (let c = this.cows.length - 1; c >= 0; c--) {
        const cow = this.cows[c];
        if (cow.lift || cow.ferry || cow.chuteY !== null) continue;
        if (Math.hypot(cow.pos.x - fx, cow.pos.z - fz) < 1.7) {
          this.splatAt(cow.pos.x, cow.pos.y, cow.pos.z, 1.8);
          this.hooks.onBanner('ROAD KILL');
          this.world.scene.remove(cow.rig, cow.shadow);
          this.cows.splice(c, 1);
          this.spawnCow();
        }
      }
    }
  }

  /* ---------------- meteor shower ---------------- */

  private spawnMeteor(): void {
    let x: number;
    let z: number;
    if (Math.random() < 0.35) {
      const a = Math.random() * Math.PI * 2;
      const r = 3 + Math.random() * 9;
      x = this.saucerPos.x + Math.cos(a) * r;
      z = this.saucerPos.z + Math.sin(a) * r;
    } else {
      const p = this.randomFieldSpot(6, PLAY_RADIUS - 6);
      x = p.x;
      z = p.z;
    }
    const gy = this.world.heightAt(x, z);
    const decal = new THREE.Mesh(
      new THREE.RingGeometry(1.5, 2.1, 24),
      new THREE.MeshBasicMaterial({
        color: '#ffab5e',
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    decal.rotation.x = -Math.PI / 2;
    decal.position.set(x, gy + 0.12, z);
    this.world.scene.add(decal);
    const rig = buildMeteor();
    rig.group.position.set(x, gy + METEOR_SPAWN_Y, z);
    this.world.scene.add(rig.group);
    this.meteors.push({ rig, x, z, y: gy + METEOR_SPAWN_Y, decal });
    sfxMeteorFall(this.panOf(x));
  }

  private updateMeteors(dt: number): void {
    if (this.meteorWave) {
      this.meteorT -= dt;
      if (this.meteorT <= 0) {
        this.spawnMeteor();
        this.meteorT = METEOR_GAP_MIN_S + Math.random() * METEOR_GAP_RAND_S;
      }
    }
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const m = this.meteors[i];
      const gy = this.world.heightAt(m.x, m.z);
      m.y -= METEOR_FALL_SPEED * dt;
      m.rig.group.position.y = m.y;
      m.rig.rock.rotation.x += dt * 7;
      m.rig.rock.rotation.y += dt * 5;
      (m.decal.material as THREE.MeshBasicMaterial).opacity = 0.45 + Math.sin(this.timeS * 16) * 0.3;
      if (m.y <= gy + 0.4) {
        this.world.scene.remove(m.rig.group, m.decal);
        this.meteors.splice(i, 1);
        this.impactMeteor(m.x, gy, m.z);
      }
    }
  }

  private impactMeteor(x: number, gy: number, z: number): void {
    const pan = this.panOf(x);
    sfxMeteorImpact(pan);
    const distToSaucer = Math.hypot(x - this.saucerPos.x, z - this.saucerPos.z);
    const near = distToSaucer < 20;
    this.addShake(near ? 0.8 : 0.3);
    if (near) this.hooks.onFlash(0.3);
    this.poofs.burst(new THREE.Vector3(x, gy + 0.8, z), 'orange', 14);
    this.poofs.burst(new THREE.Vector3(x, gy + 1.4, z), 'red', 5);
    this.world.corn.flattenAt(x, z, 2.3, Math.random() < 0.5 ? 1 : -1);
    if (distToSaucer < 3.4 && this.invulnT <= 0) this.hullHit('meteor');
    for (let i = this.cows.length - 1; i >= 0; i--) {
      const cow = this.cows[i];
      if (cow.lift || cow.ferry || cow.chuteY !== null) continue;
      if (Math.hypot(cow.pos.x - x, cow.pos.z - z) < 2.6) {
        if (cow.heifer) {
          cow.squashT = 0.45;
          this.hooks.onBanner('THE CALF ABIDES');
          continue;
        }
        this.splatAt(cow.pos.x, cow.pos.y, cow.pos.z, 1.7);
        this.world.scene.remove(cow.rig, cow.shadow);
        this.cows.splice(i, 1);
        this.spawnCow();
      }
    }
    for (let i = this.normies.length - 1; i >= 0; i--) {
      const normie = this.normies[i];
      if (normie.lift) continue;
      if (Math.hypot(normie.pos.x - x, normie.pos.z - z) < 2.6) {
        this.splatAt(normie.pos.x, normie.pos.y, normie.pos.z, 1.4);
        this.world.scene.remove(normie.rig.group, normie.shadow);
        this.normies.splice(i, 1);
        const pts = this.addScore(250);
        this.popScore(normie.pos.x, normie.pos.y + 2, normie.pos.z, pts);
        this.hooks.onBanner(`SPACE JUSTICE  +${pts}`);
        this.scheduleHostileRespawn('shooter');
      }
    }
    for (const cult of this.cultists) {
      if (cult.mode === 'ground' && Math.hypot(cult.pos.x - x, cult.pos.z - z) < 2.6) {
        cult.squashT = 0.45;
        this.poofs.burst(new THREE.Vector3(cult.pos.x, cult.pos.y + 1, cult.pos.z), 'orange', 5);
      }
    }
    if (Math.random() < 0.45) {
      const rig = buildOre();
      rig.group.position.set(x, gy, z);
      this.world.scene.add(rig.group);
      this.ores.push({ rig, pos: new THREE.Vector3(x, gy, z), lift: null, life: ORE_LIFE_S });
    }
  }

  private updateOres(dt: number): void {
    for (let i = this.ores.length - 1; i >= 0; i--) {
      const ore = this.ores[i];
      const gy = this.world.heightAt(ore.pos.x, ore.pos.z);
      if (ore.lift) {
        const result = this.updateLift(ore.pos, ore.lift, dt, gy, 2.5, LIFT_MASS.ore);
        if (result === 'captured') {
          this.world.scene.remove(ore.rig.group);
          this.ores.splice(i, 1);
          const pts = this.addScore(350);
          this.popScore(ore.pos.x, this.saucerY - 1.6, ore.pos.z, pts);
          this.hooks.onBanner(`SKY ORE SECURED  +${pts}`);
          this.addFever(0.2);
          this.bumpCombo();
          sfxAbductPop();
          this.awardBadge('moonrock');
          this.poofs.burst(new THREE.Vector3(ore.pos.x, this.saucerY - 1, ore.pos.z), 'orange');
          continue;
        }
        if (result === 'dropped') {
          ore.lift = null; // ore does not shatter; it has been through worse
          ore.pos.y = gy;
          sfxDrop(this.panOf(ore.pos.x));
        } else {
          ore.rig.group.position.set(ore.pos.x, ore.lift.y, ore.pos.z);
          ore.rig.group.rotation.y += dt * 4;
          continue;
        }
      } else if (this.inBeam(ore.pos.x, ore.pos.z)) {
        ore.lift = { y: gy, fallV: 0, peakY: gy };
        continue;
      }
      ore.life -= dt;
      if (ore.life <= 0) {
        this.world.scene.remove(ore.rig.group);
        this.ores.splice(i, 1);
        this.poofs.burst(new THREE.Vector3(ore.pos.x, gy + 0.5, ore.pos.z), 'orange', 4);
        continue;
      }
      ore.rig.group.position.set(ore.pos.x, gy + Math.sin(this.timeS * 3.2 + i) * 0.08, ore.pos.z);
      ore.rig.group.rotation.y += dt * 1.4;
      ore.rig.halo.material.opacity = 0.4 + Math.sin(this.timeS * 5 + i) * 0.2;
      ore.rig.core.visible = ore.life > 4 || Math.floor(this.timeS * 6) % 2 === 0;
    }
  }

  /* ---------------- blood moon ---------------- */

  private startBloodMoon(): void {
    this.bloodMoon = true;
    // The first howl follows active simulation time, so pausing immediately
    // after the warning cannot make it happen unheard in the background.
    this.howlT = 2.4;
    this.hooks.onBanner('🌘 BLOOD MOON — THE HERD RUNS SCARED, WORTH DOUBLE');
    playVoiceClip(VOICE.ttsBloodmoon, 0.95);
  }

  private endBloodMoon(cleared: boolean): void {
    if (!this.bloodMoon) return;
    this.bloodMoon = false;
    if (cleared) this.awardBadge('bloodmoon');
  }

  /* ---------------- council ritual ---------------- */

  private startRitual(): void {
    this.ritualT = RITUAL_MIN_S;
    this.ritualMinUntilMs = performance.now() + RITUAL_MIN_S * 1000;
    this.ritualLastUpdateMs = performance.now();
    this.ritualMaxT = RITUAL_MAX_S;
    this.ritualVoiceDone = false;
    this.finaleStartedMs = 0;
    this.finaleUntilMs = 0;
    const ritualSerial = ++this.ritualSerial;
    // The ceremony owns this moment: no lingering beam or hostile indicator,
    // and the next land warms behind the old one while the council aligns.
    this.beamActive = false;
    this.saucer.beam.group.visible = false;
    this.saucerVel.set(0, 0, 0);
    setBeamHum(false);
    this.hooks.onThreat(null, 0, 0);
    this.hooks.onRitual(true);
    const finalCouncil = this.stage >= 7 && this.world.biome === 'moon';
    if (!finalCouncil) {
      const nextLand = nextBiome(this.world.biome);
      this.world.warmBiome(nextLand);
      // The ritual is five seconds of guaranteed presentation time: use it to
      // buffer the next land's own bed so its reveal never inherits the old one.
      void preloadMusicTrack(BIOME_TRACK[nextLand]);
      for (const member of openingMembersForBiome(nextLand)) void preloadCult3dModel(member.id);
    }
    if (this.ritualPillar) this.world.scene.remove(this.ritualPillar);
    for (const ring of this.ritualRings) this.world.scene.remove(ring);
    this.ritualRings = [];
    const stone = this.world.stoneAt;
    const pillar = new THREE.Mesh(
      new THREE.CylinderGeometry(2.1, 3, 64, 18, 1, true),
      new THREE.MeshBasicMaterial({
        color: '#ffd24a',
        transparent: true,
        opacity: 0.3,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    pillar.position.set(stone.x, stone.y + 32, stone.z);
    this.world.scene.add(pillar);
    this.ritualPillar = pillar;

    for (let i = 0; i < 3; i++) {
      const radius = 2.35 + i * 1.55;
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(radius, radius + 0.16, 48),
        new THREE.MeshBasicMaterial({
          color: i === 1 ? '#fff0a6' : '#f7931a',
          transparent: true,
          opacity: 0.38 - i * 0.06,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(stone.x, stone.y + 0.16 + i * 0.015, stone.z);
      this.world.scene.add(ring);
      this.ritualRings.push(ring);
    }
    sfxChoir();
    // This 7.14s line used to begin only after the biome swap. Track the real
    // source end (including its wait behind the final member name) so the old
    // land, formation and fires remain on screen for the complete line.
    void playVoiceClipTracked(VOICE.ttsDepart, 0.95).then(() => {
      if (this.ritualSerial === ritualSerial) this.ritualVoiceDone = true;
    });
  }

  private updateRitual(dt: number): void {
    const pillar = this.ritualPillar;
    if (!pillar) return;
    if (this.finaleUntilMs > 0) {
      this.updateTourFinale(dt);
      return;
    }
    // Audio and a player's screen run in real time even when fixed-step
    // simulation sheds frames. Do not leave a dead, silent tail on a hot phone.
    this.ritualT = Math.max(0, (this.ritualMinUntilMs - performance.now()) / 1000);
    this.ritualMaxT -= dt;
    if (pillar) {
      pillar.rotation.y += dt * 0.8;
      // Once the five-second visual minimum elapses, hold at full strength if
      // speech is still running. The source-end callback owns the actual cut.
      const visualRemaining = this.ritualVoiceDone ? this.ritualT : Math.max(this.ritualT, 1.5);
      const fade = Math.min(1, visualRemaining / 1.5);
      (pillar.material as THREE.MeshBasicMaterial).opacity = (0.24 + Math.sin(this.timeS * 7) * 0.08) * fade;
      for (let i = 0; i < this.ritualRings.length; i++) {
        const ring = this.ritualRings[i];
        const pulse = 1 + Math.sin(this.timeS * (2.2 + i * 0.35) + i) * 0.045;
        ring.scale.setScalar(pulse);
        (ring.material as THREE.MeshBasicMaterial).opacity = (0.3 + Math.sin(this.timeS * 4 + i) * 0.12) * fade;
      }
      if (Math.random() < dt * 6) {
        const stone = this.world.stoneAt;
        const a = Math.random() * Math.PI * 2;
        this.poofs.burst(new THREE.Vector3(stone.x + Math.cos(a) * 4.2, stone.y + 1.5, stone.z + Math.sin(a) * 4.2), 'orange', 3);
      }
    }
    if ((this.ritualT <= 0 && this.ritualVoiceDone) || this.ritualMaxT <= 0) {
      if (this.stage >= 7 && this.world.biome === 'moon') {
        this.beginTourFinale();
        return;
      }
      this.world.scene.remove(pillar);
      this.ritualPillar = null;
      for (const ring of this.ritualRings) this.world.scene.remove(ring);
      this.ritualRings = [];
      this.ritualSerial++;
      // The ritual only ever fires on a completed council: time to move on.
      this.departStage();
    }
  }

  /** The seventh rite is an ending, not an eighth departure back to Valley. */
  private beginTourFinale(): void {
    if (this.tourComplete || !this.ritualPillar) return;
    this.tourComplete = true;
    this.finaleStartedMs = performance.now();
    this.finaleUntilMs = this.finaleStartedMs + TOUR_FINALE_MS;
    this.finaleSaucerY = this.saucerY;
    this.ritualSerial++;
    const bonus = STAGE_BONUS * this.stage;
    this.score += bonus;
    this.hooks.onScore(this.score);
    this.hooks.onCarry(null);
    this.hooks.onThreat(null, 0, 0);
    this.hooks.onFlash(0.9);
    this.hooks.onBiome(
      'THE LEDGER IS COMPLETE',
      `SEVEN LANDS · COUNCIL DELIVERED · +${bonus.toLocaleString('en-GB')}`,
      TOUR_FINALE_MS,
    );
    sfxTourComplete();
  }

  private updateTourFinale(dt: number): void {
    const now = performance.now();
    const progress = Math.min(1, Math.max(0, (now - this.finaleStartedMs) / TOUR_FINALE_MS));
    const eased = progress * progress * (3 - 2 * progress);
    this.saucerY = this.finaleSaucerY + eased * 18;
    const delivered = this.cultists.filter((cult) => cult.mode === 'delivered');
    for (let i = 0; i < delivered.length; i++) {
      const cult = delivered[i];
      const local = Math.min(1, Math.max(0, progress * 1.22 - i * 0.018));
      const lift = local * local * (3 - 2 * local);
      cult.rig.group.position.y = cult.pos.y + lift * 13;
      cult.rig.group.rotation.y += dt * (0.7 + i * 0.035);
      cult.shadow.visible = local < 0.72;
    }
    const pillar = this.ritualPillar;
    if (pillar) {
      pillar.rotation.y += dt * 1.8;
      pillar.scale.set(1 + eased * 0.55, 1, 1 + eased * 0.55);
      (pillar.material as THREE.MeshBasicMaterial).opacity = 0.34 + Math.sin(this.timeS * 8) * 0.08;
    }
    for (let i = 0; i < this.ritualRings.length; i++) {
      const ring = this.ritualRings[i];
      ring.position.y = this.world.stoneAt.y + 0.16 + eased * (3.5 + i * 2.2);
      ring.scale.setScalar(1 + eased * (0.7 + i * 0.22));
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.28 + (1 - progress) * 0.18;
    }
    if (Math.random() < dt * 14) {
      const stone = this.world.stoneAt;
      const a = Math.random() * Math.PI * 2;
      this.poofs.burst(new THREE.Vector3(
        stone.x + Math.cos(a) * (3 + progress * 5),
        stone.y + 2 + progress * 10,
        stone.z + Math.sin(a) * (3 + progress * 5),
      ), 'orange', 4);
    }
    if (now < this.finaleUntilMs) return;
    if (pillar) this.world.scene.remove(pillar);
    this.ritualPillar = null;
    for (const ring of this.ritualRings) this.world.scene.remove(ring);
    this.ritualRings = [];
    this.finaleUntilMs = 0;
    this.hooks.onRitual(false);
    this.phase = 'over';
    this.hooks.onGameOver();
  }

  /** Stage complete — the congregation ascends and the tour moves to the next biome. */
  private departStage(): void {
    if (this.phase !== 'playing') return;
    this.stage++;
    const next = nextBiome(this.world.biome);
    // Reaching a land is retained as tour progress; every fresh run still
    // starts in Valley and must earn this route again.
    if (!this.demoMode) unlockLand(next);

    // The congregation (and any leaders still afield) ascend with the ritual.
    for (const c of this.cultists) {
      this.poofs.burst(new THREE.Vector3(c.pos.x, c.pos.y + 1.2, c.pos.z), 'orange', 6);
      this.world.scene.remove(c.rig.group, c.shadow);
    }
    this.cultists = [];
    for (const g of this.groupies) {
      this.poofs.burst(new THREE.Vector3(g.pos.x, g.pos.y + 1.2, g.pos.z), 'green', 4);
      this.world.scene.remove(g.rig.group, g.shadow);
    }
    this.groupies = [];
    this.hooks.onCarry(null);
    // Clear the field for the flight out.
    for (const c of this.cows) this.world.scene.remove(c.rig, c.shadow);
    this.cows = [];
    for (const d of this.drones) this.world.scene.remove(d.rig.group, d.shadow);
    this.drones = [];
    for (const n of this.normies) this.world.scene.remove(n.rig.group, n.shadow);
    this.normies = [];
    for (const b of this.buildings) this.world.scene.remove(b.group);
    this.buildings = [];
    for (const hd of this.hideys) this.world.scene.remove(hd.rig.group);
    this.hideys = [];
    this.clearPylons();
    for (const b of this.bullets) this.world.scene.remove(b.mesh);
    this.bullets = [];
    for (const t of this.trucks) this.world.scene.remove(t.rig.group);
    this.trucks = [];
    this.clearCombine();
    this.convoyT = Math.max(this.convoyT, 30);
    for (const m of this.meteors) this.world.scene.remove(m.rig.group, m.decal);
    this.meteors = [];
    for (const o of this.ores) this.world.scene.remove(o.rig.group);
    this.ores = [];
    for (const cr of this.crates) this.world.scene.remove(cr.rig.group, cr.shadow);
    this.crates = [];
    for (const ch of this.chunks) this.world.scene.remove(ch.mesh);
    this.chunks = [];
    for (const sp of this.splats) this.world.scene.remove(sp.mesh);
    this.splats = [];
    for (const card of this.placards) this.disposePlacard(card);
    this.placards = [];
    this.closeBlackHole();
    this.councilIds.clear();
    this.councilAwarded = false;
    // The next land gets its own farm, its own loose herd and its own bonus.
    this.donkeyBonusAwarded = false;

    // Departure pay: stage bonus, and the stone restores one core cell.
    const bonus = STAGE_BONUS * (this.stage - 1);
    this.score += bonus;
    this.hooks.onScore(this.score);
    const energyBefore = this.hull;
    if (this.hull < this.hullMax) {
      this.hull = Math.min(this.hullMax, this.hull + 1);
      this.hooks.onHull(this.hull, this.hullMax);
    }
    const restored = Math.round((this.hull - energyBefore) / this.hullMax * 100);

    this.world.applyBiome(next);
    this.world.corn.reset();
    setAmbientBiome(next);
    this.arrivalUntilMs = performance.now() + TOUR_ARRIVAL_MS;
    this.herdCallT = 2.4;
    this.saucerY = this.world.heightAt(this.saucerPos.x, this.saucerPos.z) + SAUCER_ALT;
    this.saucerVel.set(0, 0, 0);
    this.beamActive = false;
    this.saucer.beam.group.visible = false;
    setBeamHum(false);
    stopRotorField();
    setHeliRotor(false);
    setGridHum(0);
    this.hooks.onThreat(null, 0, 0);
    if (this.relay) this.placeRelay(this.relay);
    // The new land gets the same unobstructed arrival card as a fresh run.
    // This only matters until the player has heard the grid tutorial once.
    if (!this.pylonHintDone) this.pylonHintDelayT = 4.4;
    this.hooks.onFlash(0.55);
    sfxDepart();
    this.hooks.onBanner(`DEPARTURE  +${bonus}${restored ? ` · ⚡ +${restored}% ENERGY` : ''}`);
    const nextAnimal = animalForBiome(next);
    // Every land owns its reveal, even after wave five. The old gate left the
    // late tour playing $600B HOLE across Moor, Mars and Moon despite their
    // dedicated beds being shipped and mapped.
    setMusicTrack(BIOME_TRACK[next]);
    const biomeVoice = BIOME_VOICE[next];
    setTimeout(() => {
      if (this.phase === 'playing') playVoiceClip(biomeVoice, 0.95);
    }, 3000);

    // Fresh herd, fresh leaders, fresh locals for the new land.
    for (let i = 0; i < HERD_TARGET; i++) this.spawnCow(true);
    const cast = openingMembersForBiome(next);
    for (let i = 0; i < CULTIST_TARGET; i++) this.spawnCultist(undefined, cast[i]);
    this.spawnBuildings();
    this.spawnHideys();
    this.spawnPylons();
    while (this.drones.length < this.dronesFor(this.wave)) this.spawnDrone();
    while (this.normies.length < this.normiesFor(this.wave)) this.spawnNormie();
    this.hooks.onQuota(this.quotaDone, this.quota, 0, COUNCIL_SIZE);
    // Spawn-time discoveries (notably the Golden Calf) may raise ordinary
    // banners. The arrival title is the final, higher-priority layer.
    this.hooks.onBiome(
      this.world.biomeLabel,
      `THE TOUR CONTINUES · ${nextAnimal.emoji} ${nextAnimal.plural.toUpperCase()}`,
      TOUR_ARRIVAL_MS,
    );

    this.awardBadge('tour');
    if (this.stage >= 3) this.awardBadge('grandtour');
    if (this.stage >= 5) this.awardBadge('worldtour');
    if (this.stage >= 6) this.awardBadge('offworld');
    if (this.stage >= 7) this.awardBadge('nearside');
  }

  /** Push a run total the daily contract may be written against. Cheap enough
   *  to call on every change: the listener drops anything the day's contract
   *  does not ask for. */
  reportObjective(metric: 'donkeys' | 'combines' | 'crop', value: number): void {
    if (this.demoMode) return;
    this.hooks.onObjective(metric, value);
  }

  /** Days of unbroken daily completion carried into this run. Read only by the
   *  draft, which widens by one card from two days, so turning up buys agency
   *  rather than power and the published score stays a record of the run. */
  setDailyStreak(days: number): void {
    this.dailyStreak = Number.isFinite(days) ? Math.max(0, Math.floor(days)) : 0;
  }

  /** Unlock a badge (once ever); announces and queues it for the score event. */
  completeDailyContract(streak: number, reward = 3000): void {
    if (this.demoMode || this.phase !== 'playing') return;
    const bonus = Math.max(0, Math.floor(reward));
    this.score += bonus;
    this.hooks.onScore(this.score);
    this.popScore(this.saucerPos.x, this.saucerY + 1.5, this.saucerPos.z, bonus);
    const days = Math.max(1, Math.floor(streak));
    this.hooks.onBanner(`📡 DAILY SIGNAL LOCKED · ${days} DAY${days === 1 ? '' : 'S'} · +${bonus.toLocaleString('en-GB')}`);
    sfxFeverStart();
    buzz([20, 30, 20, 30, 60]);
    if (days >= 7) this.awardBadge('daily7');
  }

  private awardBadge(id: string): void {
    if (this.demoMode) return; // the cabinet plays for show, not for keeps
    if (!unlock(id)) return;
    const def = awardDef(id);
    if (!def) return;
    this.runAwards.push(id);
    this.hooks.onAward(def);
    sfxComboUp(6);
    buzz([10, 20, 10]);
    playVoiceClip(VOICE.ttsBadge, 0.85);
  }

  private bumpCombo(): void {
    this.comboCount++;
    const mult = comboMultFor(this.comboCount);
    // The window is keyed to the mult you just reached, so every rung you climb
    // buys you less time to find the next target.
    this.comboT = comboWindowFor(mult);
    if (mult > this.comboMult) {
      sfxComboUp(mult);
      // x4 keeps its one-off payload: the ladder continues past it, but this
      // fires on the crossing only, so AFTERBURNER cannot re-arm every rung.
      if (mult === 4) {
        playVoiceClip(VOICE.meme);
        this.awardBadge('chain4');
        if (this.orangeAfterburner) {
          this.powerT.set('overclock', Math.max(this.powerT.get('overclock') ?? 0, 4));
          this.powerHudT = 0;
          this.hooks.onBanner('⚡ ORANGE AFTERBURNER — OVERCLOCK ONLINE');
        }
      }
      if (mult >= 8) this.awardBadge('chain8');
      if (mult >= 5) this.hooks.onBanner(`⛓ x${mult} CHAIN — ${this.comboT.toFixed(1)}s`);
    }
    this.comboMult = mult;
    this.bestCombo = Math.max(this.bestCombo, mult);
    this.comboHudBucket = -1;
    this.syncComboHud(true);
  }

  private progressQuota(): void {
    this.quotaDone++;
    if (this.quotaDone >= this.quota) {
      const clearedBlood = this.bloodMoon;
      const clearBonus = waveClearBonus(this.wave, this.waveDamaged);
      const flawless = clearBonus > 300 * this.wave;
      this.waveDamaged = false;
      this.wave++;
      const wave = this.wave;
      this.quotaDone = 0;
      this.quota = this.quotaFor(wave);
      this.score += clearBonus;
      this.hooks.onScore(this.score);
      this.hooks.onWave(wave);
      // The next armed wave begins clean. The state remains visible during
      // play so the 50% reward is a deliberate mastery target, not a surprise.
      this.hooks.onWaveMastery(wave >= 3);
      sfxWaveClear(flawless);
      if (flawless) buzz([10, 20, 30]);
      if (wave === 5) {
        setMusicTrack('hole');
        void loadFieldDefense();
        void loadLateWorld();
      }
      const want = this.dronesFor(wave);
      const wantNormies = this.normiesFor(wave);
      // Wave three is the player's first fight. Its attackers enter on their
      // lesson beat below, not silently behind the upgrade card.
      if (wave > 3) {
        while (this.drones.length < want) this.spawnDrone();
        while (this.normies.length < wantNormies) this.spawnNormie();
      }

      // The draft opens at the end of this method. These delays therefore do
      // not begin until the chosen upgrade has returned the player to the
      // field. Leave one full toast between distinct hazards so none overwrite
      // one another on a phone.
      this.queueWaveEvent('wave', wave, 0.15, () => this.hooks.onBanner(`WAVE ${wave}`));
      if (wave % 2 === 0) {
        this.queueWaveEvent('voice', wave, 0.35, () => playVoiceClip(VOICE.timeLock, 0.9));
      }
      let eventDelay = 2.55;
      if (threatSurgeForWave(wave)) {
        this.queueThreatSurge(wave, eventDelay);
        eventDelay += 2.4;
      }
      if (wave === 3) {
        this.queueWaveEvent('drone', wave, eventDelay, () => {
          this.spawnDrone();
          this.spawnNormie();
          playVoiceClip(VOICE.fiatNam);
          this.hooks.onBanner('MOVE TO DODGE · BEAM ENEMIES');
        });
        eventDelay += 2.4;
      }
      if (wave % BOSS_EVERY === 0 && !this.boss) {
        this.queueWaveEvent('boss', wave, eventDelay, () => {
          if (!this.boss) this.spawnBoss();
        });
        eventDelay += 2.4;
      }
      // Wave 3 is the player's first armed lesson: drone, shooter and lock
      // warnings already arrive together. Keep weather out of that first
      // contact; the every-third-wave storm cadence begins at wave 6.
      const shouldStorm = wave >= 6 && wave % STORM_EVERY === 0;
      const clearedStorm = !shouldStorm && this.stormActive;
      if (shouldStorm && !this.stormActive) {
        this.queueWaveEvent('storm', wave, eventDelay, () => this.startStorm());
        eventDelay += 2.4;
      } else if (clearedStorm) {
        this.endStorm(false);
      }

      // Stop expired weather immediately, but keep its badge toast behind the
      // draft alongside the incoming hazards. Meteor impacts do not begin
      // until the shower itself has been introduced.
      if (clearedBlood) this.endBloodMoon(false);
      this.meteorWave = false;
      if (wave % METEOR_EVERY === 0 && wave % BOSS_EVERY !== 0) {
        this.queueWaveEvent('meteor', wave, eventDelay, () => {
          this.meteorWave = true;
          this.meteorT = 2.5;
          this.hooks.onBanner('☄️ METEOR SHOWER — MIND THE SKY, MINE THE ORE');
          playVoiceClip(VOICE.ttsMeteor, 0.95);
        });
        eventDelay += 2.4;
      }
      if (wave % BLOOD_EVERY === 0) {
        this.queueWaveEvent('bloodmoon', wave, eventDelay, () => this.startBloodMoon());
        eventDelay += 2.4;
      }

      if (wave === 3 && !this.buildingHintDone) {
        this.buildingHintDone = true;
        this.queueWaveEvent('buildings', wave, eventDelay, () => playVoiceClip(VOICE.ttsBuildings, 0.9));
        eventDelay += 2.4;
      }
      if (clearedStorm) {
        this.queueWaveEvent('badge', wave, eventDelay, () => this.awardBadge('storm'));
        eventDelay += 3.8;
      }
      if (clearedBlood) {
        this.queueWaveEvent('badge', wave, eventDelay, () => this.awardBadge('bloodmoon'));
        eventDelay += 3.8;
      }
      if (wave === 10) {
        this.queueWaveEvent('badge', wave, eventDelay, () => this.awardBadge('wave10'));
        eventDelay += 3.8;
      }
      if (wave === 21) this.queueWaveEvent('badge', wave, eventDelay, () => this.awardBadge('block21'));
      if (wave >= 2) this.offerPerks(clearBonus);
    }
    this.hooks.onQuota(this.quotaDone, this.quota, this.councilIds.size, COUNCIL_SIZE);
  }

  /* ---------------- update ---------------- */

  update(dt: number): void {
    this.timeS += dt;
    const specialEdge = consumeSpecialPress();
    if (this.phase !== 'playing') {
      this.task?.();
      // Nothing is flying, so nothing should still be humming.
      stopRotorField();
      setHeliRotor(false);
      setGridHum(0);
      this.updateAmbient(dt);
      this.pops.update(dt);
      return;
    }
    if (this.hull <= 0) {
      return;
    }
    if (this.paused || this.userPaused) return; // perk draft or player pause
    this.runTimeS += dt;
    if (this.hitstopT > 0) { this.hitstopT -= dt; return; } // impact freeze-frame

    // A completed council is a ceremony, not another eight seconds of combat.
    // Hold the old land and its actors until the tracked ritual speech ends;
    // only formation, particles, fires and the camera continue to breathe.
    if (this.ritualPillar) {
      this.updateCultists(dt);
      this.poofs.update(dt);
      this.pops.update(dt);
      this.updateRitual(dt);
      this.updateAmbient(dt);
      this.updateSaucerVisual(dt);
      this.placeCamera(dt);
      this.hooks.onThreat(null, 0, 0);
      return;
    }

    if (this.arrivalUntilMs > 0) {
      if (performance.now() < this.arrivalUntilMs) {
        // The new land is visible, but not live yet: let the camera and world
        // breathe while hostile timers, movement and collisions remain frozen
        // behind the arrival title.
        this.saucerVel.set(0, 0, 0);
        this.beamActive = false;
        this.saucer.beam.group.visible = false;
        this.poofs.update(dt);
        this.pops.update(dt);
        this.updateAmbient(dt);
        this.updateSaucerVisual(dt);
        this.placeCamera(dt);
        this.hooks.onThreat(null, 0, 0);
        return;
      }
      this.arrivalUntilMs = 0;
      this.invulnT = Math.max(this.invulnT, 1.25);
      this.hooks.onRitual(false);
    }

    this.updateWaveEvents(dt);
    this.updateThreatSurge(dt);
    if (specialEdge && !this.demoMode) this.useSpecial();
    if (this.demoMode) this.driveBot(dt);
    this.measureBeamLoad();
    this.updatePlacards(dt);
    this.updateSaucer(dt);
    this.updateBeam();
    setBeamHum(true, this.beamActive
      ? this.beamLiftProgress
      : -0.12 - Math.hypot(this.saucerVel.x, this.saucerVel.z) / SAUCER_SPEED);
    // Targets below repopulate this for the next rendered frame. Resetting here
    // makes a released/broken beam lose its lock meter immediately.
    this.beamLiftProgress = 0;
    this.updateCrates(dt);
    this.updatePowerups(dt);
    this.updateHole(dt);
    this.updateEmpRing(dt);
    this.updateCowRain(dt);
    this.updateConvoy(dt);
    this.updateMeteors(dt);
    this.updateOres(dt);
    this.task?.(dt);
    this.updateCropArt(dt);
    this.combine?.update(dt);
    this.updateCows(dt);
    this.updateGroupies(dt);
    this.updateCultists(dt);
    this.updateBuildings(dt);
    this.updateHideys(dt);
    this.updatePylons(dt);
    this.updateHostileRespawns(dt);
    this.applyEmpField();
    this.updateRelay(dt);
    this.hostileFireLaneT = Math.max(0, this.hostileFireLaneT - dt);
    this.updateDrones(dt);
    this.updateNormies(dt);
    this.updateBoss(dt);
    this.updateBullets(dt);
    this.updateSplats(dt);
    this.poofs.update(dt);
    this.pops.update(dt);
    this.updateFever(dt);
    this.updateStorm(dt);
    this.updateRitual(dt);
    this.updateHints(dt);
    this.updateCombo(dt);
    this.updateNavigation();
    this.updateAmbient(dt);
    this.placeCamera(dt);
    this.updateThreatIndicator();
  }

  /** After one delivery, point to the next visible leader. Carrying still
   * owns this same compact HUD and replaces the search direction. */
  private updateNavigation(): void {
    const delivery = this.cultists.find((c) => c.mode === 'carried' || c.mode === 'descending');
    if (delivery) {
      const stone = this.world.stoneAt;
      const stoneDx = stone.x - this.saucerPos.x;
      const stoneDz = stone.z - this.saucerPos.z;
      const deg = Math.atan2(stoneDz, stoneDx) * 180 / Math.PI;
      this.hooks.onCarryNavigation(deg, Math.hypot(stoneDx, stoneDz), delivery.mode === 'descending');
      return;
    }
    // The Council outranks the herd; a donkey only takes the channel when
    // nothing is being flown to the stone.
    const hauling = this.cows.find((cow) => cow.ferry);
    const pen = hauling ? this.donkeyPen() : null;
    if (hauling && pen) {
      const penDx = pen.x - this.saucerPos.x;
      const penDz = pen.z - this.saucerPos.z;
      this.hooks.onCarry('DONKEY ABOARD — TO THE PEN');
      this.hooks.onCarryNavigation(Math.atan2(penDz, penDx) * 180 / Math.PI,
        Math.hypot(penDx, penDz), hauling.ferry === 'descending', 'PEN');
      return;
    }
    if (!this.councilIds.size || this.councilAwarded) return;
    let best = Infinity;
    let dx = 0;
    let dz = 0;
    for (const cult of this.cultists) {
      if (cult.mode !== 'ground') continue;
      const x = cult.pos.x - this.saucerPos.x;
      const z = cult.pos.z - this.saucerPos.z;
      const d = x * x + z * z;
      if (d < best) { best = d; dx = x; dz = z; }
    }
    if (best < Infinity) {
      this.hooks.onCarry('NEXT LEADER');
      this.hooks.onCarryNavigation(Math.atan2(dz, dx) * 180 / Math.PI, Math.sqrt(best), false);
    }
  }

  private updateCombo(dt: number): void {
    if (this.comboT > 0) {
      // A Council rescue is one continuous scoring action: once a leader is
      // aboard, preserve (but never refill) the chain while the player flies
      // the required delivery leg. The clock resumes the frame they reach the
      // stone, so holding a leader cannot create extra score by itself.
      if (this.cultists.some((cult) => cult.mode === 'carried' || cult.mode === 'descending')) return;
      this.comboT -= dt;
      if (this.comboT <= 0) {
        this.comboCount = 0;
        this.comboMult = 1;
        this.comboHudBucket = -1;
        this.hooks.onCombo(1, false, 0);
      } else {
        this.syncComboHud();
      }
    }
  }

  /** DOM work is bucketed to at most forty writes per chain window. A 3.6s
   *  high-multiplier window still reads smoothly without touching layout at 60Hz. */
  private syncComboHud(force = false): void {
    if (this.comboMult <= 1 || this.comboT <= 0) {
      if (force) this.hooks.onCombo(1, false, 0);
      return;
    }
    const fraction = THREE.MathUtils.clamp(this.comboT / comboWindowFor(this.comboMult), 0, 1);
    const bucket = Math.ceil(fraction * 40);
    if (!force && bucket === this.comboHudBucket) return;
    this.comboHudBucket = bucket;
    const rung = comboNextRung(this.comboCount);
    this.hooks.onCombo(this.comboMult, true, fraction, rung.need, rung.mult);
  }

  /** Everything the beam is currently holding up, or carrying. Counted once a
   *  frame rather than tracked incrementally: cargo is dropped, yeeted,
   *  captured and despawned from a dozen places, and a stale count would pin
   *  the saucer at cruise for a run. */
  /** Depth-test a held placard and its backing board together. Cheap enough to
   *  call every frame: three.js applies depthTest as renderer state, so this
   *  costs no shader recompile. */
  private setPlacardDepth(sign: THREE.Mesh, tested: boolean): void {
    // Both, every time, and never short-circuited on the face alone: the board
    // ships depthTest off while its backing box defaults to on, so a guard that
    // watched only the face would leave the pair inconsistent until the first
    // toggle. That mismatch is the original bug — the hull correctly hid the
    // board and the slogan carried on drawing straight over it.
    (sign.material as THREE.Material).depthTest = tested;
    const backing = sign.children[0] as THREE.Mesh | undefined;
    if (backing?.isMesh) (backing.material as THREE.Material).depthTest = tested;
  }

  /** The placard is drawn with depthTest off and renderOrder 4 so a foreground
   *  pine cannot anonymise a slogan. That also means it draws straight through
   *  the hull once its owner is aboard, so they let go of it on the way up: the
   *  board falls, lands, and fades. Geometry is shared with the live rig; only
   *  the two materials are cloned, and only so the dropped board can respect
   *  depth against the terrain it is lying on. */
  private dropPlacard(fan: GroupieEnt, groundY: number): void {
    const sign = fan.rig.sign;
    if (!sign.visible) return;
    sign.visible = false;
    const board = new THREE.Group();
    sign.getWorldPosition(board.position);
    board.rotation.set(0, fan.heading, 0);
    const grounded = (material: THREE.Material): THREE.Material => {
      const copy = material.clone();
      copy.depthTest = true;
      copy.depthWrite = true;
      copy.transparent = true;
      return copy;
    };
    const face = new THREE.Mesh(sign.geometry, grounded(sign.material as THREE.Material));
    face.renderOrder = 0;
    board.add(face);
    const backing = sign.children[0] as THREE.Mesh | undefined;
    if (backing?.isMesh) {
      const back = new THREE.Mesh(backing.geometry, grounded(backing.material as THREE.Material));
      back.position.copy(backing.position);
      back.renderOrder = 0;
      board.add(back);
    }
    this.world.scene.add(board);
    this.placards.push({
      group: board,
      vel: new THREE.Vector3((Math.random() - 0.5) * 2.2, 1.1, (Math.random() - 0.5) * 2.2),
      spin: (Math.random() - 0.5) * 5,
      groundY,
      fade: 0,
    });
  }

  private updatePlacards(dt: number): void {
    for (let i = this.placards.length - 1; i >= 0; i--) {
      const card = this.placards[i];
      if (card.group.position.y > card.groundY + 0.08) {
        card.vel.y -= GRAVITY * this.gravity * dt;
        card.group.position.addScaledVector(card.vel, dt);
        card.group.rotation.z += card.spin * dt;
        card.group.rotation.x += card.spin * 0.6 * dt;
        if (card.group.position.y <= card.groundY + 0.08) {
          // Land it face up in the grass rather than standing on its edge.
          card.group.position.y = card.groundY + 0.08;
          card.group.rotation.set(-Math.PI / 2, card.group.rotation.y, 0);
          sfxDrop(this.panOf(card.group.position.x));
        }
      } else {
        card.fade += dt;
      }
      if (card.fade > 2.4) {
        const opacity = Math.max(0, 1 - (card.fade - 2.4) / 1.2);
        for (const child of card.group.children) {
          ((child as THREE.Mesh).material as THREE.Material).opacity = opacity;
        }
        if (opacity <= 0) {
          this.disposePlacard(card);
          this.placards.splice(i, 1);
        }
      }
    }
  }

  private disposePlacard(card: Placard): void {
    for (const child of card.group.children) {
      ((child as THREE.Mesh).material as THREE.Material).dispose();
    }
    this.world.scene.remove(card.group);
  }

  /** How reachable the saucer is from the ground: 0 at cruise, 1 squatting.
   *  Dropping low is cover from the drones and the worst possible place to be
   *  standing next to a normie with a rifle, which is what stops the squat
   *  being free safety. */
  /**
   * The car wash. A happy accident that earns its keep, so it is now a badge.
   *
   * Altitude is `heightAt + alt`, and the lake bed bottoms out at -5.98 while
   * the surface sits at LAKE.level -0.75. A squat is 5.0, so an empty beam
   * held over the deepest water puts the hull at -0.98: under the surface by
   * 0.23 of a unit, and nowhere else on the map. It cannot be done by
   * accident — the empty beam that buys the squat costs 62% of top speed and
   * every point you might have scored — and it cannot be done anywhere but
   * the middle of the lake. That is a secret, and secrets should pay.
   *
   * The dwell is what makes it a wash rather than a clipped frame.
   */
  private updateCarWash(lakeDist: number, dt: number): void {
    const dunked = lakeDist < LAKE.r && this.saucerY < LAKE.level;
    if (!dunked) { this.carWashT = 0; return; }
    if (this.carWashT >= CAR_WASH_DWELL_S) return; // already paid this dip
    this.carWashT += dt;
    if (this.carWashT < CAR_WASH_DWELL_S) return;
    this.hooks.onBanner('🫧 CAR WASH — THE SAUCER COMES OUT GLEAMING');
    this.poofs.burst(
      new THREE.Vector3(this.saucerPos.x, LAKE.level + 0.4, this.saucerPos.z), 'blue', 14);
    sfxGulp(0.5);
    buzz([15, 25, 15]);
    this.awardBadge('carwash');
  }

  private groundExposure(): number {
    return THREE.MathUtils.clamp(
      (CRUISE_ALT - this.altCmd) / (CRUISE_ALT - SQUAT_ALT), 0, 1);
  }

  private measureBeamLoad(): void {
    let load = 0;
    // A donkey stays slung for the whole ferry leg to its pen, so the saucer
    // keeps hauling it until the hooves are back on the farm.
    for (const cow of this.cows) if (cow.lift || cow.ferry) load += LIFT_MASS.animal;
    for (const fan of this.groupies) if (fan.lift) load += LIFT_MASS.groupie;
    for (const normie of this.normies) if (normie.lift) load += LIFT_MASS.normie;
    for (const ore of this.ores) if (ore.lift) load += LIFT_MASS.ore;
    for (const crate of this.crates) if (crate.lift) load += LIFT_MASS.crate;
    for (const drone of this.drones) if (drone.lift) load += LIFT_MASS.drone;
    // A leader stays aboard on the ferry leg to the stone, so the saucer keeps
    // hauling their weight until they are set down.
    for (const cult of this.cultists) {
      if (cult.lift || cult.mode === 'carried' || cult.mode === 'descending') {
        load += LIFT_MASS.cultist;
      }
    }
    this.beamLoad = load;
  }

  private updateSaucer(dt: number): void {
    const beamFactor = Math.min(BEAM_SPEED_FACTOR * this.beamMoveMult, 0.85);
    const speedCap = SAUCER_SPEED * this.speedMult *
      (this.hasPower('overclock') ? 1.55 : 1) * (this.beamActive ? beamFactor : 1);
    const targetVX = input.x * speedCap;
    const targetVZ = input.z * speedCap;
    const k = 1 - Math.exp(-8.5 * dt);
    this.saucerVel.x += (targetVX - this.saucerVel.x) * k;
    this.saucerVel.z += (targetVZ - this.saucerVel.z) * k;
    this.saucerPos.x += this.saucerVel.x * dt;
    this.saucerPos.z += this.saucerVel.z * dt;
    const maxR = PLAY_RADIUS + 4;
    const r = Math.hypot(this.saucerPos.x, this.saucerPos.z);
    if (r > maxR) {
      this.saucerPos.x *= maxR / r;
      this.saucerPos.z *= maxR / r;
    }
    const groundY = this.world.heightAt(this.saucerPos.x, this.saucerPos.z);
    // An open beam with nothing in it is the only way down. It costs 62% of
    // top speed and all of your scoring to hold, so the low ground is bought,
    // not given — and the locals can reach you down there.
    const laden = this.beamLoad > 0;
    const altTarget = this.beamActive && !laden ? SQUAT_ALT : CRUISE_ALT;
    const rate = altTarget > this.altCmd ? ALT_LOAD_RATE : ALT_SQUAT_RATE;
    this.altCmd += (altTarget - this.altCmd) * (1 - Math.exp(-rate * dt));
    const targetY = groundY + this.altCmd;
    // Asymmetric: climbing is urgent, sinking is not. At 15.5 units/s across a
    // butte face the ground can rise faster than a lazy tracker follows it, and
    // the hull ends up inside the cliff. Rising fast costs nothing to feel;
    // falling away slowly over the far edge is what makes cresting a hill read.
    const track = targetY > this.saucerY ? 9 : 3.5;
    this.saucerY += (targetY - this.saucerY) * (1 - Math.exp(-track * dt));
    if (this.invulnT > 0) this.invulnT -= dt;
    this.updateSaucerVisual(dt);
  }

  private updateSaucerVisual(dt: number): void {
    const g = this.saucer.group;
    this.hostileBeamHitT = Math.max(0, this.hostileBeamHitT - dt);
    const hostileLock = this.hostileBeamHitT > 0;
    const flight = Math.hypot(this.saucerVel.x, this.saucerVel.z) / SAUCER_SPEED;
    const bob = Math.sin(this.timeS * 2.2) * 0.15;
    g.position.set(this.saucerPos.x, this.saucerY + bob, this.saucerPos.z);
    g.rotation.z = -this.saucerVel.x * 0.022;
    g.rotation.x = this.saucerVel.z * 0.022;
    // 0.96 rad/s, which is the 0.016-per-step this used to add at the fixed
    // 60Hz sim rate. Stated as a rate so retuning FIXED_STEP cannot quietly
    // respin the craft, and so the constructor's dt=0 call advances nothing.
    g.rotation.y += dt * 0.96;
    // Damage flash.
    const flashing = this.invulnT > 0 && Math.floor(this.invulnT * 10) % 2 === 0;
    g.visible = !flashing;
    // Keep the craft's full circumference readable at every charge. The HUD is
    // the precise gauge; the rim communicates state through hue and chase speed,
    // never by deleting a contiguous section of the saucer silhouette.
    this.energyPulseT = Math.max(0, this.energyPulseT - dt);
    const energyFraction = THREE.MathUtils.clamp(this.hull / this.hullMax, 0, 1);
    const rechargePulse = Math.sin((1 - this.energyPulseT / 0.75) * Math.PI);
    const energyHue = Math.max(0, (energyFraction - 0.25) / 0.75) * 0.33;
    for (let i = 0; i < this.saucer.lightMats.length; i++) {
      const phase = (this.timeS * (3 + flight * 2) + i / this.saucer.lightMats.length * Math.PI * 2) % (Math.PI * 2);
      const on = Math.sin(phase * 5) > 0.2;
      if (this.energyPulseT > 0) this.saucer.lightMats[i].color.set('#7df9ff');
      else this.saucer.lightMats[i].color.setHSL(
        energyHue,
        0.9,
        on ? 0.68 : 0.34,
      );
      this.saucer.lights[i].scale.setScalar(1 + rechargePulse * 0.55 + flight * 0.12);
    }
    if (this.energyPulseT > 0) this.saucer.energyBandMat.color.set('#7df9ff');
    else this.saucer.energyBandMat.color.setHSL(energyHue, 0.95, 0.56);
    const criticalPulse = energyFraction <= 0.25 ? (Math.sin(this.timeS * 8) + 1) * 0.07 : 0;
    this.saucer.energyBandMat.opacity = 0.68 + criticalPulse;
    this.saucer.energyBands.scale.setScalar(1 + rechargePulse * 0.07);
    // Shadow.
    const groundY = this.world.heightAt(this.saucerPos.x, this.saucerPos.z);
    this.saucer.shadow.position.set(this.saucerPos.x, groundY + 0.06, this.saucerPos.z);
    const alt = this.saucerY - groundY;
    const shScale = THREE.MathUtils.clamp(1.6 - alt * 0.06, 0.7, 1.4);
    this.saucer.shadow.scale.setScalar(shScale);
    (this.saucer.shadow.material as THREE.MeshBasicMaterial).opacity = 0.75 - alt * 0.03;

    // HODL shield: ignition pop, gyro rings, lattice spin; blinks out at the end.
    const shieldT = this.powerT.get('shield') ?? 0;
    if (shieldT > 0 && this.lastShieldT <= 0) {
      this.shieldPopT = 0.45;
      sfxShieldUp();
    }
    this.lastShieldT = shieldT;
    this.shield.group.visible = shieldT > 0 && (shieldT > 2 || Math.floor(this.timeS * 8) % 2 === 0);
    if (this.shield.group.visible) {
      this.shieldPopT = Math.max(0, this.shieldPopT - dt);
      this.shieldHitT = Math.max(0, this.shieldHitT - dt);
      const pop = this.shieldPopT > 0
        ? 1 + Math.sin(((0.45 - this.shieldPopT) / 0.45) * Math.PI) * 0.22
        : 1;
      this.shield.group.scale.setScalar((1 + Math.sin(this.timeS * 6) * 0.04) * pop);
      this.shield.wire.rotation.y += dt * 0.7;
      this.shield.wire.rotation.x += dt * 0.31;
      this.shield.rings[0].rotation.z += dt * 1.6;
      this.shield.rings[1].rotation.x += dt * 1.15;
      const hit = Math.min(1, this.shieldHitT / 0.4);
      (this.shield.wire.material as THREE.MeshBasicMaterial).opacity = 0.26 + hit * 0.5 + Math.sin(this.timeS * 9) * 0.05;
      (this.shield.shell.material as THREE.MeshBasicMaterial).opacity = 0.09 + hit * 0.25;
      this.shield.glow.material.opacity = 0.24 + hit * 0.45 + Math.sin(this.timeS * 4.5) * 0.04;
      for (const ring of this.shield.rings) {
        (ring.material as THREE.MeshBasicMaterial).opacity = 0.45 + hit * 0.45;
      }
    }

    // Beam visuals.
    const beam = this.saucer.beam;
    beam.group.visible = this.beamActive;
    if (this.beamActive) {
      const len = this.saucerY - groundY + 0.4;
      const gold = this.feverActive;
      const magnet = this.hasPower('magnet');
      const liftLock = this.beamLiftProgress > 0;
      const liftStage = liftLock ? Math.min(3, Math.floor(this.beamLiftProgress * 4)) : 0;
      if (liftStage !== this.beamLiftStage) {
        if (liftStage > this.beamLiftStage) sfxBeamGate(liftStage);
        this.beamLiftStage = liftStage;
      }
      beam.cone.scale.set(this.beamRadiusMult, len / 10, this.beamRadiusMult);
      beam.cone.position.y = -len / 2 - 0.2;
      beam.decal.position.set(0, -(this.saucerY + bob - groundY) + 0.12, 0);
      const lockPulse = hostileLock
        ? 1.08 + Math.sin(this.timeS * 30) * 0.05
        : liftLock ? 1.03 + Math.sin(this.timeS * 12) * 0.025 : 1;
      beam.decal.scale.set(this.beamRadiusMult * lockPulse, this.beamRadiusMult * lockPulse, 1);
      (beam.cone.material as THREE.MeshBasicMaterial).color.set(
        hostileLock ? '#eaffd8' : gold ? '#ffd24a' : magnet ? '#ff9ade' : '#8bff5e');
      (beam.decal.material as THREE.MeshBasicMaterial).color.set(
        hostileLock ? '#b4ff8c' : gold ? '#ffb84a' : '#ffffff');
      // Once cargo is moving, let its authored colours survive the cone. The
      // green walls and lock gates keep the tractor-beam identity; a neutral
      // key light reveals the actual face/outfit/model instead of bleaching it.
      beam.light.color.set(liftLock ? '#fff2d2' :
        hostileLock ? '#eaffd8' : gold ? '#ffd24a' : magnet ? '#ff9ade' : '#8bff5e');
      beam.light.intensity = hostileLock ? 48 : liftLock ? 34 : 26;
      const pulse = 0.16 + Math.sin(this.timeS * (hostileLock ? 30 : 14)) * 0.04;
      (beam.cone.material as THREE.MeshBasicMaterial).opacity =
        hostileLock ? pulse + 0.16 : liftLock ? 0.11 : gold ? pulse + 0.06 : pulse;
      for (let i = 0; i < beam.rings.length; i++) {
        // Empty cone: four atmospheric rings keep travelling upward. Once
        // cargo catches, they become four fixed gates and illuminate in order,
        // turning the original visual into a readable hold-to-complete meter.
        const filled = liftLock
          ? THREE.MathUtils.clamp(this.beamLiftProgress * beam.rings.length - i, 0, 1)
          : 0;
        const t = liftLock
          ? (i + 1) / (beam.rings.length + 1)
          : ((this.timeS * (hostileLock ? 1.8 : 0.9) + i / beam.rings.length) % 1);
        const y = -len + t * len;
        const radius = (0.5 + (1 - t) * 2.2) * this.beamRadiusMult;
        beam.rings[i].position.y = y;
        beam.rings[i].scale.setScalar(radius * (liftLock ? 1 + (1 - filled) * 0.06 : 1));
        (beam.rings[i].material as THREE.MeshBasicMaterial).color.set(
          liftLock ? (filled > 0.85 ? '#ffffff' : filled > 0 ? '#b4ff8c' : '#5f8d50')
            : hostileLock ? '#ffffff' : gold ? '#ffe28a' : '#b4ff8c');
        (beam.rings[i].material as THREE.MeshBasicMaterial).opacity =
          liftLock ? 0.12 + filled * 0.72 : (hostileLock ? 0.78 : 0.42) * Math.sin(t * Math.PI);
      }
      beam.light.position.y = -len * 0.6;
    } else this.beamLiftStage = 0;

    // Lake reflection: ghost saucer mirrored under the water surface.
    // Calm water (the frozen valley) reflects crisper; waves smear and wobble it.
    const lakeDist = Math.hypot(this.saucerPos.x - LAKE.x, this.saucerPos.z - LAKE.z);
    const overLake = lakeDist < LAKE.r + 3;
    this.reflection.visible = overLake;
    for (const ripple of this.reflectionRipples) ripple.visible = overLake;
    const beamOverWater = overLake && this.beamActive && lakeDist < LAKE.r + 1;
    this.waterSpill.visible = beamOverWater;
    this.updateCarWash(lakeDist, dt);
    if (overLake) {
      const fade = THREE.MathUtils.clamp(1 - (lakeDist - LAKE.r) / 3, 0, 1);
      const wobble = Math.sin(this.timeS * 2.4) * this.world.waterAmp * 5;
      this.reflection.position.set(
        g.position.x + wobble * 0.75,
        LAKE.level - (g.position.y - LAKE.level) * 0.18,
        g.position.z + wobble * 0.48,
      );
      const stretch = Math.sin(this.timeS * 3.1) * this.world.waterAmp * 0.9;
      this.reflection.scale.set(1 + stretch, -0.66, 1 - stretch * 0.55);
      this.reflection.rotation.set(-g.rotation.x, g.rotation.y, -g.rotation.z);
      const ripple = 1 + Math.sin(this.timeS * 5.2) * 0.08;
      const calm = this.world.waterAmp < 0.03 ? 1.5 : 1;
      const op = Math.min(0.52, 0.36 * fade * ripple * calm) * (1 - this.stormLevel * 0.5);
      waterDetailRuntime?.animateReflection(
        this.reflectionRipples, this.reflectionMats, this.world.water, this.world.moonColor,
        this.saucerPos, LAKE.level, this.timeS, fade, this.stormLevel, op, g.rotation.y,
      );
      if (beamOverWater) {
        // The beam pools on the surface where it meets the water.
        this.waterSpill.position.set(this.saucerPos.x, LAKE.level + 0.3, this.saucerPos.z);
        const gold = this.feverActive;
        const magnet = this.hasPower('magnet');
        this.waterSpill.material.color.set(gold ? '#ffd24a' : magnet ? '#ff9ade' : '#8bff5e');
        this.waterSpill.material.opacity = (0.3 + Math.sin(this.timeS * 12) * 0.07) * fade;
        const spillScale = 5 + Math.sin(this.timeS * 8) * 0.5;
        this.waterSpill.scale.set(spillScale, spillScale * 0.55, 1);
      }
    }
  }

  private updateBeam(): void {
    this.beamActive = this.phase === 'playing' && input.beam;
    if (!this.beamActive) this.beamLiftStage = 0;
  }

  private inBeam(x: number, z: number): boolean {
    return this.beamActive &&
      Math.hypot(x - this.saucerPos.x, z - this.saucerPos.z) < BEAM_CAPTURE_RADIUS * this.beamRadiusMult;
  }

  private beamHolding(x: number, z: number): boolean {
    return this.beamActive &&
      Math.hypot(x - this.saucerPos.x, z - this.saucerPos.z) <= BEAM_BREAK_RADIUS * this.beamRadiusMult;
  }

  private setHostileBeamFlash(
    material: THREE.MeshLambertMaterial | THREE.MeshPhongMaterial,
    active: boolean,
  ): void {
    material.emissive.set(active ? '#b4ff8c' : '#000000');
    material.emissiveIntensity = active ? 1.4 + Math.sin(this.timeS * 32) * 0.35 : 0;
  }

  /** Shared lock confirmation for armed targets: beam pulse, sparks, sound and haptics. */
  private hostileBeamContact(x: number, y: number, z: number, acquired: boolean): void {
    this.hostileBeamHitT = 0.14;
    if (!acquired) return;
    this.poofs.burst(this.effectPos.set(x, y, z), 'green', 6);
    sfxBeamHit(this.panOf(x));
    this.addShake(0.08);
    buzz(8);
  }

  /** The models are deliberately toy-sized; a cap tilt or red eye alone is not
   *  a readable dodge cue at the phone camera. Pulse a billboard only during
   *  the existing aim window, preserving timing and difficulty. */
  private showThreatWarning(warning: THREE.Sprite, remaining: number, total: number): void {
    const urgency = THREE.MathUtils.clamp(1 - remaining / total, 0, 1);
    const pulse = Math.sin(this.timeS * (18 + urgency * 12)) * 0.12;
    const scale = 2.15 + urgency * 0.45 + pulse;
    warning.visible = true;
    warning.scale.set(scale, scale, 1);
    (warning.material as THREE.SpriteMaterial).opacity = 0.72 + urgency * 0.28;
  }

  /** Keep hostile fire rhythmic enough to read on a phone. Population, damage,
   * projectile speed and each attacker's cooldown are unchanged; ready enemies
   * simply wait for the current lock and its short muzzle-flash beat to finish. */
  private canOpenHostileFireLane(): boolean {
    if (this.hostileFireLaneT > 0) return false;
    if (this.boss && this.boss.stunT <= 0 && this.boss.telegraph > 0) return false;
    if (this.drones.some((drone) => drone.stunT <= 0 && !drone.lift && drone.telegraph > 0)) return false;
    return !this.normies.some((normie) => normie.stunT <= 0 && !normie.lift && normie.telegraph > 0);
  }

  private closeHostileFireLane(): void {
    this.hostileFireLaneT = HOSTILE_FIRE_LANE_GAP_S;
  }

  /** World warnings and projectile meshes work only while they are visible.
   *  Hand active locks plus genuinely intersecting off-screen shots to one
   *  urgency-ranked HUD compass. */
  private updateThreatIndicator(): void {
    const threats: { pos: THREE.Vector3; urgency: number; kind: 'lock' | 'shot' | 'field' }[] = [];
    const fieldUrgency = this.combine?.guidance(this.fieldThreatPos);
    if (fieldUrgency != null) threats.push({ pos: this.fieldThreatPos, urgency: fieldUrgency, kind: 'field' });
    for (const drone of this.drones) {
      if (drone.telegraph > 0 && drone.stunT <= 0 && !drone.lift) {
        threats.push({
          pos: drone.pos,
          urgency: THREE.MathUtils.clamp(1 - drone.telegraph / DRONE_TELEGRAPH_S, 0, 1),
          kind: 'lock',
        });
      }
    }
    for (const normie of this.normies) {
      if (normie.telegraph > 0 && normie.stunT <= 0 && !normie.lift) {
        threats.push({
          pos: normie.pos,
          urgency: THREE.MathUtils.clamp(1 - normie.telegraph / NORMIE_TELEGRAPH_S, 0, 1),
          kind: 'lock',
        });
      }
    }
    const boss = this.boss;
    if (boss?.telegraph && boss.stunT <= 0) {
      threats.push({
        pos: boss.pos,
        urgency: THREE.MathUtils.clamp(1 - boss.telegraph / (this.wave === BOSS_EVERY ? 1.3 : TAXMAN_TELEGRAPH_S), 0, 1),
        kind: 'lock',
      });
    }

    // Do not turn every stray projectile into HUD noise. Predict closest
    // approach in the saucer's moving frame, and retain only shots which can
    // cross a small safety envelope before their own lifetime runs out.
    for (const bullet of this.bullets) {
      // warpF slows projectiles but not the player, so the prediction must use
      // wall-clock velocity and wall-clock remaining life rather than the
      // bullet's unwarped simulation values.
      const rx = bullet.mesh.position.x - this.saucerPos.x;
      const ry = bullet.mesh.position.y - this.saucerY;
      const rz = bullet.mesh.position.z - this.saucerPos.z;
      const vx = bullet.vel.x * this.warpF - this.saucerVel.x;
      const vy = bullet.vel.y * this.warpF;
      const vz = bullet.vel.z * this.warpF - this.saucerVel.z;
      const speedSq = vx * vx + vy * vy + vz * vz;
      if (speedSq < 0.01) continue;
      const interceptS = -(rx * vx + ry * vy + rz * vz) / speedSq;
      const remainingS = bullet.life / this.warpF;
      if (interceptS <= 0 || interceptS > Math.min(remainingS, 2.8)) continue;
      const miss = Math.hypot(
        rx + vx * interceptS,
        ry + vy * interceptS,
        rz + vz * interceptS,
      );
      if (miss > 3.4) continue;
      const imminence = 1 - interceptS / 2.8;
      const alignment = 1 - miss / 3.4;
      threats.push({
        pos: bullet.mesh.position,
        urgency: THREE.MathUtils.clamp(0.3 + imminence * 0.58 + alignment * 0.12, 0, 1),
        kind: 'shot',
      });
    }
    if (threats.length === 0) {
      this.hooks.onThreat(null, 0, 0);
      return;
    }

    const camera = this.world.camera;
    camera.updateMatrixWorld();
    let best: { pos: THREE.Vector3; urgency: number; kind: 'lock' | 'shot' | 'field' } | null = null;
    let offscreen = 0;
    for (const threat of threats) {
      const local = this.threatView.copy(threat.pos).applyMatrix4(camera.matrixWorldInverse);
      const ndc = this.threatNdc.copy(threat.pos).project(camera);
      const onScreen = local.z < 0 && Math.abs(ndc.x) <= 0.72 && Math.abs(ndc.y) <= 0.55;
      if (onScreen) continue;
      offscreen++;
      if (!best || threat.urgency > best.urgency) best = threat;
    }
    if (!best) {
      this.hooks.onThreat(null, 0, 0);
      return;
    }
    // The play camera never yaws: +x is screen-right and +z is screen-down.
    const deg = Math.atan2(best.pos.z - this.saucerPos.z, best.pos.x - this.saucerPos.x) * 180 / Math.PI;
    this.hooks.onThreat(deg, best.urgency, best.kind === 'field' ? 1 : offscreen, best.kind);
  }

  /** Surface gravity underfoot, Earth = 1. Mars is 0.38, the Moon 0.17: what
   *  the beam drops out there hangs, which is the whole reason to go. */
  private get gravity(): number {
    return MAPS[this.world.biome].gravity ?? 1;
  }

  private beamBroken(x: number, z: number): boolean {
    return !this.beamActive ||
      Math.hypot(x - this.saucerPos.x, z - this.saucerPos.z) > BEAM_BREAK_RADIUS * this.beamRadiusMult;
  }

  /** Returns true when the lifted entity reached the hull. */
  private updateLift(pos: THREE.Vector3, lift: Lifted, dt: number, groundY: number,
    hullGap = 2.5, mass = 1): 'rising' | 'dropped' | 'captured' {
    if (this.beamBroken(pos.x, pos.z)) {
      if (lift.vx === undefined) {
        // Yeet: the cargo keeps the saucer's momentum plus a little fling.
        lift.vx = this.saucerVel.x * 1.2 + (Math.random() - 0.5) * 0.6;
        lift.vz = this.saucerVel.z * 1.2 + (Math.random() - 0.5) * 0.6;
      }
      lift.fallV += GRAVITY * this.gravity * dt;
      lift.y -= lift.fallV * dt;
      pos.x += lift.vx * dt;
      pos.z += lift.vz! * dt;
      if (lift.y <= groundY) return 'dropped';
      return 'rising';
    }
    lift.vx = undefined;
    lift.vz = undefined;
    lift.fallV = 0;
    lift.startY ??= lift.y;
    lift.y += LIFT_SPEED * this.liftMult * dt / mass;
    if (lift.y > lift.peakY) lift.peakY = lift.y;
    // Ease toward beam centre.
    // Cargo must converge faster than the beam-limited saucer can move. The
    // old soft pull lagged a person behind the hull during a moving pickup,
    // carrying their head visibly through the top of the disc.
    const k = 1 - Math.exp(-8 * dt);
    pos.x += (this.saucerPos.x - pos.x) * k;
    pos.z += (this.saucerPos.z - pos.z) * k;
    const captureY = this.saucerY - hullGap;
    lift.captureY = captureY;
    const progress = lift.y >= captureY ? 1 : THREE.MathUtils.clamp(
      (lift.y - lift.startY) / Math.max(0.1, captureY - lift.startY), 0, 1,
    );
    this.beamLiftProgress = Math.max(this.beamLiftProgress, progress);
    if (lift.y >= captureY) return 'captured';
    return 'rising';
  }

  /**
   * The beam has held something living long enough to matter. Fires the hilltop
   * rally and the handoff from the animal to the leader, once per run, whether
   * the proof was a cow in the hold or a donkey slung under the hull.
   */
  private markBeamProven(): void {
    if (this.fanStage < this.stage) {
      this.fanStage = this.stage;
      // The compact FIRST CONTACT ticker no longer takes the instruction
      // channel. A first-time pilot can move straight to the leader, with
      // the rally waiting until that four-second handoff has been read.
      this.queueWaveEvent('badge', this.wave, this.leaderHintArmed ? 5.2 : 3.8, () => this.spawnFans());
    }
    // Hand a first-time pilot promptly from the animal to the nearby
    // orange-glowing leader; the badge remains visible in its own rail.
    if (this.leaderHintArmed && !this.beamProven) this.leaderHintAtMs = this.presentationMs;
    this.beamProven = true;
    this.awardBadge('first-beam');
  }

  /** Donkeys currently standing behind their own rails. */
  private pennedDonkeys(): number {
    return this.cows.reduce((n, c) => n + (c.species === 'donkey' && c.penned ? 1 : 0), 0);
  }

  /** The carry channel is shared with the Council ferry. Only put it away when
   *  there is genuinely nothing under the hull. */
  private clearCarryHint(): void {
    if (this.cultists.some((c) => c.mode === 'carried' || c.mode === 'descending')) return;
    if (this.cows.some((c) => c.ferry)) return;
    this.hooks.onCarry(null);
  }

  /**
   * The Donkey Farm round-up. A caught donkey hangs under the saucer until the
   * pilot brings it over its own rails, then drops into the pen and stays.
   *
   * There is no way to lose one mid-ferry on purpose or by accident: a slung
   * donkey cannot be splatted, drowned or bowled. A herd that could be
   * destroyed halfway through the round-up would leave the bonus unreachable
   * for reasons a pilot never sees coming, which is not the same kind of funny
   * as dropping a cow on a farmer.
   */
  private updateDonkeyFerry(cow: AnimalEnt, dt: number, groundY: number): void {
    const pen = this.donkeyPen();
    if (!pen) {
      // No rails on this land. Nothing to ferry to: set it back down and let
      // it graze like any other beast.
      cow.ferry = null;
      cow.penHome = null;
      cow.pos.y = groundY;
      cow.rig.position.set(cow.pos.x, groundY, cow.pos.z);
      this.clearCarryHint();
      return;
    }
    if (cow.ferry === 'carried') {
      cow.pos.x = this.saucerPos.x;
      cow.pos.z = this.saucerPos.z;
      // Phase and lane both come off the animal's own bob clock, so two
      // donkeys on the same trip hang side by side instead of inside one another.
      const sway = Math.sin(this.timeS * 2.6 + cow.bobT) * 0.26;
      const lane = Math.sin(cow.bobT) * 0.5;
      cow.rig.position.set(this.saucerPos.x + sway, this.saucerY - 2.5, this.saucerPos.z + lane);
      cow.rig.rotation.y += dt * 1.5;
      cow.rig.rotation.z = sway * 0.3;
      cow.shadow.position.set(cow.pos.x, groundY + 0.05, cow.pos.z);
      if (Math.hypot(this.saucerPos.x - pen.x, this.saucerPos.z - pen.z) < DONKEY_DROP_R) {
        cow.ferry = 'descending';
        cow.penHome = this.penSpot(pen);
      }
      return;
    }
    const home = cow.penHome ?? this.penSpot(pen);
    cow.penHome = home;
    const k = 1 - Math.exp(-2.5 * dt);
    cow.pos.x += (home.x - cow.pos.x) * k;
    cow.pos.z += (home.z - cow.pos.z) * k;
    const gy = this.world.heightAt(cow.pos.x, cow.pos.z);
    const y = Math.max(cow.rig.position.y - 3.2 * dt, gy);
    cow.rig.position.set(cow.pos.x, y, cow.pos.z);
    cow.rig.rotation.y += dt * 1.2;
    cow.rig.rotation.z = 0;
    cow.shadow.position.set(cow.pos.x, gy + 0.05, cow.pos.z);
    if (y > gy + 0.01) return;
    this.penDonkey(cow, pen, gy);
  }

  /** Hooves down inside the rails. This is the only thing a donkey pays for. */
  private penDonkey(cow: AnimalEnt, pen: { x: number; z: number; r: number }, gy: number): void {
    cow.ferry = null;
    cow.penHome = null;
    cow.penned = true;
    cow.paddock = pen;
    cow.lift = null;
    cow.pos.y = gy;
    cow.rig.position.set(cow.pos.x, gy, cow.pos.z);
    cow.rig.rotation.set(0, -cow.heading, 0);
    cow.state = 'graze';
    cow.stateT = 2 + Math.random() * 3;
    cow.squashT = 0.4;
    cow.target.copy(this.penSpot(pen));
    if (cow.aura) cow.aura.material.opacity = DONKEY_AURA_PENNED;
    const pts = this.addScore(300);
    this.popScore(cow.pos.x, gy + 2.4, cow.pos.z, pts);
    this.rechargeFromAnimal(cow);
    this.addFever(0.12);
    buzz(12);
    sfxAbductPop();
    playAnimalCall('donkey', 0.98, 0.9, this.panOf(cow.pos.x));
    this.poofs.burst(new THREE.Vector3(cow.pos.x, gy + 0.9, cow.pos.z), 'orange', 6);
    this.bumpCombo();
    this.progressQuota();
    this.clearCarryHint();
    const home = this.pennedDonkeys();
    this.reportObjective('donkeys', home);
    if (home >= DONKEY_HERD && !this.donkeyBonusAwarded) this.awardDonkeyRoundUp();
    else this.hooks.onBanner(`🫏 PENNED ${home}/${DONKEY_HERD}  +${pts}`);
  }

  /** Every donkey on the land back behind its rails. Once per land: a pilot who
   *  beams one out again is welcome to, and gets nothing further for it. */
  private awardDonkeyRoundUp(): void {
    this.donkeyBonusAwarded = true;
    const bonus = 2400 * this.stage;
    this.score += bonus;
    this.hooks.onScore(this.score);
    this.awardBadge('donkeywork');
    this.hooks.onBanner(`🫏 THE HERD IS IN — DONKEY FARM RESTOCKED  +${bonus}`);
    // The farm settles up in the only currency a saucer takes.
    this.restoreEnergy(1.5, 'HERD');
    this.addFever(0.4);
    buzz([20, 30, 20, 40]);
    playVoiceClip(VOICE.whoo);
    // The herd answers, one after another rather than as one wall of noise.
    const run = this.runId;
    for (let n = 0; n < 4; n++) {
      setTimeout(() => {
        if (this.phase !== 'playing' || this.runId !== run) return;
        playAnimalCall('donkey', 0.88 + n * 0.09, 0.6, (n % 2 ? 1 : -1) * 0.35);
      }, 260 + n * 320);
    }
  }

  private updateCows(dt: number): void {
    this.herdCallT -= dt;
    if (this.herdCallT <= 0) {
      let nearest: AnimalEnt | null = null;
      let nearestD = Infinity;
      for (const animal of this.cows) {
        // A slung donkey stands at the saucer's own coordinates, so it would win
        // this search every time and turn the ambient herd into one long bray.
        if (animal.lift || animal.ferry || animal.chuteY !== null) continue;
        const d = Math.hypot(animal.pos.x - this.saucerPos.x, animal.pos.z - this.saucerPos.z);
        if (d < nearestD) { nearest = animal; nearestD = d; }
      }
      if (nearest && nearestD < 62) {
        const level = THREE.MathUtils.clamp(0.82 - nearestD / 150, 0.42, 0.72);
        playAnimalCall(nearest.species, 0.94 + Math.random() * 0.12, level, this.panOf(nearest.pos.x));
        this.herdCallT = 7 + Math.random() * 7;
      } else {
        // Try again soon when the herd is over the horizon; do not make a
        // far-away animal sound as though it is standing beside the saucer.
        this.herdCallT = 2;
      }
    }
    for (let i = this.cows.length - 1; i >= 0; i--) {
      const cow = this.cows[i];
      const groundY = this.world.heightAt(cow.pos.x, cow.pos.z);

      // Cow rain: drifting down under canopy until landing (or the beam).
      if (cow.chuteY !== null) {
        if (this.inBeam(cow.pos.x, cow.pos.z)) {
          if (cow.chute) { cow.rig.remove(cow.chute); cow.chute = null; }
          cow.lift = { y: cow.chuteY, fallV: 0, peakY: cow.chuteY };
          cow.chuteY = null;
          sfxChutePop(this.panOf(cow.pos.x));
          playAnimalCall(cow.species, 1.05, 0.8, this.panOf(cow.pos.x));
          continue;
        }
        cow.chuteY -= CHUTE_FALL_SPEED * Math.sqrt(this.gravity) * dt;
        cow.pos.x += Math.sin(this.timeS * 1.1 + i) * dt * 0.7;
        cow.pos.z += Math.cos(this.timeS * 0.9 + i) * dt * 0.7;
        if (cow.chuteY <= groundY) {
          cow.chuteY = null;
          if (cow.chute) { cow.rig.remove(cow.chute); cow.chute = null; }
          cow.squashT = 0.35;
          cow.state = 'graze';
          cow.stateT = 1.5;
          sfxChutePop(this.panOf(cow.pos.x));
          this.poofs.burst(new THREE.Vector3(cow.pos.x, groundY + 0.6, cow.pos.z), 'green', 5);
        } else {
          cow.rig.position.set(cow.pos.x, cow.chuteY, cow.pos.z);
          cow.rig.rotation.y += dt * 0.5;
          cow.rig.rotation.z = Math.sin(this.timeS * 1.6 + i) * 0.12;
          cow.shadow.position.set(cow.pos.x, groundY + 0.05, cow.pos.z);
          continue;
        }
      }

      // Slung under the hull on the way to the pen, or settling into it. The
      // ferry owns the whole frame for that animal.
      if (cow.ferry) {
        this.updateDonkeyFerry(cow, dt, groundY);
        continue;
      }

      if (cow.lift) {
        const result = this.updateLift(cow.pos, cow.lift, dt, groundY, 2.5, LIFT_MASS.animal);
        if (result === 'captured' && cow.species === 'donkey' && this.donkeyPen()) {
          // A donkey is never taken. It reaches the hull and stays slung under
          // it, exactly as a leader rides to the stone, until it is set down
          // behind its own rails. The hold is not where a donkey is worth
          // anything — the pen is.
          cow.lift = null;
          cow.ferry = 'carried';
          cow.penHome = null;
          cow.penned = false;
          cow.paddock = undefined;
          sfxAbductPop();
          playAnimalCall('donkey', 1.12, 0.9, this.panOf(cow.pos.x));
          this.markBeamProven();
          this.hooks.onCarry('DONKEY ABOARD — TO THE PEN');
          this.poofs.burst(new THREE.Vector3(cow.pos.x, this.saucerY - 2, cow.pos.z), 'orange', 6);
          continue;
        }
        if (result === 'captured') {
          this.world.scene.remove(cow.rig, cow.shadow);
          this.cows.splice(i, 1);
          this.cowsAbducted++;
          this.markBeamProven();
          let base = cow.heifer ? 1000 : 100;
          if (cow.rustled) base = 200;
          if (this.bloodMoon) base *= 2;
          const pts = this.addScore(base);
          this.popScore(cow.pos.x, this.saucerY - 1.6, cow.pos.z, pts);
          this.rechargeFromAnimal(cow);
          this.addFever(cow.heifer ? 0.5 : 0.16);
          buzz(cow.heifer ? [15, 20, 40] : 10);
          if (cow.heifer) {
            this.awardBadge('heifer');
            this.hooks.onBanner(`✨ GOLDEN CALF  +${pts} ✨`);
            playVoiceClip(VOICE.whoo);
          }
          if (cow.rustled) {
            this.awardBadge('rustler');
            this.hooks.onBanner(`CATTLE RUSTLED  +${pts}`);
          }
          this.bumpCombo();
          this.progressQuota();
          sfxAbductPop();
          playAnimalCall(cow.species, cow.heifer ? 1.5 : 1.3, 0.9, this.panOf(cow.pos.x));
          this.poofs.burst(new THREE.Vector3(cow.pos.x, this.saucerY - 1, cow.pos.z), cow.heifer || this.feverActive ? 'orange' : 'green');
          if (this.herdDividend && this.cowsAbducted % 6 === 0 && this.crates.length < 2) this.spawnCrate();
          this.hooks.onCows(this.cowsAbducted);
          this.spawnCow();
          continue;
        }
        if (result === 'dropped') {
          const landY = this.world.heightAt(cow.pos.x, cow.pos.z);
          const yeetSpeed = Math.hypot(cow.lift.vx ?? 0, cow.lift.vz ?? 0);
          if (Math.hypot(cow.pos.x - LAKE.x, cow.pos.z - LAKE.z) < LAKE.r) {
            // Into the drink.
            this.poofs.burst(new THREE.Vector3(cow.pos.x, LAKE.level + 0.4, cow.pos.z), 'blue', 14);
            sfxSplash(this.panOf(cow.pos.x));
            this.hooks.onBanner('SPLOOSH');
            this.world.scene.remove(cow.rig, cow.shadow);
            this.cows.splice(i, 1);
            this.spawnCow();
            continue;
          }
          if (cow.lift.peakY - landY > splatFallFor(cow.lift, landY) ||
            yeetSpeed > YEET_SPLAT_SPEED) {
            this.splatAt(cow.pos.x, landY, cow.pos.z, 1.9);
            this.awardBadge('gravity');
            this.world.scene.remove(cow.rig, cow.shadow);
            this.cows.splice(i, 1);
            this.spawnCow();
            continue;
          }
          cow.lift = null;
          cow.rustled = false;
          cow.state = 'panic';
          cow.stateT = 2;
          cow.squashT = 0.45;
          this.poofs.burst(new THREE.Vector3(cow.pos.x, landY + 0.3, cow.pos.z), 'orange', 4);
          sfxDrop(this.panOf(cow.pos.x));
        } else {
          if (cow.lift.vx !== undefined && this.animalBowling(cow)) {
            this.world.scene.remove(cow.rig, cow.shadow);
            this.cows.splice(i, 1);
            this.spawnCow();
            continue;
          }
          const sway = Math.sin(this.timeS * 3.1 + i) * 0.22;
          const tumble = cow.lift.vx !== undefined ? this.timeS * 9 : 0;
          cow.rig.position.set(cow.pos.x + (tumble ? 0 : sway), cow.lift.y, cow.pos.z);
          cow.rig.rotation.y += dt * 3;
          cow.rig.rotation.z = tumble ? tumble : Math.sin(this.timeS * 6) * 0.25;
          cow.shadow.position.set(cow.pos.x, groundY + 0.05, cow.pos.z);
          continue;
        }
      } else if (this.inBeam(cow.pos.x, cow.pos.z)) {
        cow.lift = { y: groundY, fallV: 0, peakY: groundY };
        // Off the ground is out of the farm for good. A donkey yeeted across
        // the valley has to land where it was thrown; without this the fence
        // would reel it back over the hill the moment it touched down.
        cow.paddock = undefined;
        // Beaming a penned donkey back out puts it on the loose roll again:
        // the aura returns, and the round-up is one animal short until it is
        // ferried home. The bonus itself is a one-off, so this cannot be farmed.
        if (cow.penned) {
          cow.penned = false;
          if (cow.aura) cow.aura.material.opacity = DONKEY_AURA_LOOSE;
        }
        playAnimalCall(cow.species, 0.95 + Math.random() * 0.12, 1, this.panOf(cow.pos.x));
        continue;
      }

      // Ground behaviour.
      cow.stateT -= dt;
      const saucerDist = Math.hypot(cow.pos.x - this.saucerPos.x, cow.pos.z - this.saucerPos.z);
      if (this.beamActive && saucerDist < 7 && cow.state !== 'panic') {
        cow.state = 'panic';
        cow.stateT = 1.6;
        if (Math.random() < 0.3) {
          playAnimalCall(cow.species, 0.85 + Math.random() * 0.1, 0.55, this.panOf(cow.pos.x));
        }
      }
      let speed = 0;
      if (cow.state === 'panic') {
        // Black holes override ordinary fear: heading already points inward.
        const gripped = this.hole &&
          Math.hypot(cow.pos.x - this.hole.pos.x, cow.pos.z - this.hole.pos.z) < HOLE_RADIUS;
        if (!gripped) {
          cow.heading = Math.atan2(cow.pos.z - this.saucerPos.z, cow.pos.x - this.saucerPos.x);
        }
        speed = (cow.heifer ? 5.8 : 4.2) * (this.bloodMoon ? 1.35 : 1); // the heifer is quick
        if (cow.stateT <= 0) { cow.state = 'graze'; cow.stateT = 1 + Math.random() * 2; }
      } else if (cow.state === 'wander') {
        const dx = cow.target.x - cow.pos.x;
        const dz = cow.target.z - cow.pos.z;
        if (Math.hypot(dx, dz) < 1.5) {
          cow.state = 'graze';
          cow.stateT = 2 + Math.random() * 3;
        } else {
          const want = Math.atan2(dz, dx);
          cow.heading += shortestAngle(cow.heading, want) * Math.min(1, dt * 3);
          speed = cow.heifer ? 1.9 : 1.25;
        }
      } else if (cow.stateT <= 0) {
        cow.state = 'wander';
        cow.target = cow.paddock ? this.penSpot(cow.paddock) : this.randomFieldSpot();
      }
      cow.pos.x += Math.cos(cow.heading) * speed * dt;
      cow.pos.z += Math.sin(cow.heading) * speed * dt;
      this.magnetPull(cow.pos, dt);
      keepInField(cow.pos);
      // A fence is a fence, even in a panic: a bolting donkey turns along the
      // rails rather than through them. The beam still lifts it straight out.
      if (cow.paddock && !cow.lift) {
        const dx = cow.pos.x - cow.paddock.x;
        const dz = cow.pos.z - cow.paddock.z;
        const out = Math.hypot(dx, dz);
        // Walking can only ever creep a hair past the rails in one frame, so
        // anything further out was put there by something else — a throw, a
        // black hole, a rustle. That animal has left the farm, and a fence is
        // not a leash: it stops being penned rather than being dragged home.
        if (out > cow.paddock.r + 2) cow.paddock = undefined;
        else if (out > cow.paddock.r) {
          const scale = cow.paddock.r / out;
          cow.pos.x = cow.paddock.x + dx * scale;
          cow.pos.z = cow.paddock.z + dz * scale;
        }
      }
      cow.pos.y = this.world.heightAt(cow.pos.x, cow.pos.z);
      cow.bobT += dt * (speed > 2 ? 14 : speed > 0 ? 8 : 0);
      cow.rig.position.set(cow.pos.x, cow.pos.y + Math.abs(Math.sin(cow.bobT)) * 0.06, cow.pos.z);
      cow.rig.rotation.set(0, -cow.heading, 0);
      if (cow.squashT > 0) {
        cow.squashT -= dt;
        const sq = Math.sin(Math.max(0, 1 - cow.squashT / 0.45) * Math.PI);
        cow.rig.scale.set(1 + sq * 0.22, 1 - sq * 0.28, 1 + sq * 0.22);
      } else {
        cow.rig.scale.setScalar(1);
      }
      const grazing = cow.state === 'graze';
      cow.rig.rotation.z = grazing ? 0.07 : 0;
      cow.shadow.position.set(cow.pos.x, cow.pos.y + 0.05, cow.pos.z);
    }
  }

  /** Placard-waving volunteers run toward the cone and go straight aboard.
   *  They score and advance the ordinary harvest, but never touch the Council. */
  private updateGroupies(dt: number): void {
    const cam = this.world.camera;
    for (let i = this.groupies.length - 1; i >= 0; i--) {
      const fan = this.groupies[i];
      const groundY = this.world.heightAt(fan.pos.x, fan.pos.z);
      // The slogan ignores depth on purpose, so a foreground pine cannot turn a
      // volunteer into an anonymous NPC. That must not extend to the player's
      // own hull: inside the saucer's own reach, hand depth back so the ship
      // occludes the board instead of wearing it as a hat.
      this.setPlacardDepth(fan.rig.sign,
        Math.hypot(fan.pos.x - this.saucerPos.x, fan.pos.z - this.saucerPos.z) < PLACARD_DEPTH_R);
      if (fan.lift) {
        const result = this.updateLift(fan.pos, fan.lift, dt, groundY, 4.2, LIFT_MASS.groupie);
        if (result === 'captured') {
          this.world.scene.remove(fan.rig.group, fan.shadow);
          this.groupies.splice(i, 1);
          const rallyComplete = this.groupies.length === 0;
          this.groupiesAbducted++;
          const pts = this.addScore(200);
          this.popScore(fan.pos.x, this.saucerY - 1.6, fan.pos.z, pts);
          this.addFever(0.14);
          this.bumpCombo();
          this.progressQuota();
          sfxAbductPop();
          // The placard carrier wanted this. Alternate two warm spoken thank-
          // you lines instead of giving every volunteer the generic cheer.
          fan.rig.thank(this.groupiesAbducted, this.panOf(fan.pos.x));
          buzz(10);
          this.poofs.burst(new THREE.Vector3(fan.pos.x, this.saucerY - 1.2, fan.pos.z), 'green', 8);
          if (rallyComplete) {
            // The finite four-person rally is now a side-objective, not just
            // decoration. Bank the defence instead of starting its timer here:
            // an efficient pilot may finish the rally before armed wave three.
            const shield = POWER_DEFS.find((def) => def.kind === 'shield')!;
            this.storeSpecial(shield, false);
            sfxComboUp(6);
            buzz([15, 25, 40]);
            this.hooks.onBanner(`📣 RALLY COMPLETE · +${pts} · 🛡 HODL SHIELD STORED`);
          } else {
            this.hooks.onBanner(`📣 UFO GROUPIE ABOARD · ${fan.rig.phrase} · ${this.groupies.length} LEFT  +${pts}`);
          }
          continue;
        }
        if (result === 'dropped') {
          fan.lift = null;
          fan.squashT = 0.45;
          sfxDrop(this.panOf(fan.pos.x));
          this.poofs.burst(new THREE.Vector3(fan.pos.x, groundY + 0.4, fan.pos.z), 'orange', 4);
        } else {
          const sway = Math.sin(this.timeS * 3.2 + i) * 0.2;
          fan.rig.group.position.set(fan.pos.x + sway, fan.lift.y, fan.pos.z);
          fan.rig.group.rotation.y += dt * 2.8;
          fan.rig.animate(this.timeS + i * 0.425, true, dt, 0, 0);
          fan.rig.sign.rotation.z = sway * 0.18;
          fan.shadow.position.set(fan.pos.x, groundY + 0.05, fan.pos.z);
          continue;
        }
      } else if (this.inBeam(fan.pos.x, fan.pos.z)) {
        fan.lift = { y: groundY, fallV: 0, peakY: groundY };
        this.dropPlacard(fan, groundY);
        continue;
      }

      const distance = Math.hypot(fan.pos.x - this.saucerPos.x, fan.pos.z - this.saucerPos.z);
      let speed = 0;
      if (this.beamActive && distance < 20) {
        fan.heading = Math.atan2(this.saucerPos.z - fan.pos.z, this.saucerPos.x - fan.pos.x);
        speed = 2.35;
      }
      fan.pos.x += Math.cos(fan.heading) * speed * dt;
      fan.pos.z += Math.sin(fan.heading) * speed * dt;
      this.magnetPull(fan.pos, dt);
      keepInField(fan.pos);
      fan.pos.y = this.world.heightAt(fan.pos.x, fan.pos.z);
      fan.rig.group.position.set(fan.pos.x, fan.pos.y + Math.abs(Math.sin(this.timeS * 6 + i)) * 0.04, fan.pos.z);
      // Always face the fixed arcade camera: the joke only lands if the sign reads.
      fan.rig.group.rotation.y = Math.atan2(cam.position.x - fan.pos.x, cam.position.z - fan.pos.z);
      if (fan.squashT > 0) {
        fan.squashT -= dt;
      }
      fan.rig.animate(this.timeS + i * 0.425,
        this.wave === 1 || (this.beamActive && distance < 22), dt, fan.squashT, distance);
      fan.shadow.position.set(fan.pos.x, fan.pos.y + 0.05, fan.pos.z);
    }
  }

  private updateCultists(dt: number): void {
    const cam = this.world.camera;
    const stone = this.world.stoneAt;
    const aligned = this.ritualPillar
      ? this.cultists.filter((candidate) => candidate.mode === 'delivered')
      : null;
    let alignmentDt = dt;
    if (aligned) {
      const now = performance.now();
      alignmentDt = Math.min(0.25, Math.max(dt, (now - this.ritualLastUpdateMs) / 1000));
      this.ritualLastUpdateMs = now;
    }
    for (let i = this.cultists.length - 1; i >= 0; i--) {
      const cult = this.cultists[i];
      const groundY = this.world.heightAt(cult.pos.x, cult.pos.z);

      if (this.ritualPillar && cult.mode !== 'delivered') {
        // Leaders still afield remain exactly where the council caught them;
        // they ascend with everyone else after the spoken rite.
        continue;
      }

      if (cult.mode === 'carried') {
        // Dangling under the hull. Fly them to the sacred stone.
        cult.pos.x = this.saucerPos.x;
        cult.pos.z = this.saucerPos.z;
        const sway = Math.sin(this.timeS * 2.6 + i) * 0.28;
        cult.rig.group.position.set(this.saucerPos.x + sway, this.saucerY - 2.5, this.saucerPos.z);
        cult.rig.group.rotation.y += dt * 1.4;
        cult.rig.group.rotation.z = sway * 0.35;
        cult.shadow.position.set(cult.pos.x, groundY + 0.05, cult.pos.z);
        const stoneDist = Math.hypot(this.saucerPos.x - stone.x, this.saucerPos.z - stone.z);
        if (stoneDist < 7) {
          cult.mode = 'descending';
          const a = Math.random() * Math.PI * 2;
          const r = 3.6 + Math.random() * 2.4;
          cult.homeSpot = new THREE.Vector3(stone.x + Math.cos(a) * r, 0, stone.z + Math.sin(a) * r);
        }
        continue;
      }

      if (cult.mode === 'descending') {
        const home = cult.homeSpot!;
        const k = 1 - Math.exp(-2.5 * dt);
        cult.pos.x += (home.x - cult.pos.x) * k;
        cult.pos.z += (home.z - cult.pos.z) * k;
        const gy = this.world.heightAt(cult.pos.x, cult.pos.z);
        const y = Math.max(cult.rig.group.position.y - 3.2 * dt, gy);
        cult.rig.group.position.set(cult.pos.x, y, cult.pos.z);
        cult.rig.group.rotation.y += dt * 1.1;
        cult.rig.group.rotation.z = 0;
        cult.shadow.position.set(cult.pos.x, gy + 0.05, cult.pos.z);
        if (y <= gy + 0.01) {
          cult.mode = 'delivered';
          cult.pos.y = gy;
          const member = cult.rig.member;
          this.cultistsAbducted++;
          this.councilIds.add(member.id);
          const pts = this.addScore(Math.round(600 * this.cultScoreMult));
          this.popScore(cult.pos.x, gy + 2.6, cult.pos.z, pts);
          this.addFever(0.3);
          buzz(15);
          this.bumpCombo();
          this.progressQuota();
          sfxAbductPop();
          this.cultAbductStreak++;
          // Name them. Every leader has their own line, but it was only ever
          // heard for the ones hidden in a building or a wheelie bin — the
          // wandering ones, which is most of them, went up in silence.
          playVoiceClip(memberVoice(member.id), 0.95);
          if (this.cultAbductStreak % 3 === 0) playVoiceClip(VOICE.meme);
          this.hooks.onToast(member);
          if (this.sacredStatic && this.cultistsAbducted % 3 === 0) {
            this.fireEmp();
            this.hooks.onBanner('💥 SACRED STATIC — THE COUNCIL DISCHARGES');
          }
          this.poofs.burst(new THREE.Vector3(cult.pos.x, gy + 1, cult.pos.z), 'orange');
          // The Congregation: every soul is written to the (local) ledger —
          // unless the cabinet is playing itself.
          if (!this.demoMode) {
            const soul = recordSoul(member.id);
            if (soul.first) {
              setTimeout(() => {
                if (this.phase !== 'playing') return;
                this.hooks.onBanner(`⛪ SOUL GATHERED — ${member.name.toUpperCase()} (${soul.gathered}/${soul.total})`);
              }, 2600);
              // A congregation milestone changes what the beam signs its work
              // with. Announced after the soul line so the two do not collide.
              // The ladder is a deferred chunk; a banner five seconds out can
              // afford to fetch it, and a failed fetch just means no banner.
              void loadGlyphMarks().then((marks) => {
                const line = marks.unlockBanner(soul.gathered);
                if (!line) return;
                setTimeout(() => {
                  if (this.phase === 'playing') this.hooks.onBanner(line);
                }, 5200);
              }).catch(() => undefined);
              if (soul.gathered >= soul.total) this.awardBadge('flock');
            }
          }
          if (!this.cultists.some((c) => c.mode === 'carried' || c.mode === 'descending')) {
            this.hooks.onCarry(null);
          }
          if (!this.councilAwarded && this.councilIds.size >= COUNCIL_SIZE) {
            // Stage complete: the council convenes, the ritual opens the way,
            // and when the pillar fades the saucer departs for the next land.
            this.councilAwarded = true;
            const bonus = 3000 * this.stage;
            this.score += bonus;
            this.hooks.onScore(this.score);
            this.hooks.onBanner(`COUNCIL OF 600 ASSEMBLED  +${bonus}`);
            playVoiceClip(VOICE.whoo);
            this.awardBadge('council');
            this.startRitual();
          }
          if (!this.councilAwarded) {
            if (this.wanderingCultists() < CULTIST_TARGET) this.spawnCultist();
            // Every signature already on the ledger strengthens the call. All
            // remaining leaders turn toward a broad ring around the stone; no
            // one teleports, disappears or bypasses the original lift.
            this.summonCouncilPilgrims();
          }
        }
        continue;
      }

      if (cult.mode === 'delivered') {
        if (aligned) {
          // Twelve leaders resolve from loose milling into two precise ranks,
          // facing inward across the monolith like a ceremonial avenue. The
          // near-corner fires remain just outside the endpoints.
          const slot = aligned.indexOf(cult);
          const firstRow = Math.ceil(aligned.length / 2);
          const row = slot < firstRow ? 0 : 1;
          const within = row === 0 ? slot : slot - firstRow;
          const rowCount = row === 0 ? firstRow : aligned.length - firstRow;
          const targetX = stone.x + (within - (rowCount - 1) / 2) * 1.28;
          const targetZ = stone.z + (row === 0 ? -3.6 : 3.6);
          const k = 1 - Math.exp(-3.2 * alignmentDt);
          cult.pos.x += (targetX - cult.pos.x) * k;
          cult.pos.z += (targetZ - cult.pos.z) * k;
          cult.pos.y = this.world.heightAt(cult.pos.x, cult.pos.z);
          const face = Math.atan2(stone.z - cult.pos.z, stone.x - cult.pos.x);
          cult.heading += shortestAngle(cult.heading, face) * Math.min(1, alignmentDt * 4.5);
          const breathe = Math.abs(Math.sin(this.timeS * 2.4 + slot * 0.35)) * 0.045;
          cult.rig.group.position.set(cult.pos.x, cult.pos.y + breathe, cult.pos.z);
          cult.rig.group.rotation.z = 0;
          if (cult.rig.is3d) cult.rig.group.rotation.y = Math.PI / 2 - cult.heading;
          else cult.rig.group.rotation.y = Math.atan2(cam.position.x - cult.pos.x, cam.position.z - cult.pos.z);
          cult.shadow.position.set(cult.pos.x, cult.pos.y + 0.05, cult.pos.z);
          continue;
        }
        // Congregation: mill about the stone circle — and never leave it.
        cult.stateT -= dt;
        if (cult.stateT <= 0) {
          cult.stateT = 3 + Math.random() * 4;
          const a = Math.random() * Math.PI * 2;
          const r = 2.2 + Math.random() * 3.4;
          cult.target.set(stone.x + Math.cos(a) * r, 0, stone.z + Math.sin(a) * r);
        }
        const dx = cult.target.x - cult.pos.x;
        const dz = cult.target.z - cult.pos.z;
        let speed = 0;
        if (Math.hypot(dx, dz) > 0.6) {
          const want = Math.atan2(dz, dx);
          cult.heading += shortestAngle(cult.heading, want) * Math.min(1, dt * 2.4);
          speed = 0.7;
        }
        cult.pos.x += Math.cos(cult.heading) * speed * dt;
        cult.pos.z += Math.sin(cult.heading) * speed * dt;
        // The stones are the fence: turning arcs must not carry anyone out.
        const rdx = cult.pos.x - stone.x;
        const rdz = cult.pos.z - stone.z;
        const rd = Math.hypot(rdx, rdz);
        if (rd > STONE_RING_R) {
          cult.pos.x = stone.x + (rdx / rd) * STONE_RING_R;
          cult.pos.z = stone.z + (rdz / rd) * STONE_RING_R;
        }
        cult.pos.y = this.world.heightAt(cult.pos.x, cult.pos.z);
        cult.rig.group.position.set(cult.pos.x, cult.pos.y, cult.pos.z);
        cult.rig.group.rotation.z = Math.sin(this.timeS * 4 + i) * 0.03;
        if (cult.rig.is3d) cult.rig.group.rotation.y = Math.PI / 2 - cult.heading;
        else cult.rig.group.rotation.y = Math.atan2(cam.position.x - cult.pos.x, cam.position.z - cult.pos.z);
        cult.shadow.position.set(cult.pos.x, cult.pos.y + 0.05, cult.pos.z);
        continue;
      }

      // mode === 'ground'
      if (cult.lift) {
        const result = this.updateLift(cult.pos, cult.lift, dt, groundY, 2.5, LIFT_MASS.cultist);
        if (result === 'captured') {
          cult.mode = 'carried';
          cult.lift = null;
          sfxAbductPop();
          playVoiceClip(VOICE.notACult);
          this.hooks.onCarry(`${cult.rig.member.name.toUpperCase()} ABOARD — TO THE STONE`);
          this.poofs.burst(new THREE.Vector3(cult.pos.x, this.saucerY - 2, cult.pos.z), 'green', 6);
          continue;
        }
        if (result === 'dropped') {
          // Meme protection: cultists never splat, they dust themselves off.
          cult.lift = null;
          cult.squashT = 0.45;
          this.poofs.burst(new THREE.Vector3(cult.pos.x, groundY + 0.3, cult.pos.z), 'orange', 4);
          sfxDrop(this.panOf(cult.pos.x));
        } else {
          const sway = Math.sin(this.timeS * 2.8 + i) * 0.2;
          cult.rig.group.position.set(cult.pos.x + sway, cult.lift.y, cult.pos.z);
          cult.rig.group.rotation.y += dt * 2.4;
          cult.shadow.position.set(cult.pos.x, groundY + 0.05, cult.pos.z);
          continue;
        }
      } else if (this.inBeam(cult.pos.x, cult.pos.z)) {
        cult.lift = { y: groundY, fallV: 0, peakY: groundY };
        continue;
      }

      // Cultists never flee. If the beam is near, they walk toward it —
      // they think the mothership has finally come.
      cult.stateT -= dt;
      const saucerDist = Math.hypot(cult.pos.x - this.saucerPos.x, cult.pos.z - this.saucerPos.z);
      const pilgrimage = councilPilgrimageFor(this.councilIds.size);
      let speed = pilgrimage?.walkSpeed ?? 0.95;
      // Keep the featured first-flight leader close enough to remain in the
      // portrait camera until the animal-to-leader handoff has done its job.
      // An active beam still makes them volunteer like every other leader.
      if (this.leaderHintArmed && i === 0 && !this.beamActive) {
        if (saucerDist > 4.8) {
          cult.heading = Math.atan2(this.saucerPos.z - cult.pos.z, this.saucerPos.x - cult.pos.x);
          speed = 1.05;
        } else speed = 0;
      } else if (this.beamActive && saucerDist < 9) {
        cult.heading = Math.atan2(this.saucerPos.z - cult.pos.z, this.saucerPos.x - cult.pos.x);
        speed = 1.5;
      } else {
        if (Math.hypot(cult.target.x - cult.pos.x, cult.target.z - cult.pos.z) < 1.6 || cult.stateT <= 0) {
          cult.target.copy(pilgrimage
            ? this.randomCouncilPilgrimageSpot(pilgrimage)
            : this.randomLeaderSpot(4, PLAY_RADIUS - 6));
          cult.stateT = pilgrimage ? 5 + Math.random() * 4 : 6 + Math.random() * 6;
        }
        const dx = cult.target.x - cult.pos.x;
        const dz = cult.target.z - cult.pos.z;
        const want = Math.atan2(dz, dx);
        cult.heading += shortestAngle(cult.heading, want) * Math.min(1, dt * 2.4);
      }
      cult.pos.x += Math.cos(cult.heading) * speed * dt;
      cult.pos.z += Math.sin(cult.heading) * speed * dt;
      this.magnetPull(cult.pos, dt);
      keepInField(cult.pos);
      // The stones are a one-way door. Walking in behind a hovering beam, being
      // dragged in by a magnet, or landing in there off a broken lift all put a
      // leader among the congregation without a delivery ever happening — which
      // reads on screen as one that spawned in the ring, or one that was never
      // dropped off. Only the completed ferry gets through this fence.
      this.clearOfStone(cult.pos, STONE_RING_R + 0.5);
      cult.pos.y = this.world.heightAt(cult.pos.x, cult.pos.z);
      cult.rig.group.position.set(cult.pos.x, cult.pos.y, cult.pos.z);
      cult.rig.group.rotation.z = Math.sin(this.timeS * 5 + i) * 0.045; // waddle
      if (cult.squashT > 0) {
        cult.squashT -= dt;
        const sq = Math.sin(Math.max(0, 1 - cult.squashT / 0.45) * Math.PI);
        cult.rig.group.scale.set(1 + sq * 0.15, 1 - sq * 0.25, 1);
      } else {
        cult.rig.group.scale.setScalar(1);
      }
      if (cult.rig.is3d) {
        // Real model: face the walking direction.
        cult.rig.group.rotation.y = Math.PI / 2 - cult.heading;
      } else {
        // Sprite placeholder: billboard toward camera (yaw only).
        cult.rig.group.rotation.y = Math.atan2(cam.position.x - cult.pos.x, cam.position.z - cult.pos.z);
      }
      cult.shadow.position.set(cult.pos.x, cult.pos.y + 0.05, cult.pos.z);
    }
  }

  private wanderingCultists(): number {
    return this.cultists.filter((c) => c.mode === 'ground').length;
  }

  /** A flung animal meets a normie. Ten-pin rules. */
  private animalBowling(cow: AnimalEnt): boolean {
    const lift = cow.lift!;
    const speed = Math.hypot(lift.vx ?? 0, lift.vz ?? 0);
    if (speed < 3 && lift.fallV < 4) return false;
    for (let n = this.normies.length - 1; n >= 0; n--) {
      const normie = this.normies[n];
      if (normie.lift) continue;
      if (Math.hypot(normie.pos.x - cow.pos.x, normie.pos.z - cow.pos.z) < 1.4 &&
          Math.abs(lift.y - normie.pos.y) < 1.9) {
        this.splatAt(normie.pos.x, normie.pos.y, normie.pos.z, 1.6);
        this.splatAt(cow.pos.x, normie.pos.y, cow.pos.z, 1.9);
        this.world.scene.remove(normie.rig.group, normie.shadow);
        this.normies.splice(n, 1);
        this.scheduleHostileRespawn('shooter');
        const pts = this.addScore(400);
        this.popScore(cow.pos.x, normie.pos.y + 2.2, cow.pos.z, pts);
        this.hooks.onBanner(`${animalName(cow.species).toUpperCase()} BOWLING  +${pts}`);
        this.awardBadge('bowling');
        this.addFever(0.25);
        this.addShake(0.5);
        buzz([15, 20, 40]);
        return true;
      }
    }
    return false;
  }

  private updateBuildings(dt: number): void {
    for (let i = this.buildings.length - 1; i >= 0; i--) {
      const b = this.buildings[i];
      const beamOn = this.beamActive &&
        Math.hypot(b.pos.x - this.saucerPos.x, b.pos.z - this.saucerPos.z) < 3.4;
      if (beamOn) {
        b.hp -= dt;
        const stress = 1 - Math.max(b.hp, 0) / b.maxHp;
        b.group.position.set(
          b.pos.x + (Math.random() - 0.5) * 0.24 * stress,
          b.baseY + stress * 0.55 + Math.sin(this.timeS * 30) * 0.05 * stress,
          b.pos.z + (Math.random() - 0.5) * 0.24 * stress,
        );
        b.group.rotation.z = Math.sin(this.timeS * 26) * 0.07 * stress;
        sfxRumble();
        if (b.hp <= 0) {
          this.world.scene.remove(b.group);
          this.buildings.splice(i, 1);
          this.explodeAt(b.pos, b.group);
          if (b.occupant) {
            this.spawnCultist(b.pos, b.occupant);
            this.hooks.onBanner(`LEADER REVEALED: ${b.occupant.name.toUpperCase()}`);
            playVoiceClip(memberVoice(b.occupant.id), 0.95);
          } else if (Math.random() < 0.5) {
            const animal = this.spawnCow(true);
            this.hooks.onBanner(`THERE WAS A ${animalName(animal.species).toUpperCase()} INSIDE`);
            playAnimalCall(animal.species, 1.05, 0.8);
          } else {
            const pts = this.addScore(150);
            this.popScore(b.pos.x, b.pos.y + 2.4, b.pos.z, pts);
            this.hooks.onBanner(`SALVAGE  +${pts}`);
          }
        }
      } else {
        // Settle back onto the foundations.
        const settle = Math.min(1, dt * 6);
        b.group.position.x += (b.pos.x - b.group.position.x) * settle;
        b.group.position.y += (b.baseY - b.group.position.y) * settle;
        b.group.position.z += (b.pos.z - b.group.position.z) * settle;
        b.group.rotation.z *= Math.max(0, 1 - dt * 8);
      }
    }
    // Debris.
    for (let i = this.chunks.length - 1; i >= 0; i--) {
      const ch = this.chunks[i];
      ch.life -= dt;
      if (ch.life <= 0) {
        this.world.scene.remove(ch.mesh);
        this.chunks.splice(i, 1);
        continue;
      }
      ch.vel.y -= 16 * dt;
      ch.mesh.position.addScaledVector(ch.vel, dt);
      ch.mesh.rotation.x += ch.rotVel.x * dt;
      ch.mesh.rotation.y += ch.rotVel.y * dt;
      ch.mesh.rotation.z += ch.rotVel.z * dt;
      const gy = this.world.heightAt(ch.mesh.position.x, ch.mesh.position.z);
      if (ch.mesh.position.y < gy + 0.1) {
        ch.mesh.position.y = gy + 0.1;
        ch.vel.multiplyScalar(0.4);
        ch.vel.y = Math.abs(ch.vel.y) * 0.3;
      }
    }
  }

  private explodeAt(pos: THREE.Vector3, source: THREE.Group, palette?: string[]): void {
    sfxExplode(this.panOf(pos.x));
    this.addShake(0.7);
    this.hitstopT = Math.max(this.hitstopT, 0.07);
    buzz(35);
    this.poofs.burst(new THREE.Vector3(pos.x, pos.y + 1.2, pos.z), 'orange', 16);
    // Fling plank chunks in the building's palette.
    const mats = (palette ?? ['#6b4a2c', '#54432f', '#7c5a38'])
      .map((color) => new THREE.MeshLambertMaterial({ color, flatShading: true }));
    void source;
    for (let k = 0; k < 12; k++) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.15 + Math.random() * 0.3, 0.08, 0.5 + Math.random() * 0.5),
        mats[k % mats.length],
      );
      mesh.position.set(pos.x, pos.y + 0.8 + Math.random(), pos.z);
      const a = Math.random() * Math.PI * 2;
      this.chunks.push({
        mesh,
        vel: new THREE.Vector3(Math.cos(a) * (2 + Math.random() * 4), 4 + Math.random() * 5, Math.sin(a) * (2 + Math.random() * 4)),
        rotVel: new THREE.Vector3(Math.random() * 8, Math.random() * 8, Math.random() * 8),
        life: 1.4 + Math.random() * 0.6,
      });
      this.world.scene.add(mesh);
    }
  }

  private rotorSources: RotorSource[] = [];

  private updateDrones(dt: number): void {
    // Rebuilt each frame and handed to the mixer at the end: one rotor bed per
    // drone, so a patrol closing on you is something you can hear coming.
    this.rotorSources.length = 0;
    for (let i = this.drones.length - 1; i >= 0; i--) {
      const drone = this.drones[i];
      drone.rig.warning.visible = false;
      const groundY = this.world.heightAt(drone.pos.x, drone.pos.z);
      const beamHeld = drone.lift !== null && this.beamHolding(drone.pos.x, drone.pos.z);
      this.setHostileBeamFlash(drone.rig.bodyMat, beamHeld);
      if (beamHeld) {
        this.hostileBeamContact(drone.pos.x, drone.lift!.y, drone.pos.z, false);
      }

      if (drone.lift) {
        const result = this.updateLift(drone.pos, drone.lift, dt, groundY, 2.5, LIFT_MASS.drone);
        if (result === 'captured') {
          this.world.scene.remove(drone.rig.group, drone.shadow);
          this.drones.splice(i, 1);
          this.dronesAbducted++;
          const pts = this.addScore(250);
          this.popScore(drone.pos.x, this.saucerY - 1.6, drone.pos.z, pts);
          this.addFever(0.22);
          buzz(10);
          this.bumpCombo();
          this.progressQuota();
          this.restoreEnergy(combatChargeFor('drone'), 'DRONE CORE');
          sfxAbductPop();
          playVoiceClip(VOICE.fiatNam, 0.9);
          this.poofs.burst(new THREE.Vector3(drone.pos.x, this.saucerY - 1, drone.pos.z), 'orange');
          this.scheduleHostileRespawn('drone');
          continue;
        }
        if (result === 'dropped') {
          drone.lift = null;
        } else {
          drone.rig.group.position.set(drone.pos.x, drone.lift.y, drone.pos.z);
          drone.rig.group.rotation.y += dt * 5;
          drone.shadow.position.set(drone.pos.x, groundY + 0.05, drone.pos.z);
          continue;
        }
      }

      const stunned = drone.stunT > 0;
      if (stunned) drone.stunT -= dt;
      drone.angle += drone.dir * dt * this.warpF * (0.24 + this.wave * 0.012) * (stunned ? 0.25 : 1);
      drone.pos.x = Math.cos(drone.angle) * drone.radius;
      drone.pos.z = Math.sin(drone.angle) * drone.radius;
      const droneGroundY = this.world.heightAt(drone.pos.x, drone.pos.z);
      const targetY = droneGroundY + (stunned ? 3.4 : 4.6);
      drone.pos.y = drone.pos.y === 0 ? targetY : drone.pos.y + (targetY - drone.pos.y) * Math.min(1, dt * 2);

      if (!drone.lift && this.inBeam(drone.pos.x, drone.pos.z) && Math.abs(drone.pos.y - droneGroundY) < SAUCER_ALT) {
        drone.lift = { y: drone.pos.y, fallV: 0, peakY: drone.pos.y };
        drone.telegraph = 0;
        drone.rig.warning.visible = false;
        this.setHostileBeamFlash(drone.rig.bodyMat, true);
        this.hostileBeamContact(drone.pos.x, drone.pos.y, drone.pos.z, true);
        continue;
      }

      const distToSaucer = Math.hypot(
        drone.pos.x - this.saucerPos.x,
        drone.pos.y - this.saucerY,
        drone.pos.z - this.saucerPos.z,
      );
      if (stunned) {
        // Fried avionics: eye dark, no fire until the smoke clears.
        drone.rig.eyeMat.color.set(Math.floor(this.timeS * 7) % 2 === 0 ? '#331a1a' : '#552020');
      } else if (drone.telegraph > 0) {
        this.showThreatWarning(drone.rig.warning, drone.telegraph, DRONE_TELEGRAPH_S);
        drone.telegraph -= dt * this.warpF;
        const pulse = 1 + Math.sin(this.timeS * 26) * 0.5;
        drone.rig.eyeMat.color.setRGB(1, 0.15 * pulse, 0.15 * pulse);
        if (drone.telegraph <= 0) {
          drone.rig.warning.visible = false;
          const bullet = buildBullet();
          bullet.position.copy(drone.pos);
          const dir = new THREE.Vector3(this.saucerPos.x, this.saucerY, this.saucerPos.z).sub(drone.pos).normalize();
          bullet.quaternion.setFromUnitVectors(BULLET_AXIS, dir);
          this.world.scene.add(bullet);
          this.bullets.push({
            mesh: bullet,
            vel: dir.multiplyScalar((9.5 + this.wave * 0.35) * this.bulletSlow),
            life: 4,
            source: 'drone',
          });
          this.closeHostileFireLane();
          sfxDroneZap(this.panOf(drone.pos.x));
        }
      } else {
        drone.rig.eyeMat.color.set('#ff4040');
        drone.fireT -= dt * this.warpF;
        if (drone.fireT <= 0 && distToSaucer < 30 && this.canOpenHostileFireLane()) {
          drone.telegraph = DRONE_TELEGRAPH_S;
          drone.fireT = droneFireBaseForWave(this.wave) + Math.random() * 1.2;
          sfxDroneLock(this.panOf(drone.pos.x)); // it has picked its shot
        }
      }
      this.rotorSources.push({ dist: distToSaucer, pan: this.panOf(drone.pos.x), stunned });

      const wobble = stunned ? Math.sin(this.timeS * 17 + i) * 0.4 : 0;
      drone.rig.group.position.set(drone.pos.x + wobble * 0.15, drone.pos.y + Math.sin(this.timeS * 3 + i) * 0.2 + wobble * 0.1, drone.pos.z);
      drone.rig.rotor.rotation.z += dt * (stunned ? 2.5 : 9);
      drone.rig.group.rotation.y = -drone.angle;
      drone.shadow.position.set(drone.pos.x, droneGroundY + 0.05, drone.pos.z);
      drone.shadow.scale.setScalar(0.7);
    }
    updateRotorField(this.rotorSources);
  }

  private updateBullets(dt: number): void {
    this.dodgeT -= dt;
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.life -= dt * this.warpF;
      b.mesh.position.addScaledVector(b.vel, dt * this.warpF);
      const hitGround = b.mesh.position.y < this.world.heightAt(b.mesh.position.x, b.mesh.position.z);
      const saucerDist = Math.hypot(
        b.mesh.position.x - this.saucerPos.x,
        b.mesh.position.y - this.saucerY,
        b.mesh.position.z - this.saucerPos.z,
      );
      if (saucerDist < 2 && this.invulnT <= 0) {
        this.world.scene.remove(b.mesh);
        this.bullets.splice(i, 1);
        this.hullHit(b.source);
        continue;
      }
      const closest = b.closest ?? saucerDist;
      if (saucerDist > closest) {
        b.closest = 0; // resolved: a linear shot gets one closest approach
        if (closest >= 2 && closest < 3.2 && this.invulnT <= 0 && this.dodgeT <= 0) {
          this.dodgeT = 0.6;
          const restored = this.restoreEnergy(combatChargeFor('dodge'), null, false);
          this.pops.spawn(
            this.effectPos.set(this.saucerPos.x, this.saucerY + 1.4, this.saucerPos.z),
            restored ? `DODGED · ⚡ +${restored}%` : 'DODGED', restored ? '#7df9ff' : '#ffb45e',
          );
          sfxComboUp(1);
          buzz(6);
        }
      } else b.closest = saucerDist;
      if (b.life <= 0 || hitGround) {
        this.world.scene.remove(b.mesh);
        this.bullets.splice(i, 1);
      }
    }
  }

  private hullHit(source: DamageSource = 'unknown'): void {
    if (this.hasPower('shield')) {
      this.hooks.onBanner('🛡 HODL SHIELD HELD');
      this.shieldHitT = 0.4; // the lattice flares where it ate the hit
      sfxComboUp(2);
      this.addShake(0.3);
      buzz(20);
      this.poofs.burst(new THREE.Vector3(this.saucerPos.x, this.saucerY, this.saucerPos.z), 'blue', 8);
      return;
    }
    if (!this.waveDamaged) this.hooks.onWaveMastery(false);
    this.waveDamaged = true;
    this.lastDamageSource = source;
    this.hull = Math.max(0, this.hull - energyDrainFor(source));
    const signatureSaved = this.hull <= 0 && this.secondSignatureReady;
    if (signatureSaved) {
      this.secondSignatureReady = false;
      this.hull = 1;
      this.powerT.set('shield', 8);
      this.powerHudT = 0;
    }
    this.invulnT = INVULN_S;
    this.hooks.onHull(this.hull, this.hullMax);
    this.hooks.onDamage(this.hull > 0 && this.hull / this.hullMax <= 0.25);
    if (!this.demoMode && !this.energyHintDone && this.hull > 0 && this.hull / this.hullMax <= 0.5) {
      this.energyHintDone = true;
      // Let the impact land first. This active-time cue freezes behind pause,
      // ritual and perk screens just like the other important instructions.
      this.queueWaveEvent('badge', this.wave, 0.55, () => {
        this.hooks.onBanner('⚡ CORE DAMAGED — ABDUCT ANIMALS TO RECHARGE');
      });
    }
    sfxHullHit();
    playVoiceClip(VOICE.wuh, 0.9);
    this.addShake(1.0);
    this.hitstopT = 0.11;
    buzz(90);
    this.poofs.burst(new THREE.Vector3(this.saucerPos.x, this.saucerY, this.saucerPos.z), 'orange', 14);
    if (signatureSaved) {
      this.hooks.onBanner('✍️ SECOND SIGNATURE — RUN RATIFIED · SHIELD ONLINE');
      sfxShieldUp();
      return;
    }
    if (this.hull <= 0) {
      this.beginCrash();
    }
  }

  private splatAt(x: number, groundY: number, z: number, scale: number): void {
    const mesh = makeBloodSplat(scale);
    mesh.position.set(x, groundY + 0.06, z);
    this.world.scene.add(mesh);
    this.splats.push({ mesh, life: 30 });
    this.poofs.burst(new THREE.Vector3(x, groundY + 0.4, z), 'red', 12);
    sfxSplat(this.panOf(x));
    this.addShake(0.3);
    buzz(25);
    this.hooks.onBanner('SPLAT');
  }

  private updateSplats(dt: number): void {
    for (let i = this.splats.length - 1; i >= 0; i--) {
      const sp = this.splats[i];
      sp.life -= dt;
      if (sp.life < 6) {
        (sp.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, sp.life / 6) * 0.85;
      }
      if (sp.life <= 0) {
        this.world.scene.remove(sp.mesh);
        this.splats.splice(i, 1);
      }
    }
  }

  /** Hold the beam steady over corn and the formation grows ever more ornate. */
  private updateCropArt(dt: number): void {
    this.cropPopT += dt;
    if (this.cropPopPending > 0 && this.cropPopT >= 0.32) this.flushCornPopup();
    if (!this.beamActive) { this.hoverT = 0; return; }
    const corn = this.world.corn;
    const before = corn.knocked;
    const cut = corn.flattenAt(this.saucerPos.x, this.saucerPos.z, 2.9, 1);
    if (cut > 0) sfxRustle();

    const moved = Math.hypot(this.saucerPos.x - this.hoverPos.x, this.saucerPos.z - this.hoverPos.z);
    if (moved > 1.8) {
      this.hoverPos.set(this.saucerPos.x, 0, this.saucerPos.z);
      this.hoverT = 0;
      this.awardCorn(corn.knocked - before);
      return;
    }
    this.hoverT += dt;

    const key = `${Math.round(this.hoverPos.x / 6)},${Math.round(this.hoverPos.z / 6)}`;
    const done = this.stampTiers.get(key) ?? 1;
    const cx = this.hoverPos.x;
    const cz = this.hoverPos.z;
    const spin = (cx * 0.7 + cz * 1.3) % (Math.PI * 2);
    if (this.hoverT > 1.3 && done < 2) {
      // Tier 2: crisp outer ring, counter-swirled.
      const n = corn.flattenBand(cx, cz, 4.3, 5.4, -1);
      this.stampTiers.set(key, 2);
      if (n > 14) this.awardCrop(2, 300);
    } else if (this.hoverT > 2.8 && done < 3) {
      // Tier 3: four satellite circles joined by narrow avenues.
      let n = corn.flattenSatellites(cx, cz, 4, 7.6, 1.7, 1, spin);
      n += corn.flattenSpokes(cx, cz, 4, 5.4, 6.2, 0.55, -1, spin);
      this.stampTiers.set(key, 3);
      // Someone is working a formation in earnest, so start pulling the tier-5
      // artwork now rather than at the moment it is needed.
      void loadGlyphMarks().catch(() => undefined);
      if (n > 20) {
        this.awardCrop(3, 800);
        this.challengeFormation(key, cx, cz);
      }
    } else if (this.hoverT > 4.5 && done < 4) {
      // Tier 4: grand outer ring with eight radial spokes.
      let n = corn.flattenBand(cx, cz, 9.4, 10.6, 1);
      n += corn.flattenSpokes(cx, cz, 8, 5.4, 9.4, 0.5, -1, spin + 0.4);
      this.stampTiers.set(key, 4);
      if (n > 30) this.awardCrop(4, 2000);
    } else if (this.hoverT > 6.5 && done < 5) {
      // Tier 5: THE SIGNAL — the cult's own mark, laid into a field left
      // standing. Inverted, because a raised motif on a flattened bed is a
      // bright smudge from 200 units up while flattened strokes hold an edge.
      // The artwork was requested back at tier 3. If it somehow has not landed,
      // leave the tier unspent and try again next frame — a formation that
      // stopped at four because a chunk was slow would be a silent robbery.
      const marks = glyphMarksRuntime;
      if (!marks) {
        void loadGlyphMarks().catch(() => undefined);
      } else {
        const n = marks.stampSignal(corn, cx, cz, spin, gatheredCount());
        if (n > 40) {
          this.awardCrop(5, 6000);
          playVoiceClip(VOICE.whoo);
          this.awardBadge('signal');
        }
      }
      // Spent once the artwork is in hand, whether or not the bed qualified: a
      // bed too shallow tops out at tier 4, which has already paid, and
      // re-testing it every frame would cost a full stalk sweep for an answer
      // that cannot change. Until then the tier stays unspent.
      if (marks) this.stampTiers.set(key, 5);
    }
    this.awardCorn(corn.knocked - before);
  }

  /** Tester rule: one raw point for each stalk, once. Corn deliberately ignores
   *  combo, fever and double-score multipliers so one stalk always means +1. */
  private awardCorn(points: number): void {
    if (points <= 0) return;
    this.score += points;
    this.cropBonus += points;
    this.hooks.onScore(this.score);
    this.reportObjective('crop', this.cropBonus);
    if (this.cropPopPending === 0) this.cropPopT = 0;
    this.cropPopPending += points;
    if (this.cropPopPending >= 10) this.flushCornPopup();
  }

  /** Crop keeps its exact +1-per-stalk score while presenting a readable
   *  bundle instead of a smoke trail of overlapping +1 sprites. */
  private flushCornPopup(): void {
    if (this.cropPopPending <= 0) return;
    const points = this.cropPopPending;
    this.cropPopPending = 0;
    this.cropPopT = 0;
    this.popScore(this.saucerPos.x, this.saucerY - 2.4, this.saucerPos.z, points);
  }

  private awardCrop(tier: number, bonus: number): void {
    this.cropBonus += bonus;
    this.score += bonus;
    this.hooks.onScore(this.score);
    this.reportObjective('crop', this.cropBonus);
    this.popScore(this.hoverPos.x, this.saucerY - 2.4, this.hoverPos.z, bonus);
    this.addFever(tier * 0.08);
    buzz(12);
    const label = tier === 5 ? 'THE SIGNAL RISES' : `CROP FORMATION ${'I'.repeat(Math.min(tier, 3)) + (tier === 4 ? 'V' : '')}`;
    this.hooks.onBanner(`${label}  +${bonus}`);
    sfxComboUp(tier + 1);
    if (tier === 4) playVoiceClip(VOICE.meme, 0.9);
  }

  private updateNormies(dt: number): void {
    for (let i = this.normies.length - 1; i >= 0; i--) {
      const normie = this.normies[i];
      normie.rig.warning.visible = false;
      const groundY = this.world.heightAt(normie.pos.x, normie.pos.z);
      const beamHeld = normie.lift !== null && this.beamHolding(normie.pos.x, normie.pos.z);
      this.setHostileBeamFlash(normie.rig.torsoMat, beamHeld);
      if (beamHeld) {
        this.hostileBeamContact(normie.pos.x, normie.lift!.y + 0.9, normie.pos.z, false);
      }

      if (normie.lift) {
        const result = this.updateLift(normie.pos, normie.lift, dt, groundY, 2.5, LIFT_MASS.normie);
        if (result === 'captured') {
          this.world.scene.remove(normie.rig.group, normie.shadow);
          this.normies.splice(i, 1);
          this.normiesAbducted++;
          if (this.normiesAbducted >= 5) this.awardBadge('normie5');
          const pts = this.addScore(300);
          this.popScore(normie.pos.x, this.saucerY - 1.6, normie.pos.z, pts);
          this.addFever(0.22);
          buzz(10);
          this.bumpCombo();
          this.progressQuota();
          this.restoreEnergy(combatChargeFor('shooter'), 'WEAPON CELL');
          sfxAbductPop();
          this.hooks.onBanner(`NORMIE ABDUCTED  +${pts}`);
          this.poofs.burst(new THREE.Vector3(normie.pos.x, this.saucerY - 1, normie.pos.z), 'orange');
          this.scheduleHostileRespawn('shooter');
          continue;
        }
        if (result === 'dropped') {
          if (normie.lift.peakY - groundY > splatFallFor(normie.lift, groundY)) {
            this.splatAt(normie.pos.x, groundY, normie.pos.z, 1.4);
            this.world.scene.remove(normie.rig.group, normie.shadow);
            this.normies.splice(i, 1);
            this.scheduleHostileRespawn('shooter');
            continue;
          }
          normie.lift = null;
          normie.squashT = 0.45;
          sfxDrop(this.panOf(normie.pos.x));
        } else {
          const sway = Math.sin(this.timeS * 3 + i) * 0.2;
          normie.rig.group.position.set(normie.pos.x + sway, normie.lift.y, normie.pos.z);
          normie.rig.group.rotation.y += dt * 4;
          normie.shadow.position.set(normie.pos.x, groundY + 0.05, normie.pos.z);
          continue;
        }
      } else if (this.inBeam(normie.pos.x, normie.pos.z)) {
        normie.lift = { y: groundY, fallV: 0, peakY: groundY };
        normie.telegraph = 0;
        normie.rig.warning.visible = false;
        this.setHostileBeamFlash(normie.rig.torsoMat, true);
        this.hostileBeamContact(normie.pos.x, groundY + 0.9, normie.pos.z, true);
        continue;
      }

      const dxs = this.saucerPos.x - normie.pos.x;
      const dzs = this.saucerPos.z - normie.pos.z;
      const saucerDist = Math.hypot(dxs, dzs);
      let speed = 0;

      if (normie.stunT > 0) {
        // EMP rang their ears too. Sit down, farmer.
        normie.stunT -= dt;
        normie.rig.gun.rotation.x = -1.15;
        normie.rig.group.rotation.z = Math.sin(this.timeS * 13 + i) * 0.08;
      } else if (this.beamActive && saucerDist < 8) {
        // Normies are not cultists: they run from the beam.
        normie.heading = Math.atan2(-dzs, -dxs);
        speed = 3.4;
        normie.telegraph = 0;
      } else if (normie.telegraph > 0) {
        this.showThreatWarning(normie.rig.warning, normie.telegraph, NORMIE_TELEGRAPH_S);
        normie.telegraph -= dt * this.warpF;
        normie.rig.gun.rotation.x = -0.45; // raised, aiming up
        if (normie.telegraph <= 0) {
          normie.rig.warning.visible = false;
          for (let p = 0; p < 3; p++) {
            const bullet = buildBullet();
            bullet.scale.setScalar(0.7);
            bullet.position.set(normie.pos.x, groundY + 1.4, normie.pos.z);
            const target = new THREE.Vector3(
              this.saucerPos.x + (Math.random() - 0.5) * 3.2,
              this.saucerY + (Math.random() - 0.5) * 1.6,
              this.saucerPos.z + (Math.random() - 0.5) * 3.2,
            );
            const dir = target.sub(bullet.position).normalize();
            bullet.quaternion.setFromUnitVectors(BULLET_AXIS, dir);
            this.world.scene.add(bullet);
            this.bullets.push({
              mesh: bullet,
              vel: dir.multiplyScalar(11.5 * this.bulletSlow),
              life: 3,
              source: 'local',
            });
          }
          this.closeHostileFireLane();
          sfxDroneZap(this.panOf(normie.pos.x));
          // A saucer down in the weeds is an easy second shot.
          normie.cooldown = (3.0 + Math.random() * 1.6) * (1 - 0.4 * this.groundExposure());
        }
      } else {
        normie.rig.gun.rotation.x = -1.15;
        normie.cooldown -= dt * this.warpF;
        // Locals engage further out when the saucer comes down to their level.
        const reach = 20 + 9 * this.groundExposure();
        if (saucerDist < reach && normie.cooldown <= 0 && this.canOpenHostileFireLane()) {
          normie.telegraph = NORMIE_TELEGRAPH_S;
          normie.heading = Math.atan2(dzs, dxs);
        } else if (saucerDist > 14) {
          normie.heading = Math.atan2(dzs, dxs);
          speed = 1.7;
        }
      }

      normie.pos.x += Math.cos(normie.heading) * speed * dt;
      normie.pos.z += Math.sin(normie.heading) * speed * dt;
      this.magnetPull(normie.pos, dt);
      keepInField(normie.pos);
      normie.pos.y = this.world.heightAt(normie.pos.x, normie.pos.z);
      normie.rig.group.position.set(normie.pos.x, normie.pos.y, normie.pos.z);
      normie.rig.group.rotation.y = -normie.heading + Math.PI / 2;
      if (normie.squashT > 0) {
        normie.squashT -= dt;
        const sq = Math.sin(Math.max(0, 1 - normie.squashT / 0.45) * Math.PI);
        normie.rig.group.scale.set(1 + sq * 0.2, 1 - sq * 0.3, 1 + sq * 0.2);
      } else {
        normie.rig.group.scale.setScalar(1);
      }
      normie.shadow.position.set(normie.pos.x, normie.pos.y + 0.05, normie.pos.z);
    }
  }

  /* ---------------- storm waves ---------------- */

  private startStorm(): void {
    // Same reasoning as spawnBoss: a second call used to stack a rain loop and
    // reset the "survived on this much hull" mark mid-storm.
    if (this.stormActive) return;
    this.stormActive = true;
    this.stormHullAtStart = this.hull;
    this.strikeT = 2.5;
    startRainLoop();
    playVoiceClip(VOICE.ttsStorm, 0.95);
    this.hooks.onBanner('⛈ STORM FRONT INBOUND ⛈');
  }

  private endStorm(survived: boolean): void {
    const wasActive = this.stormActive;
    this.stormActive = false;
    stopRainLoop();
    for (const s of this.strikes) this.removeStrike(s);
    this.strikes = [];
    if (wasActive && survived) this.awardBadge('storm');
    void this.stormHullAtStart;
  }

  private updateStorm(dt: number): void {
    for (let i = this.strikes.length - 1; i >= 0; i--) {
      const s = this.strikes[i];
      s.t -= dt;
      if (s.phase === 'telegraph') {
        (s.decal.material as THREE.MeshBasicMaterial).opacity = 0.5 + Math.sin(this.timeS * 18) * 0.3;
        s.decal.scale.setScalar(1 + Math.sin(this.timeS * 12) * 0.12);
        if (s.t <= 0) this.igniteStrike(s);
      } else if (s.t <= 0) {
        this.removeStrike(s);
        this.strikes.splice(i, 1);
      } else if (s.bolt) {
        s.bolt.visible = Math.random() > 0.25;
      }
    }
    if (!this.stormActive) return;
    this.strikeT -= dt;
    if (this.strikeT <= 0) {
      // Early storms rumble more than they strike.
      this.strikeT = (this.wave <= 4 ? 3.2 : 2.2) + Math.random() * 2.3;
      this.spawnStrike();
    }
  }

  private spawnStrike(): void {
    let x: number;
    let z: number;
    if (Math.random() < 0.4) {
      // Hunt the saucer.
      const a = Math.random() * Math.PI * 2;
      const r = 2 + Math.random() * 6;
      x = this.saucerPos.x + Math.cos(a) * r;
      z = this.saucerPos.z + Math.sin(a) * r;
    } else {
      const p = this.randomFieldSpot(6, PLAY_RADIUS - 6);
      x = p.x;
      z = p.z;
    }
    const decal = new THREE.Mesh(
      new THREE.RingGeometry(1.4, 2.0, 24),
      new THREE.MeshBasicMaterial({
        color: '#9fd0ff',
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    decal.rotation.x = -Math.PI / 2;
    decal.position.set(x, this.world.heightAt(x, z) + 0.12, z);
    this.world.scene.add(decal);
    this.strikes.push({ x, z, t: 1.1, phase: 'telegraph', decal, bolt: null });
  }

  private igniteStrike(s: Strike): void {
    s.phase = 'bolt';
    s.t = 0.24;
    const gy = this.world.heightAt(s.x, s.z);
    s.bolt = makeLightningBolt(46);
    s.bolt.position.set(s.x, gy, s.z);
    this.world.scene.add(s.bolt);
    (s.decal.material as THREE.MeshBasicMaterial).opacity = 0.9;
    const distToSaucer = Math.hypot(s.x - this.saucerPos.x, s.z - this.saucerPos.z);
    const near = distToSaucer < 22;
    sfxThunder(near, this.panOf(s.x));
    this.hooks.onFlash(near ? 0.85 : 0.4);
    this.world.sun.intensity = 4.5;
    this.addShake(near ? 0.6 : 0.25);
    this.world.corn.flattenAt(s.x, s.z, 1.9, Math.random() < 0.5 ? 1 : -1);
    if (distToSaucer < 3.2 && this.invulnT <= 0) this.hullHit('lightning');
    for (let i = this.cows.length - 1; i >= 0; i--) {
      const cow = this.cows[i];
      if (cow.lift || cow.ferry) continue;
      if (Math.hypot(cow.pos.x - s.x, cow.pos.z - s.z) < 2.4) {
        this.splatAt(cow.pos.x, cow.pos.y, cow.pos.z, 1.7);
        this.world.scene.remove(cow.rig, cow.shadow);
        this.cows.splice(i, 1);
        this.spawnCow();
      }
    }
    for (let i = this.normies.length - 1; i >= 0; i--) {
      const normie = this.normies[i];
      if (normie.lift) continue;
      if (Math.hypot(normie.pos.x - s.x, normie.pos.z - s.z) < 2.4) {
        this.splatAt(normie.pos.x, normie.pos.y, normie.pos.z, 1.4);
        this.world.scene.remove(normie.rig.group, normie.shadow);
        this.normies.splice(i, 1);
        const pts = this.addScore(200);
        this.popScore(normie.pos.x, normie.pos.y + 2, normie.pos.z, pts);
        this.hooks.onBanner(`ACT OF GOD  +${pts}`);
        this.scheduleHostileRespawn('shooter');
      }
    }
    // Cultists are protected by the prophecy — knocked about, never killed.
    for (const cult of this.cultists) {
      if (cult.mode === 'ground' && Math.hypot(cult.pos.x - s.x, cult.pos.z - s.z) < 2.4) {
        cult.squashT = 0.45;
        this.poofs.burst(new THREE.Vector3(cult.pos.x, cult.pos.y + 1, cult.pos.z), 'orange', 5);
      }
    }
  }

  private removeStrike(s: Strike): void {
    this.world.scene.remove(s.decal);
    if (s.bolt) this.world.scene.remove(s.bolt);
  }

  /* ---------------- hints ---------------- */

  private updateHints(dt: number): void {
    if (this.leaderHintArmed) {
      const leaderInProgress = this.cultists.some((cult) =>
        cult.lift !== null || cult.mode !== 'ground');
      if (this.cultistsAbducted > 0 || leaderInProgress) {
        this.leaderHintArmed = false;
        this.leaderHintAtMs = -1;
      } else if (this.leaderHintAtMs >= 0) {
        // Never stack this handoff over the four-second land arrival card.
        if (this.presentationMs >= this.leaderHintAtMs && this.presentationMs >= 4800) {
          this.leaderHintArmed = false;
          this.hooks.onLeaderHint();
        }
      }
    }
    if (this.cultists.some((c) => c.mode === 'carried')) {
      this.carriedT += dt;
      if (this.carriedT > 10 && this.carryHints < 2) {
        this.carryHints++;
        this.carriedT = 0;
        playVoiceClip(VOICE.ttsStone, 0.95);
        this.hooks.onBanner('➤ DELIVER THEM TO THE SACRED STONE ➤');
      }
    } else {
      this.carriedT = 0;
    }
    if (this.wave === 1 && !this.beamHintDone && this.cowsAbducted === 0 && this.quotaDone === 0 && this.presentationMs > 5000) {
      this.beamHintDone = true;
      playVoiceClip(VOICE.ttsHoldBeam, 0.95);
      const animal = animalForBiome(this.world.biome);
      this.hooks.onBeamHint(animal.singular.toUpperCase());
    }
    // The live-grid warning lives in updatePylons, which is where the distance
    // to the nearest span is already known.
  }

  /* ---------------- THE AGENCY ---------------- */

  private async warmTaxman(): Promise<void> {
    this.taxmanBuilder ??= (await import('./taxman')).buildTaxman;
  }

  private async spawnBoss(): Promise<void> {
    const runId = this.runId;
    const wave = this.wave;
    if (!this.taxmanBuilder) await this.warmTaxman();
    if (runId !== this.runId || wave !== this.wave || this.phase !== 'playing') return;
    // Replace, never stack. `this.boss` is a single slot, so overwriting it
    // orphaned the old rig in the scene — still drawn, no longer simulated, a
    // frozen helicopter for every extra call. The wave scheduler happens to
    // guard with `!this.boss`, but that guard belongs here, not at one call site.
    if (this.boss) this.world.scene.remove(this.boss.rig.group);
    const rig = this.taxmanBuilder!();
    this.world.scene.add(rig.group);
    const angle = Math.random() * Math.PI * 2;
    const maxHp = TAXMAN_BASE_BEAM_S + this.wave * TAXMAN_BEAM_S_PER_WAVE;
    this.boss = {
      rig,
      pos: new THREE.Vector3(
        this.saucerPos.x + Math.cos(angle) * 42,
        this.saucerY + 6,
        this.saucerPos.z + Math.sin(angle) * 42,
      ),
      angle,
      radius: TAXMAN_ORBIT_RADIUS,
      hp: maxHp,
      maxHp,
      fireT: 3,
      telegraph: 0,
      beamedT: 0,
      stunT: 0,
    };
    this.hooks.onBoss(1);
    playVoiceClip(VOICE.ttsTaxman, 0.95);
    // Naming the beam without naming the approach was an instruction that could
    // not be followed: he holds station at `TAXMAN_ORBIT_RADIUS`, and a held
    // beam costs 62% of cruise, so a pilot who reads it literally and flies at
    // him beaming never closes on him at all.
    this.hooks.onBanner('THE AGENCY — CLOSE IN, THEN BEAM HIM DOWN');
  }

  /** This run's Taxman beam reach, BEAM WIDTH included. */
  taxmanBeamReach(): number {
    return taxmanBeamReachFor(this.beamRadiusMult);
  }

  private updateBoss(dt: number): void {
    const boss = this.boss;
    if (!boss) {
      setHeliRotor(false);
      return;
    }
    boss.rig.warning.visible = false;
    boss.beamedT = Math.max(0, boss.beamedT - dt);

    // Orbit the saucer; the beam drags him slow and low.
    boss.angle += dt * this.warpF * 0.55 * (boss.beamedT > 0 ? 0.3 : 1);
    const tx = this.saucerPos.x + Math.cos(boss.angle) * boss.radius;
    const tz = this.saucerPos.z + Math.sin(boss.angle) * boss.radius;
    const k = 1 - Math.exp(-TAXMAN_STATION_KEEPING * dt);
    boss.pos.x += (tx - boss.pos.x) * k;
    boss.pos.z += (tz - boss.pos.z) * k;
    const gy = this.world.heightAt(boss.pos.x, boss.pos.z);
    const hoverY = boss.beamedT > 0 ? 3.4 : 4.9;
    boss.pos.y += (gy + hoverY - boss.pos.y) * (1 - Math.exp(-2.2 * dt));

    // One distance for the whole tick — the beam column, his firing range and
    // the rotor mix all wanted it, and two of them were recomputing it.
    const bossDist = Math.hypot(boss.pos.x - this.saucerPos.x, boss.pos.z - this.saucerPos.z);
    const inColumn = this.beamActive && bossDist < this.taxmanBeamReach();
    const acquired = inColumn && boss.beamedT <= 0;
    this.setHostileBeamFlash(boss.rig.shellMat, inColumn);
    if (inColumn) {
      boss.hp -= dt;
      boss.beamedT = 0.25;
      this.hostileBeamContact(boss.pos.x, boss.pos.y + 0.25, boss.pos.z, acquired);
      sfxRumble();
      if (Math.random() < dt * 10) {
        this.poofs.burst(new THREE.Vector3(boss.pos.x, boss.pos.y + 0.5, boss.pos.z), 'orange', 2);
      }
      this.hooks.onBoss(Math.max(0, boss.hp) / boss.maxHp, true);
      if (boss.hp <= 0) { this.killBoss(); return; }
    }
    // A held beam that is not biting him is still unexplained on screen. Every
    // way of saying so cost 62-87 bytes of cold core against 130 of headroom,
    // so all of them were cut; `v0.20-the-audit.md` has the costings.

    if (boss.stunT > 0) {
      boss.stunT -= dt;
      boss.telegraph = 0;
    } else if (boss.telegraph > 0) {
      const telegraphS = this.wave === BOSS_EVERY ? 1.3 : TAXMAN_TELEGRAPH_S;
      this.showThreatWarning(boss.rig.warning, boss.telegraph, telegraphS);
      boss.telegraph -= dt * this.warpF;
      if (boss.telegraph <= 0) {
        boss.rig.warning.visible = false;
        for (let p = 0; p < (this.wave === BOSS_EVERY ? 2 : 4); p++) {
          const bullet = buildBullet();
          bullet.scale.setScalar(0.8);
          bullet.position.set(boss.pos.x, boss.pos.y, boss.pos.z);
          const target = new THREE.Vector3(
            this.saucerPos.x + (Math.random() - 0.5) * 3.6,
            this.saucerY + (Math.random() - 0.5) * 2,
            this.saucerPos.z + (Math.random() - 0.5) * 3.6,
          );
          const dir = target.sub(bullet.position).normalize();
          bullet.quaternion.setFromUnitVectors(BULLET_AXIS, dir);
          this.world.scene.add(bullet);
          this.bullets.push({
            mesh: bullet,
            vel: dir.multiplyScalar((11.5 + this.wave * 0.2) * this.bulletSlow),
            life: 4,
            source: 'taxman',
          });
        }
        this.closeHostileFireLane();
        sfxDroneZap(this.panOf(boss.pos.x));
        boss.fireT = Math.max(1.8, 3.2 - this.wave * 0.08) + Math.random();
      }
    } else boss.fireT -= dt * this.warpF;
    if (boss.stunT <= 0 && boss.telegraph <= 0 && boss.fireT <= 0 && bossDist < 30 &&
      this.canOpenHostileFireLane()) {
      boss.telegraph = this.wave === BOSS_EVERY ? 1.3 : TAXMAN_TELEGRAPH_S;
      sfxDroneLock(this.panOf(boss.pos.x));
    }

    const g = boss.rig.group;
    const jitter = boss.beamedT > 0 ? 0.22 : 0;
    g.position.set(
      boss.pos.x + (Math.random() - 0.5) * jitter,
      boss.pos.y + Math.sin(this.timeS * 2.4) * 0.25 + (Math.random() - 0.5) * jitter,
      boss.pos.z + (Math.random() - 0.5) * jitter,
    );
    g.rotation.y = -boss.angle - Math.PI / 2; // nose along the orbit tangent
    g.rotation.z = boss.beamedT > 0 ? Math.sin(this.timeS * 24) * 0.14 : Math.sin(this.timeS * 1.7) * 0.05;
    g.rotation.x = 0.08;
    boss.rig.mainRotor.rotation.y += dt * (boss.stunT > 0 ? 5 : boss.beamedT > 0 ? 9 : 26);
    boss.rig.tailRotor.rotation.x += dt * (boss.stunT > 0 ? 8 : 34);
    if (boss.telegraph <= 0) boss.rig.beaconMat.color.set(
      boss.stunT > 0 ? '#2a0808' : Math.sin(this.timeS * 8) > 0 ? '#ff3030' : '#5a1010');
    // Blade slap, loud enough to feel him behind you and no louder.
    setHeliRotor(boss.stunT <= 0, bossDist);
  }

  private killBoss(): void {
    const boss = this.boss!;
    this.boss = null;
    setHeliRotor(false);
    this.world.scene.remove(boss.rig.group);
    this.hooks.onBoss(null);
    const groundPos = new THREE.Vector3(boss.pos.x, this.world.heightAt(boss.pos.x, boss.pos.z), boss.pos.z);
    this.explodeAt(groundPos, boss.rig.group, ['#14161c', '#2a2e38', '#3a2e20']);
    sfxBossDown();
    const pts = this.addScore(5000);
    this.popScore(boss.pos.x, boss.pos.y + 1, boss.pos.z, pts);
    this.hooks.onBanner(`THE AGENCY AUDITED  +${pts}`);
    this.addShake(1.2);
    this.hitstopT = 0.14;
    buzz([30, 40, 90]);
    this.addFever(1);
    playVoiceClip(VOICE.whoo);
    this.awardBadge('taxman');
    this.bumpCombo();
    this.progressQuota();
  }

  /* ---------------- ambience & camera ---------------- */

  private fogStorm = new THREE.Color('#0c0a1e');
  private fogBlood = new THREE.Color('#2a0c14');
  private moonBlood = new THREE.Color('#ff4636');
  private haloBlood = new THREE.Color('#ff5a4a');
  private hemiBlood = new THREE.Color('#9c4a52');

  private updateAmbient(dt: number): void {
    // Storm ambience: darken the valley, drive the rain, decay flash spikes.
    // Base colours come from the world's current biome palette.
    const s = this.stormLevel += ((this.stormActive ? 1 : 0) - this.stormLevel) * Math.min(1, dt * 0.7);
    // Phone panels crush the shadows; lift the whole night a step there.
    const lift = this.mobileView ? 1.22 : 1;
    this.world.hemi.intensity = (2.15 - s * 0.85) * lift;
    const sunBase = (1.55 - s * 0.6) * lift;
    this.world.sun.intensity = Math.max(sunBase, this.world.sun.intensity - dt * 10);
    (this.world.scene.fog as THREE.Fog).color.copy(this.world.fogColor).lerp(this.fogStorm, s);

    // Blood moon: the whole valley shifts to rust; the moon leads the way.
    const b = this.bloodLevel += ((this.bloodMoon ? 1 : 0) - this.bloodLevel) * Math.min(1, dt * 0.5);
    if (b > 0.005) {
      (this.world.scene.fog as THREE.Fog).color.lerp(this.fogBlood, b * 0.65);
      this.world.moonMat.color.copy(this.world.moonColor).lerp(this.moonBlood, b);
      this.world.moonHaloMat.color.copy(this.world.haloColor).lerp(this.haloBlood, b);
      this.world.hemi.color.copy(this.world.hemiSkyColor).lerp(this.hemiBlood, b * 0.8);
      if (this.bloodMoon && this.phase === 'playing') {
        this.howlT -= dt;
        if (this.howlT <= 0) {
          this.howlT = 12 + Math.random() * 10;
          sfxHowl(Math.random() * 1.6 - 0.8);
        }
      }
    } else {
      this.world.moonMat.color.copy(this.world.moonColor);
      this.world.moonHaloMat.color.copy(this.world.haloColor);
      this.world.hemi.color.copy(this.world.hemiSkyColor);
    }

    // The frogs need to know whether anyone is hovering over their lake.
    setNearLake(Math.hypot(this.saucerPos.x - LAKE.x, this.saucerPos.z - LAKE.z) < LAKE.r + 6);
    this.world.rain.visible = s > 0.02;
    if (this.world.rain.visible) {
      const snow = this.world.precip === 'snow';
      const rpos = this.world.rain.geometry.attributes.position as THREE.BufferAttribute;
      const rarr = rpos.array as Float32Array;
      for (let i = 0; i < rarr.length; i += 3) {
        rarr[i + 1] -= dt * (snow ? 8 : 30);
        rarr[i] += snow ? Math.sin(this.timeS * 1.4 + i) * dt * 2.2 : dt * 4;
        if (rarr[i + 1] < 0) rarr[i + 1] += 26;
        if (rarr[i] > 36) rarr[i] -= 72;
        if (rarr[i] < -36) rarr[i] += 72;
      }
      rpos.needsUpdate = true;
      this.world.rain.position.set(this.saucerPos.x, this.world.heightAt(this.saucerPos.x, this.saucerPos.z), this.saucerPos.z);
      const weatherMat = this.world.rain.material as THREE.ShaderMaterial;
      weatherMat.uniforms.uOpacity.value = (snow ? 0.72 : 0.46) * s;
    }

    // Living water: two wave octaves, rougher under storm — near-still under ice.
    const wpos = this.world.water.geometry.attributes.position as THREE.BufferAttribute;
    const warr = wpos.array as Float32Array;
    const base = this.world.waterBase;
    const amp = this.world.waterAmp + s * (this.world.precip === 'snow' ? 0.02 : 0.09);
    for (let i = 0; i < warr.length; i += 3) {
      warr[i + 2] = Math.sin(base[i] * 0.55 + this.timeS * 1.7) * amp +
        Math.cos(base[i + 1] * 0.48 + this.timeS * 1.3) * amp +
        Math.sin(base[i] * 1.7 + base[i + 1] * 1.2 + this.timeS * 2.6) * amp * 0.35;
    }
    wpos.needsUpdate = true;
    this.world.water.geometry.computeVertexNormals();
    waterDetailRuntime?.animateWater(
      this.world.water, this.timeS, this.world.waterAmp, s, this.world.precip === 'snow',
    );

    // Moon glitter flickers along the lake's light path; storms drown it.
    const glintDim = (1 - s * 0.7) * (1 - this.bloodLevel * 0.35);
    for (let i = 0; i < this.world.moonGlints.length; i++) {
      const glint = this.world.moonGlints[i];
      const u = glint.userData as { base: number };
      const tw = 0.3 + 0.7 * Math.abs(Math.sin(this.timeS * (1.1 + (i % 4) * 0.4) + i * 2.17));
      (glint.material as THREE.MeshBasicMaterial).opacity = u.base * tw * glintDim;
    }

    // The sacred circle breathes. Once a leader is aboard it answers the
    // pickup: the existing ring, embers and fires become the destination
    // beacon, growing stronger on approach without adding another draw call.
    const deliveryActive = this.cultists.some((c) => c.mode === 'carried' || c.mode === 'descending');
    const stoneDistance = deliveryActive
      ? Math.hypot(this.saucerPos.x - this.world.stoneAt.x, this.saucerPos.z - this.world.stoneAt.z)
      : Infinity;
    const approach = deliveryActive ? 1 - THREE.MathUtils.clamp((stoneDistance - 7) / 48, 0, 1) : 0;
    const guideGlow = deliveryActive ? 0.16 + approach * 0.18 : 0;
    const ringGlow = this.ritualT > 0 ? 0.3 : guideGlow;
    (this.world.stoneRing.material as THREE.MeshBasicMaterial).opacity =
      0.2 + Math.sin(this.timeS * 1.6) * 0.06 + ringGlow;
    this.world.stoneRing.rotation.z += dt * 0.06;
    const guidePulse = deliveryActive ? Math.sin(this.timeS * 4.2) * guideGlow * 0.08 : 0;
    this.world.stoneRing.scale.setScalar(1 + guideGlow * 0.12 + guidePulse);
    for (const ember of this.world.embers) {
      const u = ember.userData as { r: number; a: number; speed: number; phase: number; size: number };
      const t = (this.timeS * u.speed + u.phase) % 1;
      const a = u.a + this.timeS * 0.14;
      ember.position.set(Math.cos(a) * u.r, 0.4 + t * 4.8, Math.sin(a) * u.r);
      ember.material.opacity = (0.5 + ringGlow) * Math.sin(t * Math.PI);
      const emberScale = u.size * (this.ritualPillar ? 1.55 : 1 + guideGlow * 0.9);
      ember.scale.set(emberScale, emberScale, 1);
    }

    // Ground mist drifts; bats circle the moon side.
    for (let i = 0; i < this.world.mist.length; i++) {
      const mist = this.world.mist[i];
      mist.position.x += Math.sin(this.timeS * 0.05 + i * 2.1) * dt * 1.3;
      mist.position.z += Math.cos(this.timeS * 0.045 + i * 1.7) * dt * 1.1;
      mist.material.opacity = 0.38 + Math.sin(this.timeS * 0.12 + i) * 0.16;
    }
    for (const bat of this.world.bats) {
      const u = bat.userData as { radius: number; height: number; speed: number; phase: number; flap: number };
      const a = this.timeS * u.speed + u.phase;
      bat.position.set(
        Math.cos(a) * u.radius,
        u.height + Math.sin(this.timeS * 0.7 + u.phase) * 2.5,
        Math.sin(a) * u.radius,
      );
      bat.scale.y = 1.6 * (0.55 + Math.abs(Math.sin(this.timeS * u.flap + u.phase)) * 0.8);
      bat.rotation.y = -a;
    }
    // Bonfire flicker.
    for (let i = 0; i < this.world.flames.length; i++) {
      const flame = this.world.flames[i];
      const u = flame.userData as { baseX: number; baseY: number; phase: number; layer: number };
      const flick = 1 + Math.sin(this.timeS * 9 + u.phase) * 0.12 + Math.sin(this.timeS * 23 + i) * 0.06;
      const ritualBoost = this.ritualPillar ? (u.layer ? 1.2 : 1.38) : 1 + guideGlow * 0.28;
      flame.scale.set(u.baseX * flick * ritualBoost, u.baseY * (2 - flick) * ritualBoost, 1);
      flame.material.opacity = (u.layer ? 0.92 : 0.82) + (this.ritualPillar ? 0.08 : 0);
    }
    this.world.fireLight.intensity = 13 + Math.sin(this.timeS * 11) * 2.5 + Math.sin(this.timeS * 29) * 1.2 +
      (this.ritualPillar ? 24 : guideGlow * 16); // cargo calls the fires up; the council makes them roar
  }

  private camPos = new THREE.Vector3(0, 20, 26);
  private camTarget = new THREE.Vector3();
  private camLook = new THREE.Vector3();

  private placeCamera(dt: number): void {
    if (this.phase === 'playing') {
      // Mobile pulls the camera back — portrait most of all, where the
      // narrow frame otherwise hides everything worth dodging.
      // A real hold earns a restrained hero shot. The existing camera lerp
      // supplies the ease in/out; phones retain more peripheral threat space,
      // and reduced-motion players keep the completely fixed flight framing.
      let zoom = this.mobileView ? (this.world.camera.aspect < 1 ? 1.34 : 1.16) : 1;
      if (!this.reducedMotion && this.beamLiftProgress) zoom *= this.mobileView ? 0.9 : 0.86;
      if (this.crash) {
        // Death belongs to the world, not a distant HUD transition. Move in
        // close enough to read the banked, half-buried disc and its earth fan.
        const crashZoom = this.world.camera.aspect < 1 ? 1.12 : 1;
        this.camTarget.set(
          this.saucerPos.x,
          this.saucerY + 8.2 * crashZoom,
          this.saucerPos.z + 10.2 * crashZoom,
        );
        this.camLook.set(this.saucerPos.x, this.saucerY - 0.35, this.saucerPos.z - 0.7);
      } else if (this.ritualPillar) {
        // The council is the subject now. Ease away from the saucer and frame
        // the two ranks, both fires and the monolith as one readable tableau.
        const stone = this.world.stoneAt;
        const ritualZoom = this.world.camera.aspect < 1 ? 1.18 : 1;
        const lift = this.finaleUntilMs > 0 ? Math.max(0, this.saucerY - this.finaleSaucerY) : 0;
        this.camTarget.set(stone.x, stone.y + (15.5 + lift * 0.38) * ritualZoom, stone.z + 17.5 * ritualZoom);
        this.camLook.set(stone.x, stone.y + 1.45 + lift * 0.42, stone.z);
      } else {
        this.camTarget.set(
          this.saucerPos.x,
          this.saucerY + 13.5 * zoom,
          this.saucerPos.z + 16.5 * zoom,
        );
        this.camLook.set(this.saucerPos.x, this.saucerY - 2.5, this.saucerPos.z - 2);
      }
    } else {
      // Attract mode: drift slowly around the valley facing the moon side.
      const a = this.timeS * 0.05 + Math.PI * 0.3;
      this.camTarget.set(Math.cos(a) * 30, 12, Math.sin(a) * 30);
      this.camLook.set(0, 4, 0);
    }
    // The ordinary camera glides; a crash camera must catch a moving wreck
    // before impact rather than arriving after the report has taken over.
    const cameraFollow = this.crash ? 9.5 : 4.2;
    const k = 1 - Math.exp(-cameraFollow * Math.min(dt, 0.1));
    this.camPos.lerp(this.camTarget, dt >= 1 ? 1 : k);
    this.world.camera.position.copy(this.camPos);
    if (!this.reducedMotion && this.shakeMag > 0.002) {
      this.world.camera.position.x += (Math.random() - 0.5) * this.shakeMag;
      this.world.camera.position.y += (Math.random() - 0.5) * this.shakeMag * 0.6;
      this.world.camera.position.z += (Math.random() - 0.5) * this.shakeMag;
      this.shakeMag *= Math.exp(-6 * dt);
    } else if (this.reducedMotion) {
      this.shakeMag = 0;
    }
    this.world.camera.lookAt(this.camLook);
  }
}

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function keepInField(pos: THREE.Vector3): void {
  const r = Math.hypot(pos.x, pos.z);
  const max = PLAY_RADIUS - 1;
  if (r > max) {
    pos.x *= max / r;
    pos.z *= max / r;
  }
  // Nobody walks on water.
  const ldx = pos.x - LAKE.x;
  const ldz = pos.z - LAKE.z;
  const ld = Math.hypot(ldx, ldz);
  const lmin = LAKE.r + 0.8;
  if (ld < lmin && ld > 0.001) {
    pos.x = LAKE.x + (ldx / ld) * lmin;
    pos.z = LAKE.z + (ldz / ld) * lmin;
  }
}
